import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/loading-button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useSSE } from "@/hooks/useSSE";
import { GapManagementFullPageSkeleton } from "@/components/ui/skeletons/GapManagementSkeleton";

import { useToast } from "@/components/ui/use-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { NoPracticeState } from "@/components/shared/NoPracticeState";
import { useProgramYear } from "@/contexts/ProgramYearContext";
import { useQueryClient } from '@tanstack/react-query';
import { invalidatePracticeScoring } from "@/lib/query-keys";
import { mipsScoreCalculator } from "@/services/mips-score-calculator";
import AddMeasureModal from "@/components/gap-management/quality/QualityMeasureSelector";

import Dropzone from "@/components/gap-management/shared/MeasureEvidenceFileUploader";
import { YearLockBanner } from "@/components/gap-management/shared/YearLockBanner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { isFeatureEnabled } from "@/config/features";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { MoreHorizontal, Settings2, X, Layers, ArrowUpDown, Award, Percent, Shield, CheckCircle2, Circle, BarChart3, DollarSign, CircleHelp, ChevronDown, ChevronRight, BarChart2, ExternalLink, InfoIcon, Loader2, Download, FileText, FileSpreadsheet, Columns, Users, User } from "lucide-react";
import userStorage from "@/lib/userStorage";
import DecileBenchmarkBar, { type DecileRange } from "@/components/gap-management/shared/BenchmarkChart";
import { getDecilesForMeasure, getDecilesForMeasureBySubmissionMethod } from "@/lib/benchmarks";
import { 
  computeDataCompletenessRate, 
  computePerformanceRate, 
  calculateOverallPerformanceRate,
  calculateOverallDataCompletenessRate,
  type PracticeSize 
} from "@/lib/mips-scoring";
import { type StrataData } from "@/lib/mips-data-service";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import RichTextEditor from "@/components/gap-management/shared/MeasureNotesEditor";
import { Switch } from "@/components/ui/switch";
import ManageViewsModal from "@/components/gap-management/shared/TableViewsManager";
import { MipsDataService } from "@/lib/mips-data-service";
import { getOutcomeSummary, getAllOutcomeSummaries, toMeasureRow, clearOutcomeCache, getProviders, type Provider } from "@/lib/measure-outcomes";
import {
  buildGapRow,
  rowMetaFor,
  rowHasData,
  toCategoryPayload,
  fromScorePayload,
  computeQualityCategory,
  submissionGuidance,
} from "@/lib/scenario";
import GapsToClose from "@/components/gap-management/shared/GapRecommendationsCard";
import { hasCustomStrata, getCustomStrataAlgorithm } from "@/lib/custom-strata-utils";
import { PICalculator } from "@/components/gap-management/pi";
import { IACalculator } from "@/components/gap-management/ia";
import { CostCalculator } from "@/components/gap-management/cost";
import { getIAScore } from "@/lib/ia-calculator-utils";
import { getCostDashboardData } from "@/lib/cost-calculator-utils";
import { getPICalculatorData } from "@/lib/pi-calculator-utils";
import { UnifiedScoreCard } from "@/components/gap-management/shared/UnifiedScoreCard";
import { PaymentAdjustmentModal } from "@/components/gap-management/payment-adjustment";
import { ChartAbstractionActionMenuItem, ChartAbstractionExportDropdown } from "@/components/gap-management/chart-abstraction";
import QualityPrintControls from "@/components/gap-management/quality/QualityPrintControls";
// MVP pathway preview (Phase D) — display-only seam; Traditional mode is untouched.
import { useMvpScoring } from "@/lib/mvp/useMvpScoring";
import { isQcdrMeasureId } from "@/lib/mvp/quality";
import { useMvpSelections, useSubgroups } from "@/hooks/queries/useMvpQueries";
import { apiClient } from "@/lib/api-client";
const dataService = MipsDataService.getInstance();

interface EvidenceFile {
  name: string;
  type: string;
  size: number;
  dataUrl: string;
  uploadedAt: number;
}
interface MeasureRow {
  id: string;
  title: string;
  // For single-strata measures (backward compatibility)
  ip?: number; // Initial Population
  ex?: number; // Exclusions
  den?: number; // Denominator
  met?: number; // Numerator Met
  exc?: number; // Numerator Exception
  not?: number; // Numerator Not Met
  dc?: number;  // Data Completeness %
  pr?: number;  // Performance Rate %
  // For multi-strata measures
  strata?: StrataData[];
  overallAlgorithm?: string;
  // Computed overall values
  overallDc?: number;
  overallPr?: number;
  // Metadata
  isMultiStrata?: boolean;
  submissionMethod?: string;
}

// Removed estimatePoints - now using scoreQualityMeasure from mips-scoring.ts

function toCsv(rows: any[], headers: string[]) {
  const esc = (v: any) => `"${String(v).replace(/"/g, '""')}"`;
  return [headers.join(","), ...rows.map(r => headers.map(h => esc(r[h])).join(","))].join("\n");
}

type AddedMeasureMode = "calc" | "manual";
interface AddedMeasure {
  id: string;
  title: string;
  mode: AddedMeasureMode;
  status?: "pending" | "calculated"; // for calc mode
  noData?: boolean; // if calculated but no data found
  manual?: { ip: number; ex: number; den: number; met: number; not: number; exc: number };
  // Multi-strata properties
  isMultiStrata?: boolean;
  strata?: StrataData[];
  overallAlgorithm?: string;
  overallPr?: number;
  overallDc?: number;
  submissionMethod?: string;
}

// Standardized badge colors for Navy Clinical Gentle theme (lighter variant)
const BADGE_COLORS = {
  MIPS_CQM: 'bg-[#5a7a9f] text-white border-[#4a6a8f]',
  QCDR: 'bg-[#5a7a9f] text-white border-[#4a6a8f]',
  PROCESS: 'bg-[#7a9bac] text-white border-[#6a8b9c]',
  OUTCOME: 'bg-[#8aabbc] text-white border-[#7a9bac]',
  INT_OUTCOME: 'bg-[#8aabbc] text-white border-[#7a9bac]',
  SEVEN_PT_CAP: 'bg-[#7a9aaa] text-white border-[#6a8a9a]',
  INVERSE: 'bg-[#6d9f9f] text-white border-[#5d8f8f]',
  BONUS: 'bg-[#3b82f6] text-white border-[#2563eb]', // Using Tailwind's blue-500 for hover-like effect
  eCQM: 'bg-[#7a9bac] text-white border-[#6a8b9c]',
  DEFAULT: 'bg-gray-400 text-white border-gray-500'
};

// Helper to format algorithm names (fallback for non-Nth algorithms)
const formatAlgorithmName = (name: string) => {
  if (!name) return "Overall Rate";
  return name
    .replace(/([A-Z])/g, ' $1')
    .replace('Performance', 'Perf')
    .replace('Average', 'Avg')
    .trim();
};

// Helper to get stratum description for Nth Performance Rate algorithms
const getStratumDescription = (algorithm: string, measureId: string, dataService: MipsDataService): string => {
  if (!algorithm) return "Overall Rate";
  
  // Check if this is an Nth Performance Rate algorithm
  const nthMatch = /^(\d)(?:st|nd|rd|th) Performance Rate$/i.exec(algorithm);
  if (!nthMatch) {
    // Not an Nth algorithm, use the formatted name
    return formatAlgorithmName(algorithm);
  }
  
  const position = parseInt(nthMatch[1], 10);
  const strataDefinitions = dataService.getStrataDefinitions(measureId);
  
  if (strataDefinitions && strataDefinitions.length >= position) {
    const targetDef = strataDefinitions[position - 1];
    // Extract a short description - use name if available, otherwise truncate description
    if (targetDef.name) {
      // Convert camelCase/technical names to readable format
      const readableName = targetDef.name
        .replace(/([a-z])([A-Z])/g, '$1 $2')  // camelCase to spaces
        .replace(/[_-]/g, ' ')  // underscores/dashes to spaces
        .replace(/\b\w/g, c => c.toUpperCase());  // capitalize words
      return readableName;
    }
    if (targetDef.description) {
      // Try to extract a short phrase from the description
      // Look for pattern like "Performance rate N: Description"
      const descMatch = /Performance rate \d+:\s*(.+?)(?:\.|$)/i.exec(targetDef.description);
      if (descMatch) {
        return descMatch[1].trim();
      }
      // Fallback: truncate description
      return targetDef.description.length > 30 
        ? targetDef.description.substring(0, 30) + '...'
        : targetDef.description;
    }
  }
  
  // Fallback to formatted algorithm name
  return formatAlgorithmName(algorithm);
};

// Helper to format measure types to short labels
const formatMeasureType = (measureType?: string): JSX.Element | null => {
  if (!measureType) return null;
  
  const shortLabels: Record<string, string> = {
    'process': 'Process',
    'outcome': 'Outcome', 
    'intermediateOutcome': 'Int. Outcome',
    'patientReportedOutcome': 'PRO',
    'patientEngagementExperience': 'Experience',
    'efficiency': 'Efficiency'
  };

  const label = shortLabels[measureType] || measureType;
  
  // Map measure types to badge colors
  const colorClass = measureType === 'process' ? BADGE_COLORS.PROCESS :
                     measureType === 'outcome' ? BADGE_COLORS.OUTCOME :
                     measureType === 'intermediateOutcome' ? BADGE_COLORS.INT_OUTCOME :
                     BADGE_COLORS.PROCESS;
  
  return (
    <Badge
      variant="secondary"
      className="rounded-full bg-slate-100 text-slate-700 border-slate-200/80 hover:bg-slate-200/70 whitespace-nowrap px-2.5 py-1 text-xs font-medium"
    >
      {label}
    </Badge>
  );
};

const getDefaultReportDate = () => {
  return new Intl.DateTimeFormat('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  }).format(new Date());
};

const normalizeRegistryValue = (raw: unknown): string => {
  if (raw === null || raw === undefined) return '';
  const trimmed = String(raw).trim();
  if (!trimmed) return '';
  const num = parseFloat(trimmed);
  if (!Number.isFinite(num)) return '';
  return num.toFixed(2);
};

const normalizeRegistryAveragesMap = (parsed: unknown): Record<string, string> => {
  if (!parsed || typeof parsed !== 'object') return {};
  const normalized: Record<string, string> = {};
  Object.entries(parsed as Record<string, unknown>).forEach(([k, v]) => {
    const formatted = normalizeRegistryValue(v);
    if (formatted) normalized[k] = formatted;
  });
  return normalized;
};

function GapManagementContent(props: any) {
  const { toast } = useToast();
  const { user, selectedPractice } = useAuth();
  const { programYear, isCurrentYearLocked } = useProgramYear();
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const selectedPracticeId = selectedPractice?.id?.toString();
  const registryAverageStorageKey = selectedPracticeId
    ? `gaps:qualityRegistryAverages:${selectedPracticeId}`
    : 'gaps:qualityRegistryAverages';
  const reportDateStorageKey = selectedPracticeId
    ? `gaps:qualityReportDate:${selectedPracticeId}`
    : 'gaps:qualityReportDate';
  const [reportDate, setReportDate] = useState(() => {
    try {
      const key = selectedPracticeId
        ? `gaps:qualityReportDate:${selectedPracticeId}`
        : 'gaps:qualityReportDate';
      return userStorage.getItem(key) || getDefaultReportDate();
    } catch {
      return getDefaultReportDate();
    }
  });
  const [registryAverages, setRegistryAverages] = useState<Record<string, string>>(() => {
    try {
      const key = selectedPracticeId
        ? `gaps:qualityRegistryAverages:${selectedPracticeId}`
        : 'gaps:qualityRegistryAverages';
      return normalizeRegistryAveragesMap(JSON.parse(userStorage.getItem(key) || '{}'));
    } catch {
      return {};
    }
  });
  
  // MIPS Scores State
  const [mipsScores, setMipsScores] = useState({
    qualityScore: 0,
    piScore: 0,
    iaScore: 0,
    costScore: 0,
    patientComplexityBonus: 0,
    compositeMipsScore: 0
  });
  const [scoresCalculated, setScoresCalculated] = useState(false); // Track when initial scores are ready
  const [pageReadyDelayDone, setPageReadyDelayDone] = useState(false); // tiny buffer to avoid flash
  
  const [mipsWeights, setMipsWeights] = useState({
    qualityWeight: 30,
    piWeight: 25,
    iaWeight: 15,
    costWeight: 30
  });
  
  // Track whether to use modeled scores per category
  const [useModeledPi, setUseModeledPi] = useState(true);
  const [useModeledIa, setUseModeledIa] = useState(true);
  const [useModeledCost, setUseModeledCost] = useState(true);
  
  // Store modeled percentages for display in PI Calculator, IA Calculator, and Cost Calculator
  const [modeledPiPercent, setModeledPiPercent] = useState(0);
  const [modeledIaPercent, setModeledIaPercent] = useState(0);
  const [modeledCostPercent, setModeledCostPercent] = useState(0);
  
  // Evaluation type state (GROUP/TIN vs NPI)
  const [evaluationType, setEvaluationType] = useState<'GROUP' | 'NPI'>('GROUP');
  const [selectedNpi, setSelectedNpi] = useState<string>('');
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(false);

  // ── MVP pathway preview (Phase D — spec §8-D) ─────────────────────────────
  // Two orthogonal axes (owner decision 2026-08-07):
  //  - View scope: Group/TIN · NPI · Subgroup (`viewSubgroupId` — subgroup scope
  //    derives quality counts from the roster; cost/PI/pop-health stay
  //    group-level per CMS).
  //  - Pathway: 'traditional' | '<mvpId>' — at Subgroup scope Traditional is
  //    DISABLED (CMS subgroups only report MVPs), so ANY MVP can be previewed
  //    at any subgroup's scope. Preview ≠ commit: previewing never writes.
  const [pathway, setPathway] = useState<string>('traditional');
  const [viewSubgroupId, setViewSubgroupId] = useState<number | null>(null);
  const pathwayMvpId = pathway === 'traditional' ? null : pathway;
  const pathwaySubgroupId = pathway === 'traditional' ? null : viewSubgroupId;
  // Composite ids in the MVP pool but NOT practice-tracked (greyed in the table)
  const [mvpUntrackedIds, setMvpUntrackedIds] = useState<Set<string>>(new Set());
  // Which pathway+scope PRODUCED the current baseData. Guards the switch-back
  // race: flipping to 'traditional' updates `pathway` instantly while the table
  // still holds MVP-pool rows until the reload lands — auto-calc firing in that
  // window persisted a pool-filtered score (frozen-dataset diff, 2026-08-07).
  const TRADITIONAL_DATA_KEY = 'traditional|sg:none';
  const activeDataKey = `${pathway}|sg:${viewSubgroupId ?? 'none'}`;
  const rowsLoadedForPathway = useRef<string>(TRADITIONAL_DATA_KEY);
  const mvpSelectionsQ = useMvpSelections(selectedPracticeId, programYear);
  const mvpSubgroupsQ = useSubgroups(selectedPracticeId, programYear);
  const mvpCatalog = useMemo(() => dataService.getMvps(programYear), [programYear]);
  // Engine scoring for the previewed pathway — queries disabled in Traditional mode
  const mvpScoring = useMvpScoring(
    pathwayMvpId ? selectedPracticeId : undefined, programYear, pathwayMvpId, pathwaySubgroupId);
  const mvpPreviewRow = pathwayMvpId ? mvpScoring.row : null;
  const committedGroupMvp = (mvpSelectionsQ.data ?? []).find(s => s.entity_type === 'group') ?? null;
  const mvpSubgroups = mvpSubgroupsQ.data ?? [];
  const activeSubgroup = viewSubgroupId != null
    ? mvpSubgroups.find(sg => sg.id === viewSubgroupId) ?? null
    : null;
  const committedMvpForSubgroup = useCallback((sgId: number) =>
    (mvpSelectionsQ.data ?? [])
      .find(s => s.entity_type === 'subgroup' && s.subgroup_id === sgId)?.mvp_id ?? null,
    [mvpSelectionsQ.data]);
  /** Enter/keep Subgroup scope: force an MVP pathway (Traditional invalid here). */
  const selectSubgroupView = useCallback((sgId: number) => {
    setEvaluationType('GROUP');
    setSelectedNpi('');
    setViewSubgroupId(sgId);
    const committed = committedMvpForSubgroup(sgId);
    if (committed) {
      setPathway(committed);
    } else {
      setPathway(prev => (prev === 'traditional'
        ? (committedGroupMvp?.mvp_id ?? mvpCatalog[0]?.mvpId ?? prev)
        : prev));
    }
  }, [committedMvpForSubgroup, committedGroupMvp, mvpCatalog]);

  // Leaving the practice/year resets the preview (it is session-scoped, never stored)
  useEffect(() => { setPathway('traditional'); setViewSubgroupId(null); }, [selectedPracticeId, programYear]);

  // Entering a preview kills any in-flight debounced score calc (a Traditional
  // calc scheduled milliseconds earlier must not land mid-preview).
  useEffect(() => {
    if (pathway !== 'traditional') mipsScoreCalculator.cancelPendingCalculation();
  }, [pathway]);

  const commitPreviewedMvp = useCallback(async () => {
    if (!selectedPracticeId || !pathwayMvpId) return;
    try {
      await apiClient.post(`/api/mvp/${selectedPracticeId}/selections?year=${programYear}`, {
        mvp_id: pathwayMvpId, entity_type: 'group',
      });
      await queryClient.invalidateQueries({ queryKey: ['mvp', 'selections', selectedPracticeId, programYear] });
      toast({ title: 'MVP selected', description: `${pathwayMvpId} is now this practice's MVP for PY${programYear}. CMS registration (QPP website) closes Nov 30.` });
    } catch (e: any) {
      toast({ title: 'Failed to save selection', description: e?.message ?? '', variant: 'destructive' });
    }
  }, [selectedPracticeId, pathwayMvpId, programYear, queryClient, toast]);
  // ──────────────────────────────────────────────────────────────────────────
  
  const [mipsDataLoading, setMipsDataLoading] = useState(true); // Start as true for initial load
  const [mipsDataError, setMipsDataError] = useState<string | null>(null);
  const [calculatingScore, setCalculatingScore] = useState(false);
  const hasLoadedMipsDataOnce = useRef(false); // Track if we've loaded MIPS data at least once
  
  // Payment Adjustment Modal state
  const [showPaymentAdjustmentModal, setShowPaymentAdjustmentModal] = useState(false);
  
  // Calculate composite score locally from displayed individual scores
  // This ensures the composite always matches the sum of displayed category scores,
  // regardless of timing issues with backend score recalculation after weight changes
  const displayCompositeScore = useMemo(() => {
    return mipsScores.qualityScore + mipsScores.piScore + mipsScores.iaScore + 
           mipsScores.costScore + mipsScores.patientComplexityBonus;
  }, [mipsScores.qualityScore, mipsScores.piScore, mipsScores.iaScore, mipsScores.costScore, mipsScores.patientComplexityBonus]);

  useEffect(() => {
    try {
      setReportDate(userStorage.getItem(reportDateStorageKey) || getDefaultReportDate());
      setRegistryAverages(
        normalizeRegistryAveragesMap(JSON.parse(userStorage.getItem(registryAverageStorageKey) || '{}'))
      );
    } catch {
      setReportDate(getDefaultReportDate());
      setRegistryAverages({});
    }
  }, [reportDateStorageKey, registryAverageStorageKey]);

  const handleReportDateChange = useCallback((value: string) => {
    setReportDate(value);
    userStorage.setItem(reportDateStorageKey, value);
  }, [reportDateStorageKey]);

  const updateRegistryAverage = useCallback((compositeId: string, value: string) => {
    const cleaned = value.replace(/[^0-9.]/g, '').slice(0, 6);
    setRegistryAverages(prev => {
      const next = { ...prev, [compositeId]: cleaned };
      userStorage.setItem(registryAverageStorageKey, JSON.stringify(next));
      return next;
    });
  }, [registryAverageStorageKey]);

  const normalizeRegistryAverage = useCallback((compositeId: string) => {
    setRegistryAverages(prev => {
      const raw = prev[compositeId] ?? '';
      const formatted = normalizeRegistryValue(raw);
      if (formatted === raw) return prev;
      const next = { ...prev };
      if (formatted) {
        next[compositeId] = formatted;
      } else {
        delete next[compositeId];
      }
      userStorage.setItem(registryAverageStorageKey, JSON.stringify(next));
      return next;
    });
  }, [registryAverageStorageKey]);
  
  // Navigation lifecycle logging
  console.log('[NAV-LIFECYCLE] Summary component rendering, path:', location.pathname);
  
  // Track if we're on the main gap management page (not detail view)
  const isOnMainPage = !location.pathname.includes('/measure/');
  const prevPathRef = useRef(location.pathname);
  
  // Use refreshTrigger from QualityWrapper prop (incremented on Detail→Summary transitions)
  // plus a local counter for internally-triggered refreshes (e.g. navigation from other pages)
  const [localRefreshTrigger, setLocalRefreshTrigger] = useState(0);
  const wrapperRefreshTrigger = props.refreshTrigger ?? 0;
  const refreshTrigger = wrapperRefreshTrigger + localRefreshTrigger;
  
  // Detect navigation back to main page from another route (like Configuration)
  // This triggers a refresh of MIPS data when the page becomes visible again
  useEffect(() => {
    const prevPath = prevPathRef.current;
    const currentPath = location.pathname;
    
    // Check if we navigated FROM a non-gap-management page TO the gap management page
    const wasOnDifferentPage = !prevPath.includes('/gap-management/quality');
    const isNowOnGapManagement = currentPath.includes('/gap-management/quality') && isOnMainPage;
    
    if (wasOnDifferentPage && isNowOnGapManagement) {
      console.log('[NAV-LIFECYCLE] Navigated back to Gap Management - triggering MIPS data refresh');
      setLocalRefreshTrigger(prev => prev + 1);
    }
    
    prevPathRef.current = currentPath;
  }, [location.pathname, isOnMainPage]);
  
  // Ref to hold silentRefresh function for use in focus handler
  // This avoids the need to move the focus useEffect after silentRefresh definition
  const silentRefreshRef = useRef<(() => Promise<void>) | null>(null);

  // [B3 2026-08-01] Phase 3 in-flight hold: while pipelined Phase 3 is writing
  // outcomes for this practice, the summaries API anchors on the PREVIOUS
  // fully-complete batch, and masters already advanced by Phase 3 drop out of
  // it — so a mid-run fetch would replace good numbers with a sparse snapshot.
  // We freeze the last-good display until the phase3 completed/failed SSE
  // arrives (belt to the API gate's suspenders). Timestamped so a missed SSE
  // event (connection died on an API restart) can't wedge refreshes forever.
  const PHASE3_HOLD_MAX_MS = 15 * 60_000;
  const [phase3InFlight, setPhase3InFlight] = useState(false);
  const phase3HoldStartedRef = useRef(0);
  const isPhase3HoldActive = useCallback(() => {
    if (!phase3HoldStartedRef.current) return false;
    if (Date.now() - phase3HoldStartedRef.current > PHASE3_HOLD_MAX_MS) {
      phase3HoldStartedRef.current = 0;
      setPhase3InFlight(false);
      return false;
    }
    return true;
  }, []);
  
  // Also refresh when window regains focus (user switches tabs/windows).
  // Uses silentRefresh to update values in place without full reload cascade.
  // Debounced with a 30s cooldown: every focus fired the heavy /summaries
  // query pair, so rapid tab-switching hammered the DB for idle users. Edits
  // and Phase 3 completion have their own refresh paths (SSE + navigation
  // detailUpdatePending), which are NOT debounced.
  const lastFocusRefreshRef = useRef(0);
  useEffect(() => {
    const FOCUS_REFRESH_COOLDOWN_MS = 30_000;
    const handleFocus = () => {
      if (!isOnMainPage || !silentRefreshRef.current) return;
      const now = Date.now();
      if (now - lastFocusRefreshRef.current < FOCUS_REFRESH_COOLDOWN_MS) {
        console.log('[NAV-LIFECYCLE] Window focused - refresh skipped (cooldown)');
        return;
      }
      lastFocusRefreshRef.current = now;
      console.log('[NAV-LIFECYCLE] Window focused - triggering silent refresh');
      silentRefreshRef.current();
    };
    
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [isOnMainPage]);
  
  // Track mount/unmount
  useEffect(() => {
    console.log('[NAV-LIFECYCLE] Summary component MOUNTED');
    return () => {
      console.log('[NAV-LIFECYCLE] Summary component UNMOUNTING');
    };
  }, []); // Empty deps - only runs on mount/unmount
  
  // Fetch providers when practice is selected (needed for both GROUP and NPI mode)
  // GROUP mode needs providers for IA clinician selection
  // NPI mode needs providers for the NPI dropdown
  useEffect(() => {
    const fetchProviders = async () => {
      if (!selectedPracticeId) {
        return;
      }
      
      setLoadingProviders(true);
      try {
        const providersList = await getProviders();
        setProviders(providersList);
        // Auto-select first provider if NPI mode and none selected
        if (evaluationType === 'NPI' && !selectedNpi && providersList.length > 0) {
          setSelectedNpi(providersList[0].npi);
        }
      } catch (error) {
        console.error('Error fetching providers:', error);
        toast({
          title: "Error loading providers",
          description: "Could not load provider list",
          variant: "destructive"
        });
      } finally {
        setLoadingProviders(false);
      }
    };
    
    fetchProviders();
  }, [selectedPracticeId, toast]);
  
  // Fetch MIPS scores and weights.
  // [Data-freshness 2026-08-12] Extracted to a useCallback (body unchanged) so
  // the doorbell listener below can re-run it when another page's save
  // invalidates this practice's scoring caches. NOTE: `apiUrl` (declared later
  // in the component) is intentionally NOT in the deps — it is a stable
  // module-env constant, and referencing it in the deps array would hit the
  // temporal dead zone at render time.
  const fetchMipsData = useCallback(async () => {
      if (!selectedPracticeId) {
        setMipsDataLoading(false);
        return;
      }
      
      setMipsDataLoading(true);
      setMipsDataError(null);
      
      try {
        // Use programYear from context (set via Admin settings)
        
        // [Snappiness 0.5] weights + scores are independent GETs — fetch in
        // parallel (was sequential: weights, then scores, then IA, then cost)
        const authHeaders = {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
        };
        const [weightsResponse, scoresResponse] = await Promise.all([
          fetch(`${apiUrl}/api/practices/${selectedPracticeId}/mips/weights?year=${programYear}`, { headers: authHeaders }),
          fetch(`${apiUrl}/api/practices/${selectedPracticeId}/mips/scores?year=${programYear}`, { headers: authHeaders }),
        ]);
        
        if (weightsResponse.ok) {
          const weightsData = await weightsResponse.json();
          const loadedWeights = {
            qualityWeight: weightsData.quality_weight !== undefined ? Number(weightsData.quality_weight) : 30,
            piWeight: weightsData.pi_weight !== undefined ? Number(weightsData.pi_weight) : 25,
            iaWeight: weightsData.ia_weight !== undefined ? Number(weightsData.ia_weight) : 15,
            costWeight: weightsData.cost_weight !== undefined ? Number(weightsData.cost_weight) : 30
          };
          setMipsWeights(loadedWeights);
          // Set the individual toggle states for using modeled scores
          // Default to false (Calculate mode) if database has null
          const loadedUseModeledPi = weightsData.use_modeled_pi === true;
          const loadedUseModeledIa = weightsData.use_modeled_ia === true;
          const loadedUseModeledCost = weightsData.use_modeled_cost === true;
          setUseModeledPi(loadedUseModeledPi);
          setUseModeledIa(loadedUseModeledIa);
          setUseModeledCost(loadedUseModeledCost);
          // Store modeled percentages for PI Calculator and IA Calculator display
          const loadedModeledPiPercent = weightsData.modeled_pi_percent ?? 0;
          const loadedModeledIaPercent = weightsData.modeled_ia_percent ?? 0;
          const loadedModeledCostPercent = weightsData.modeled_cost_percent ?? 0;
          setModeledPiPercent(loadedModeledPiPercent);
          setModeledIaPercent(loadedModeledIaPercent);
          setModeledCostPercent(loadedModeledCostPercent);
          
          if (scoresResponse.ok) {
            const scoresData = await scoresResponse.json();
            
            // [Snappiness 0.5] Category enrichments (PI-for-NPI, IA, Cost) are
            // independent of each other — run in parallel instead of awaiting
            // one after another. Error behavior preserved: PI/Cost failures
            // fall back to 0 (same as the old per-call try/catch); an IA
            // failure still propagates to the outer catch (same as before).
            const npi = evaluationType === 'NPI' ? selectedNpi : undefined;
            const needsNpiPi = !loadedUseModeledPi && evaluationType === 'NPI' && !!selectedNpi;
            const [npiPiData, iaScoreData, costData] = await Promise.all([
              needsNpiPi
                ? getPICalculatorData(selectedPracticeId, selectedNpi, programYear).catch((e) => {
                    console.error('Error fetching NPI PI score:', e);
                    return null;
                  })
                : Promise.resolve(null),
              !loadedUseModeledIa
                ? getIAScore(selectedPracticeId, { npi, year: programYear })
                : Promise.resolve(null),
              !loadedUseModeledCost
                ? getCostDashboardData(selectedPracticeId, { npi, year: programYear }).catch((e) => {
                    console.error('Error fetching cost score:', e);
                    return null;
                  })
                : Promise.resolve(null),
            ]);
            
            // For PI in NPI mode, use the provider-specific PI data so the
            // header card matches the IA/Cost cards (parity fix)
            let piScore = 0;
            if (loadedUseModeledPi) {
              piScore = (loadedModeledPiPercent / 100) * loadedWeights.piWeight;
            } else if (needsNpiPi) {
              const npiTotal = npiPiData?.calculatedScore?.totalScore ?? 0;
              piScore = (npiTotal / 100) * loadedWeights.piWeight;
            } else {
              piScore = scoresData.pi_score !== undefined ? Number(scoresData.pi_score) : 0;
            }
            
            // For IA: if NOT using modeled, use the calculated score from IA API
            let iaScore = 0;
            if (loadedUseModeledIa) {
              iaScore = (loadedModeledIaPercent / 100) * loadedWeights.iaWeight;
            } else if (iaScoreData) {
              const effectiveWeight = loadedWeights.iaWeight ?? 15;
              iaScore = (iaScoreData.totalScore / 40) * effectiveWeight;
            }
            
            // For Cost: if NOT using modeled, use the calculated score from Cost API
            let costScore = 0;
            if (loadedUseModeledCost) {
              costScore = (loadedModeledCostPercent / 100) * loadedWeights.costWeight;
            } else if (costData?.summary?.categoryScore !== undefined) {
              const effectiveWeight = loadedWeights.costWeight ?? 30;
              costScore = (costData.summary.categoryScore / 100) * effectiveWeight;
            }
            
            setMipsScores({
              qualityScore: scoresData.quality_score !== undefined ? Number(scoresData.quality_score) : 0,
              piScore: piScore,
              iaScore: iaScore,
              costScore: costScore,
              patientComplexityBonus: scoresData.patient_complexity_bonus !== undefined ? Number(scoresData.patient_complexity_bonus) : 0,
              compositeMipsScore: scoresData.composite_mips_score !== undefined ? Number(scoresData.composite_mips_score) : 0
            });
          } else {
            const error = await scoresResponse.json();
            throw new Error(error.error || 'Failed to fetch scores');
          }
        } else {
          const error = await weightsResponse.json();
          throw new Error(error.error || 'Failed to fetch weights');
        }
      } catch (error) {
        console.error('Error fetching MIPS data:', error);
        setMipsDataError(error instanceof Error ? error.message : 'Failed to load MIPS data');
        // Set default values on error
        setMipsScores({
          qualityScore: 0,
          piScore: 0,
          iaScore: 0,
          costScore: 0,
          patientComplexityBonus: 0,
          compositeMipsScore: 0
        });
      } finally {
        setMipsDataLoading(false);
        hasLoadedMipsDataOnce.current = true;
      }
  }, [selectedPracticeId, evaluationType, selectedNpi, programYear]);

  useEffect(() => {
    fetchMipsData();
  }, [fetchMipsData, refreshTrigger]);

  // [Data-freshness 2026-08-12] Doorbell listener. This page is kept mounted
  // (DashboardLayout display:none) and its header uses raw fetches, so shared
  // React Query invalidations (invalidatePracticeScoring — IA/PI saves, weights
  // and modeled changes, QPP uploads, score persistence) never reached it: the
  // header served stale scores until a full reload. Subscribe to the query
  // cache and re-run the header's OWN fetch pipeline when one of this
  // practice's scoring keys is invalidated. Table data (loadMeasureData /
  // baseRows / silentRefresh) is deliberately untouched — see
  // quality-data-flow.mdc. Debounced: one doorbell invalidates several keys.
  // Loop-safe: fetchMipsData uses raw fetch (writes no query cache), and
  // re-running it never re-triggers the debounced quality persist (that is
  // keyed on table rows, not header scores).
  const fetchMipsDataRef = useRef(fetchMipsData);
  useEffect(() => { fetchMipsDataRef.current = fetchMipsData; }, [fetchMipsData]);

  useEffect(() => {
    if (!selectedPracticeId) return;
    const pid = String(selectedPracticeId);
    const watchedPrefixes: unknown[][] = [
      ['mips', 'scores', pid],
      ['mips', 'weights', pid],
      ['ia', 'score', pid],
      ['cost', 'dashboard', pid],
    ];
    let timer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated' || event.action.type !== 'invalidate') return;
      const key = event.query.queryKey as unknown[];
      const matches = watchedPrefixes.some((prefix) =>
        prefix.every((segment, i) => key[i] === segment));
      if (!matches) return;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        fetchMipsDataRef.current?.();
      }, 400);
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, [selectedPracticeId, queryClient]);

  // NOTE: Window focus refresh is handled by silentRefresh (lines 305-315)
  // which updates data in place without loading state flashes.
  // Do NOT add another focus handler here - it causes duplicate refreshes
  // and table re-render/unmount issues when clicking dropdowns.
  
  // Handler to toggle modeled PI setting from PI Calculator
  const handleToggleModeledPi = useCallback(async (useModeled: boolean) => {
    if (!selectedPracticeId) return;
    
    try {
      const response = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/mips/weights?year=${programYear}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ use_modeled_pi: useModeled }),
      });
      
      if (response.ok) {
        setUseModeledPi(useModeled);
        setLocalRefreshTrigger(prev => prev + 1);
        invalidatePracticeScoring(selectedPracticeId);
        
        toast({
          title: useModeled ? "Switched to Modeled Score" : "Switched to PI Calculator",
          description: useModeled 
            ? "PI score is now using the modeled value from Configuration."
            : "PI score will be calculated from your entered data.",
        });
      } else {
        throw new Error('Failed to update PI setting');
      }
    } catch (error) {
      console.error('Error toggling modeled PI:', error);
      toast({
        title: "Error",
        description: "Failed to update PI score setting",
        variant: "destructive"
      });
    }
  }, [selectedPracticeId, programYear, toast]);

  // Handler to toggle modeled IA setting from IA Calculator
  const handleToggleModeledIa = useCallback(async (useModeled: boolean) => {
    if (!selectedPracticeId) return;

    try {
      const response = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/mips/weights?year=${programYear}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ use_modeled_ia: useModeled }),
      });

      if (response.ok) {
        setUseModeledIa(useModeled);
        setLocalRefreshTrigger(prev => prev + 1);
        invalidatePracticeScoring(selectedPracticeId);
        
        toast({
          title: useModeled ? "Switched to Modeled Score" : "Switched to IA Calculator",
          description: useModeled
            ? "IA score is now using the modeled value from Configuration."
            : "IA score will be calculated from your attestations.",
        });
      } else {
        throw new Error('Failed to update IA setting');
      }
    } catch (error) {
      console.error('Error toggling modeled IA:', error);
      toast({
        title: "Error",
        description: "Failed to update IA score setting",
        variant: "destructive"
      });
    }
  }, [selectedPracticeId, programYear, toast]);
  
  // Handler to toggle modeled Cost setting from Cost Calculator
  const handleToggleModeledCost = useCallback(async (useModeled: boolean) => {
    if (!selectedPracticeId) return;

    try {
      const response = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/mips/weights?year=${programYear}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ use_modeled_cost: useModeled }),
      });

      if (response.ok) {
        setUseModeledCost(useModeled);
        setLocalRefreshTrigger(prev => prev + 1);
        invalidatePracticeScoring(selectedPracticeId);
        
        toast({
          title: useModeled ? "Switched to Modeled Score" : "Switched to Cost Calculator",
          description: useModeled
            ? "Cost score is now using the modeled value from Configuration."
            : "Cost score will be calculated from your uploaded data.",
        });
      } else {
        throw new Error('Failed to update Cost setting');
      }
    } catch (error) {
      console.error('Error toggling modeled Cost:', error);
      toast({
        title: "Error",
        description: "Failed to update Cost score setting",
        variant: "destructive"
      });
    }
  }, [selectedPracticeId, programYear, toast]);
  
  // Track if returning from detail view
  const [previousPath, setPreviousPath] = useState<string>('');
  
  // Track previous practice ID to detect changes
  const prevPracticeIdRef = useRef<string | undefined>(selectedPracticeId);
  const isMountedRef = useRef(false);

  // Helper function to get the correct specification URL based on submission method
  const getSpecificationUrl = useCallback((measure: any): string | null => {
    if (!measure) return null;
    
    // Get the CMS measure data to access specification URLs
    const cmsMeasure = dataService.getActiveMeasure(measure.id);
    if (!cmsMeasure?.measureSpecification) return null;
    
    const submissionMethod = measure.submissionMethod || 'MIPS CQM';
    
    switch (submissionMethod) {
      case 'MIPS CQM':
      case 'QCDR':
        return cmsMeasure.measureSpecification.registry || null;
      case 'eCQM':
        return cmsMeasure.measureSpecification.electronicHealthRecord || null;
      case 'Claims':
        return cmsMeasure.measureSpecification.claims || null;
      default:
        return cmsMeasure.measureSpecification.registry || null;
    }
  }, []);
  
  // Get practice size from database (consistent with Configuration page)
  const [practiceSize, setPracticeSize] = useState<PracticeSize>('large'); // Default to large
  
  // Load practice settings from API to get small practice status
  useEffect(() => {
    const loadPracticeSettings = async () => {
      if (!selectedPracticeId) return;
      
      try {
        const response = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/settings?year=${programYear}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
        });
        
        if (response.ok) {
          const settings = await response.json();
          const newPracticeSize = settings.is_small_practice ? 'small' : 'large';
          setPracticeSize(newPracticeSize);
          console.log(`[Gap Management] Loaded practice size: ${newPracticeSize} (is_small_practice: ${settings.is_small_practice})`);
        } else {
          console.error('[Gap Management] Failed to load practice settings:', response.status);
        }
      } catch (error) {
        console.error('[Gap Management] Error loading practice settings:', error);
        // Keep default 'large' on error
      }
    };
    
    loadPracticeSettings();
  }, [selectedPracticeId, refreshTrigger]); // Re-load when practice changes or navigating back
  
  // Add error state
  const [error, setError] = useState<Error | null>(null);
  const [simulate, setSimulate] = useState<"off" | "2.5" | "5" | "10" | "15" | "20">("off");
  const simDelta = useMemo(() => (simulate === "off" ? 0 : Number(simulate)), [simulate]);
  const [simRule, setSimRule] = useState<'none' | 'no75' | 'noden' | 'both'>('none');
  const [activeCategory, setActiveCategory] = useState<"quality" | "ia" | "pi" | "composite" | "cost">("quality");

  // [Snappiness Phase 0.3] Lazy-mount the PI/IA/Cost calculators: mount on
  // FIRST tab select, keep mounted afterwards (display:none) so tab returns
  // don't remount/refetch. Previously all three mounted with the Quality tab
  // and fired their fetches immediately — every Quality visit paid the PI+IA+
  // Cost network tax. Header scores are self-sufficient (fetchMipsData +
  // live-recomputed /mips/scores), verified 2026-08-03 (plan §0.3a).
  const [mountedCategories, setMountedCategories] = useState<Set<string>>(() => new Set(["quality"]));
  useEffect(() => {
    setMountedCategories(prev => {
      if (prev.has(activeCategory)) return prev;
      const next = new Set(prev);
      next.add(activeCategory);
      return next;
    });
  }, [activeCategory]);

  // Helper: parse composite id (measureId:submissionMethod)
  const parseCompositeId = useCallback((id: string): { measureId: string; submissionMethod?: string } => {
    const [measureId, submissionMethod] = id.includes(":") ? id.split(":") : [id, undefined];
    return { measureId, submissionMethod };
  }, []);

  // Helper: find primary submission method for a measure (fallback to first benchmark)
  const getPrimarySubmissionMethod = useCallback((measureId: string): string | undefined => {
    const dataService = MipsDataService.getInstance();
    const benchmark = dataService.getPrimaryBenchmark(measureId);
    return benchmark?.submissionMethods || undefined;
  }, []);
  
  // Data from MipsDataService
  const [baseData, setBaseData] = useState<MeasureRow[]>([]);
  const [measureCatalog, setMeasureCatalog] = useState<{ id: string; title: string }[]>([]);
  const [loadingMeasures, setLoadingMeasures] = useState(false); // Don't show loading by default
  const [initialLoad, setInitialLoad] = useState(true); // Track first mount
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false); // Track if we've loaded data at least once
  
  const [dataVersion, setDataVersion] = useState(0); // Trigger for data refresh
  
  // Loading states for buttons
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [isAddingMeasure, setIsAddingMeasure] = useState(false);
  
  // Expandable rows state for multi-strata measures
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  // Bulk selection state
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedMeasures, setSelectedMeasures] = useState<Set<string>>(new Set());
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  
  // State for partial measures export
  const [partialMeasuresModalOpen, setPartialMeasuresModalOpen] = useState(false);
  const [selectedPartialMeasures, setSelectedPartialMeasures] = useState<Set<string>>(new Set());
  const [exportingPartialMeasures, setExportingPartialMeasures] = useState(false);
  
  // Draft edits for manual entry; commit on "Confirm Updates & Recalculate"
  const [drafts, setDrafts] = useState<Record<string, {
    manual?: { ip: number; ex: number; den: number; met: number; not: number; exc: number };
    strata?: StrataData[];
  }>>({});
  
  // Track if user has pending drafts - used to stabilize sorting while editing
  const hasPendingDraftsGlobal = Object.keys(drafts).length > 0;
  
  // Helper functions for expandable rows
  const toggleRowExpansion = useCallback((measureId: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(measureId)) {
        next.delete(measureId);
      } else {
        next.add(measureId);
      }
      return next;
    });
  }, []);
  
  const isRowExpanded = useCallback((measureId: string) => {
    return expandedRows.has(measureId);
  }, [expandedRows]);
  
  // Helper functions for bulk selection
  const toggleMeasureSelection = useCallback((compositeId: string) => {
    setSelectedMeasures(prev => {
      const next = new Set(prev);
      if (next.has(compositeId)) {
        next.delete(compositeId);
      } else {
        next.add(compositeId);
      }
      return next;
    });
  }, []);
  
  const selectAllMeasures = useCallback((measureIds: string[]) => {
    setSelectedMeasures(new Set(measureIds));
  }, []);
  
  const clearAllSelections = useCallback(() => {
    setSelectedMeasures(new Set());
  }, []);
  
  const isMeasureSelected = useCallback((compositeId: string) => {
    return selectedMeasures.has(compositeId);
  }, [selectedMeasures]);
  
  const exitSelectionMode = useCallback(() => {
    setIsSelectionMode(false);
    clearAllSelections();
  }, [clearAllSelections]);
  
  // Load real measure data
  useEffect(() => {
    console.log('=== LOAD MEASURE DATA EFFECT TRIGGERED ===');
    console.log('[TRACE-USEEFFECT] useEffect triggered - dataVersion:', dataVersion, 'practiceId:', selectedPracticeId);
    console.log('[TRACE-USEEFFECT] Current baseData.length:', baseData.length);
    console.log('[TRACE-USEEFFECT] prevPracticeIdRef.current:', prevPracticeIdRef.current);
    
    // Detect if this is a fresh mount
    const isFreshMount = !isMountedRef.current;
    if (isFreshMount) {
      isMountedRef.current = true;
      console.log('[TRACE-USEEFFECT] Fresh mount detected');
    }
    
    // Create AbortController for this effect
    const abortController = new AbortController();
    
    const loadMeasureData = async () => {
      if (!selectedPracticeId) {
        // If no practice selected, we're done loading (nothing to load)
        setInitialLoad(false);
        setHasLoadedOnce(true);
        return;
      }
      
      console.log('[TRACE-USEEFFECT] Starting loadMeasureData...');
      
      // Only clear existing data when practice actually changes
      const isPracticeChange = prevPracticeIdRef.current !== selectedPracticeId;
      console.log('[TRACE-USEEFFECT] Is practice change?', isPracticeChange);
      console.log('[TRACE-USEEFFECT] Previous practice:', prevPracticeIdRef.current, 'Current:', selectedPracticeId);
      
      // If practice changed, we need to clear data and reload
      if (isPracticeChange) {
        console.log('[TRACE-USEEFFECT] Practice changed, will clear and reload');
        setBaseData([]);
        setEvaluationType('GROUP');
        setSelectedNpi('');
        prevPracticeIdRef.current = selectedPracticeId;
      } else {
        console.log('[TRACE-USEEFFECT] Same practice, keeping existing baseData while loading');
        console.log('[TRACE-USEEFFECT] Existing baseData count:', baseData.length);
      }
      
      // Load saved additions from user-scoped storage (will filter later after baseData is loaded)
      const savedAdditionsRaw = userStorage.getItem("gaps:additions");
      console.log('[TRACE-USEEFFECT] localStorage gaps:additions:', savedAdditionsRaw);
      
      const savedAdditions = JSON.parse(savedAdditionsRaw || "[]");
      console.log('[TRACE-USEEFFECT] Parsed savedAdditions:', savedAdditions);
      
      // [Snappiness Phase 0.2] No unconditional clearOutcomeCache() here — it
      // defeated the 5-min summaries cache on every load. The cache is cleared
      // at real data-change points instead: manual/batch edits
      // (measure-outcomes.ts), uploads/deletes (UploadHistory), year switches
      // (ProgramYearContext), recalc + bulk delete (this file), silentRefresh.

      try {
        setError(null);
        
        // First, get practice-specific measure selections
        let practiceSelectedMeasures: Set<string> = new Set();
        // CRITICAL FIX: Declare these in outer scope to prevent ReferenceError
        let measureModes = new Map();
        let measureManualValues = new Map();
        
        try {
          console.log('[TRACE] Fetching practice measures from API...');
          const response = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/measures?category=quality&active_only=true&year=${programYear}`, {
            signal: abortController.signal,
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            },
          });
          
          if (response.ok) {
            const selections = await response.json();
        console.log('[TRACE] Received practice measures from API:', selections);
        console.log('[TRACE] Full measure details:');
        selections.forEach((s: any) => {
          console.log(`  - Measure ${s.measure_id}: submission_method="${s.submission_method}" category="${s.measure_category}" active=${s.is_active} mode=${s.calculation_mode}`);
        });
        console.log('[TRACE] Measure IDs in response:', selections.map((s: any) => `${s.measure_id}:${s.submission_method}`));
        
            practiceSelectedMeasures = new Set(
              selections.map((s: any) => `${s.measure_id}:${s.submission_method}`)
            );
        
        // Store calculation modes and manual values from backend
        // NOTE: These were moved to outer scope to prevent ReferenceError
        measureModes = new Map();
        measureManualValues = new Map();
        selections.forEach((s: any) => {
          const compositeId = `${s.measure_id}:${s.submission_method}`;
          if (s.calculation_mode) {
            measureModes.set(compositeId, s.calculation_mode);
          }
          if (s.manual_values) {
            measureManualValues.set(compositeId, s.manual_values);
          }
        });
        
        console.log('[TRACE] practiceSelectedMeasures Set contains:', Array.from(practiceSelectedMeasures));
        console.log('[TRACE] Measures in manual mode:', Array.from(measureModes.entries()).filter(([, mode]) => mode === 'manual'));
          } else {
            console.warn('[Gaps API] Failed to load practice measure selections, status:', response.status, 'showing all measures');
          }
        } catch (error) {
          console.error('Error loading practice measures:', error);
        }
        
        // Get all active measures with submission methods
        const allMeasuresWithSubmissionMethods = dataService.getActiveMeasuresWithAllSubmissionMethods();
    
      // PERFORMANCE: Batch load all summaries at once instead of sequential calls
      const evaluationContext = { 
        evaluationType, 
        npi: evaluationType === 'NPI' ? selectedNpi : undefined,
        year: programYear
      };
      // MVP subgroup preview: quality counts derive from the roster (Phase C
      // endpoint returns the exact /summaries shape). Group/Traditional paths
      // keep the legacy fetch untouched.
      const allSummaries = pathwaySubgroupId != null
        ? await apiClient.get(`/api/mvp/${selectedPracticeId}/subgroups/${pathwaySubgroupId}/summaries?year=${programYear}`)
        : await getAllOutcomeSummaries(abortController.signal, evaluationContext);
      console.log('[Summary] getAllOutcomeSummaries returned', allSummaries.length, 'summaries for', evaluationType, selectedNpi || 'all');
      
      const summaryMap = new Map(
        allSummaries.map(s => [`${s.measureId}:${s.submissionMethod}`, s])
      );
      console.log('[Summary] summaryMap has', summaryMap.size, 'entries');
      if (summaryMap.size > 0 && summaryMap.size <= 10) {
        console.log('[Summary] summaryMap keys:', Array.from(summaryMap.keys()));
      }
      
      // Convert to MeasureRow format using outcome data or zeroed fallback
      const measures: MeasureRow[] = [];
      
      // Process each measure using cached summary data
      // Filter to only show practice-selected measures if we have selections
      const qualityMeasures = allMeasuresWithSubmissionMethods.filter((m) => m.category === 'quality');
      
      console.log('[TRACE] Total quality measures available:', qualityMeasures.length);
      console.log('[TRACE] Quality measures:', qualityMeasures.map(m => `${m.measureId}:${m.submissionMethod}`));
      
      // MVP pathway preview: the table shows the MVP's FULL quality pool (owner
      // decision 2026-08-05) — practice-tracked measures behave normally, pool
      // measures the practice doesn't track render greyed (mvpUntrackedIds).
      // QCDR ids never appear here (Explore-tab-only). Traditional mode keeps
      // the legacy expression byte-for-byte.
      const mvpQualityPool = pathwayMvpId
        ? new Set((dataService.getMvp(pathwayMvpId, programYear)?.qualityMeasureIds ?? [])
            .filter(id => !isQcdrMeasureId(id)))
        : null;

      const measuresToShow = mvpQualityPool
        ? qualityMeasures.filter(m => mvpQualityPool.has(m.measureId))
        : (practiceSelectedMeasures.size > 0
        ? qualityMeasures.filter(m => {
            const key = `${m.measureId}:${m.submissionMethod}`;
            const isSelected = practiceSelectedMeasures.has(key);
            if (m.measureId === '047' || m.measureId === '508' || m.measureId === '001' || m.measureId === '118') {
              console.log(`[TRACE] Checking ${key}: isSelected=${isSelected}`);
            }
            return isSelected;
          })
        : []); // Only show measures that are actually selected in the database

      setMvpUntrackedIds(mvpQualityPool
        ? new Set(measuresToShow
            .filter(m => !practiceSelectedMeasures.has(`${m.measureId}:${m.submissionMethod}`))
            .map(m => `${m.measureId}:${m.submissionMethod}`))
        : new Set());
      
      console.log('[Summary] measuresToShow has', measuresToShow.length, 'measures after filtering');
      
      console.log('[TRACE] Measures to show after filtering:', measuresToShow.map(m => `${m.measureId}:${m.submissionMethod}`));
      console.log('[TRACE] Showing 047?', measuresToShow.some(m => m.measureId === '047'));
      console.log('[TRACE] Showing 508?', measuresToShow.some(m => m.measureId === '508'));
      console.log('[TRACE] Showing 052?', measuresToShow.some(m => m.measureId === '052'));
      
      // Check if 052 is in the summaryMap
      console.log('[DEBUG] Is 052:MIPS CQM in summaryMap?', summaryMap.has('052:MIPS CQM'));
      if (summaryMap.has('052:MIPS CQM')) {
        console.log('[DEBUG] 052:MIPS CQM data:', summaryMap.get('052:MIPS CQM'));
      }
      
      
      for (const measureWithMethod of measuresToShow) {
        const { measureId, submissionMethod, title } = measureWithMethod;
        const lookupKey = `${measureId}:${submissionMethod}`;

        // Get algorithm definition from strata config JSON (unchanged)
        const strataCfg = dataService.getStrataConfig(measureId, submissionMethod);
        const algorithm = strataCfg?.performanceRate;

        // Get summary from batch-loaded data
        const summary = summaryMap.get(lookupKey);
        
        // Determine if this is a multi-strata measure from configuration
        const isMultiStrata = dataService.isMultiStrataMeasure(measureId, submissionMethod);

        // DEBUG: Comprehensive logging for Measure 052
        if (measureId === '052') {
          console.log('=== MEASURE 052 DEBUG ===');
          console.log('1. Lookup key:', lookupKey);
          console.log('2. Summary found?', !!summary);
        if (summary) {
            console.log('3. Summary data:', JSON.stringify(summary, null, 2));
          }
          console.log('4. Config says multi-strata?', isMultiStrata);
          console.log('5. Strata config:', strataCfg);
          console.log('6. CMS strata definitions:', dataService.getStrataDefinitions(measureId));
        }

        if (summary) {
          // COMPREHENSIVE FIX: Handle multi-strata measures with incomplete data
          // This ensures measures like 052 work even when only partial strata have data
          
          // Check if configuration says this is multi-strata but API doesn't recognize it as such
          // This happens when only 1 stratum has data (e.g., Measure 052 with only "spirometry")
          const configSaysMultiStrata = isMultiStrata;
          const apiSaysMultiStrata = summary.isMultiStrata;
          const needsMultiStrataOverride = configSaysMultiStrata && !apiSaysMultiStrata;
          
          if (measureId === '052') {
            console.log('7. API says multi-strata?', apiSaysMultiStrata);
            console.log('8. Needs override?', needsMultiStrataOverride);
            console.log('9. Has counts?', !!summary.counts);
            console.log('10. Has strata?', !!summary.strata);
          }
          
          // Create a processed summary - always use configuration's determination for multi-strata
          let processedSummary = {
            ...summary,
            isMultiStrata: configSaysMultiStrata || apiSaysMultiStrata
          };
          
        // Special handling when config says multi-strata but API returns single-strata format
        // CRITICAL FIX: Check for empty strata array as well as missing strata
        // The API may return an empty strata array [] along with counts object
        if (needsMultiStrataOverride && summary.counts && (!summary.strata || summary.strata.length === 0)) {
          // API returned single-strata format (counts object) but config says it should be multi-strata
          // The API now provides 'singleStratum' field to tell us which stratum this data belongs to
          
          // Get the defined strata for this measure
          const cmsStrataDefinitions = dataService.getStrataDefinitions(measureId);
          
          if (cmsStrataDefinitions.length > 0) {
            // Use the stratum name provided by the API if available
            // Otherwise fall back to first stratum (for backward compatibility)
            const actualStratumName = (summary as any).singleStratum || cmsStrataDefinitions[0].name || '';
            
            // Find the matching CMS definition for this stratum
            const matchingDefinition = cmsStrataDefinitions.find(def => 
              def.name === actualStratumName
            ) || cmsStrataDefinitions[0];
            
            if (measureId === '052' && (summary as any).singleStratum) {
              console.log(`[FIX] Using API-provided stratum: ${(summary as any).singleStratum}`);
            }
            
            processedSummary.strata = [{
              name: matchingDefinition.name || actualStratumName,
              description: matchingDefinition.description,
              ...summary.counts
            }];
          } else {
            // No CMS strata definitions - use the API-provided stratum name or default
            processedSummary.strata = [{
              name: (summary as any).singleStratum || 'default',
              ...summary.counts
            }];
          }
          
          delete processedSummary.counts;
        }
          
          if (measureId === '052') {
            console.log('11. Processed summary after conversion:', JSON.stringify(processedSummary, null, 2));
          }
          
          const row = toMeasureRow(processedSummary, title) as MeasureRow;
          
          if (measureId === '052') {
            console.log('12. Row after toMeasureRow:', JSON.stringify(row, null, 2));
          }
          
          // Check if this measure has custom strata (e.g., 118_A, 118_B)
          const hasCustomStrataFlag = row.strata && hasCustomStrata(row.strata);
          
          // Override algorithm for custom strata measures
          if (hasCustomStrataFlag) {
            row.overallAlgorithm = getCustomStrataAlgorithm(); // Always "Weighted Average"
          } else if (summary.overallAlgorithm) {
            // Use the algorithm from the API if provided (for custom strata detected on backend)
            row.overallAlgorithm = summary.overallAlgorithm;
          } else {
            row.overallAlgorithm = algorithm;
          }
          
          // Ensure isMultiStrata reflects configuration
          row.isMultiStrata = isMultiStrata;
          
          // For CMS multi-strata measures, ensure ALL defined strata are present
          if (isMultiStrata) {
            const cmsStrataDefinitions = dataService.getStrataDefinitions(measureId);
            
            if (cmsStrataDefinitions.length > 0) {
              // This is a CMS-defined strata measure
              if (!row.strata || row.strata.length === 0) {
                // No strata data at all - populate with definitions and zero values
                row.strata = cmsStrataDefinitions.map(def => ({
                  name: def.name || '',
                  description: def.description,
                  ip: 0,
                  ex: 0,
                  den: 0,
                  met: 0,
                  exc: 0,
                  not: 0,
                  pr: 0,
                  dc: 0
                }));
                // Recalculate overall rates with the zero strata
                row.overallPr = 0;
                row.overallDc = 0;
                row.pr = 0;
                row.dc = 0;
              } else {
                // Some strata exist - check if any are missing
                const existingStrataNames = new Set(row.strata.map(s => s.name));
                const missingStrata = cmsStrataDefinitions.filter(def => 
                  !existingStrataNames.has(def.name || '')
                );
                
                // Add any missing strata with zero values
                if (missingStrata.length > 0) {
                  const zeroStrata = missingStrata.map(def => ({
                    name: def.name || '',
                    description: def.description,
                    ip: 0,
                    ex: 0,
                    den: 0,
                    met: 0,
                    exc: 0,
                    not: 0,
                    pr: 0,
                    dc: 0
                  }));
                  
                  row.strata = [...row.strata, ...zeroStrata];
                  
                  // Recalculate overall rates with all strata
                  row.overallPr = calculateOverallPerformanceRate(row.strata, row.overallAlgorithm || 'Simple Average');
                  row.overallDc = calculateOverallDataCompletenessRate(row.strata, 'Weighted Average');
                  row.pr = row.overallPr;
                  row.dc = row.overallDc;
                }
              }
            }
          }
          
          if (measureId === '052') {
            console.log('13. Final row before pushing to measures array:', JSON.stringify(row, null, 2));
            console.log('14. Row.isMultiStrata:', row.isMultiStrata);
            console.log('15. Row.strata:', row.strata);
          }
          
          measures.push(row);
        } else {
          // Fallback for measures with no data
          const fallbackRow = {
            id: measureId,
            title,
            submissionMethod,
            isMultiStrata, // Use configuration-based determination
            ip: 0,
            ex: 0,
            den: 0,
            met: 0,
            exc: 0,
            not: 0,
            dc: 0,
            pr: 0,
            overallAlgorithm: algorithm,
            // If multi-strata, populate with strata definitions and zero values
            ...(isMultiStrata ? { 
              strata: dataService.getStrataDefinitions(measureId).map(def => ({
                name: def.name || '',
                description: def.description,
                ip: 0,
                ex: 0,
                den: 0,
                met: 0,
                exc: 0,
                not: 0,
                pr: 0,
                dc: 0
              })),
              overallPr: 0,
              overallDc: 0
            } : {}),
          };
          measures.push(fallbackRow);
        }
      }
    
    // OPTION 2: Store calculation modes and manual values in baseData
    // Enhance baseData with mode and manual values from backend
    const enhancedMeasures = measures.map(measure => {
      const compositeId = `${measure.id}:${measure.submissionMethod}`;
      const mode = measureModes.get(compositeId) || 'calculated';
      const manualValues = measureManualValues.get(compositeId);
      
      // Add mode and manual values to the measure
      return {
        ...measure,
        calculationMode: mode,
        manualValues: mode === 'manual' ? manualValues : undefined,
      };
    });
    
    console.log('[OPTION2] Enhanced measures with modes:', enhancedMeasures.filter(m => m.calculationMode === 'manual').map(m => ({
      id: m.id,
      mode: m.calculationMode
    })));
    
    // Add partial measures for any selected parent measures
    const partialMeasuresToAdd: MeasureRow[] = [];
    const parentMeasureIds = new Set(enhancedMeasures.map(m => m.id));
    
    // Check summaryMap for any partial measures that correspond to selected parent measures
    for (const [key, summary] of summaryMap.entries()) {
      const measureId = summary.measureId;
      
      // Check if this is a partial measure (ends with _P)
      if (measureId.endsWith('_P')) {
        // Extract parent measure ID (e.g., "005_P" -> "005")
        const parentMeasureId = measureId.slice(0, -2);
        
        // If the parent measure is selected, include this partial measure
        if (parentMeasureIds.has(parentMeasureId)) {
          console.log(`[PARTIAL] Adding partial measure ${measureId} because parent ${parentMeasureId} is selected`);
          
          // Create a measure row for the partial measure
          const partialMeasureRow = toMeasureRow(summary, `${parentMeasureId} (Partial)`) as MeasureRow;
          
          // Mark it as a partial measure for easy identification
          partialMeasureRow.id = measureId;
          partialMeasureRow.title = `${parentMeasureId} - Partial Measure`;
          
          // Add calculation mode (always calculated for partial measures)
          const enhancedPartialMeasure = {
            ...partialMeasureRow,
            calculationMode: 'calculated' as const,
            manualValues: undefined,
          };
          
          partialMeasuresToAdd.push(enhancedPartialMeasure);
        }
      }
    }
    
    // Combine regular measures with partial measures
    const allMeasuresIncludingPartial = [...enhancedMeasures, ...partialMeasuresToAdd];
    
    console.log(`[PARTIAL] Added ${partialMeasuresToAdd.length} partial measures to baseData`);
    console.log('[PARTIAL] Partial measures:', partialMeasuresToAdd.map(m => m.id));
    
    // Set measure catalog for the AddMeasureModal with all submission methods
      const catalog = allMeasuresWithSubmissionMethods
        .filter(m => m.category === 'quality')
        .map(m => ({ 
          id: `${m.measureId}:${m.submissionMethod}`,
          title: `${m.title} (${m.submissionMethod})`
        }));
      
      // Debug logging for measure 493
      const measure493InCatalog = catalog.find(m => m.id.startsWith('493:'));
      if (measure493InCatalog) {
        console.log('[CATALOG] Measure 493 found in catalog:', measure493InCatalog);
      } else {
        console.log('[CATALOG] Measure 493 NOT found in catalog');
      }
    
    // Debug: Check for measure 493 in baseData
    const measure493InBase = enhancedMeasures.find(m => m.id === '493');
    if (measure493InBase) {
      console.log('[BASEDATA] Measure 493 found in baseData:', {
        id: measure493InBase.id,
        submissionMethod: measure493InBase.submissionMethod,
        isMultiStrata: measure493InBase.isMultiStrata
      });
    } else {
      console.log('[BASEDATA] Measure 493 NOT found in baseData');
    }
    
    // DEBUG: Check if Measure 052 made it to the final data
    console.log('[FINAL DEBUG] allMeasuresIncludingPartial contains 052?', 
      allMeasuresIncludingPartial.some(m => m.id === '052'));
    if (allMeasuresIncludingPartial.some(m => m.id === '052')) {
      const measure052 = allMeasuresIncludingPartial.find(m => m.id === '052');
      console.log('[FINAL DEBUG] Measure 052 data:', measure052);
    }
    
    // BATCH UPDATE: Set all states together to reduce renders
    // React 18 automatically batches these, but we can be explicit
    rowsLoadedForPathway.current = activeDataKey; // stamp BEFORE rows recompute (race guard)
    setBaseData(allMeasuresIncludingPartial);
    setMeasureCatalog(catalog);
    setInitialLoad(false); // Mark initial load complete
    setHasLoadedOnce(true); // We've successfully loaded data
    
    // OPTION 2: Clean up old localStorage items that are no longer needed
    localStorage.removeItem("gaps:additions");
    } catch (err: any) {
      // Ignore abort errors (happens when switching practices quickly)
      if (err.name === 'AbortError') {
        console.log('Measure loading cancelled due to practice switch');
        return;
      }
      console.error('Gaps page: Error loading measures:', err);
      setError(err instanceof Error ? err : new Error('Failed to load measures'));
      setInitialLoad(false); // Mark initial load complete even on error
      toast({
        title: "Error loading measures",
        description: err instanceof Error ? err.message : "An unexpected error occurred",
        variant: "destructive"
      });
    }
  };

  loadMeasureData();
  
  // Cleanup function to abort pending requests
  return () => {
    abortController.abort();
    // Reset mount flag on unmount
    isMountedRef.current = false;
  };
}, [selectedPracticeId, dataVersion, toast, evaluationType, selectedNpi, refreshTrigger, programYear, pathway, viewSubgroupId]);

  // Silent refresh function - updates data without loading states
  const silentRefresh = useCallback(async () => {
    if (!selectedPracticeId) return;

    // MVP preview mode: skip — the merge below fetches GROUP-lane summaries,
    // which would clobber subgroup-derived counts; preview data is load-time.
    if (pathway !== 'traditional') return;

    // [B3] Hold last-good numbers while Phase 3 is writing this practice's
    // outcomes; the phase3 completed/failed SSE handler clears the hold and
    // triggers the refresh itself.
    if (isPhase3HoldActive()) {
      console.log('[Summary] Silent refresh skipped - Phase 3 in flight (holding last-good data)');
      return;
    }
    
    try {
      const refreshStartTime = new Date().toISOString();
      console.log('[Summary] Starting silent refresh at:', refreshStartTime);
      
      // Clear cache to ensure fresh data
      clearOutcomeCache();
      console.log('[Summary] Cache cleared at:', new Date().toISOString());
      
      // Fetch fresh summaries without setting loading state
      console.log('[Summary] API call START at:', new Date().toISOString());
      const evaluationContext = { 
        evaluationType, 
        npi: evaluationType === 'NPI' ? selectedNpi : undefined,
        year: programYear
      };
      const allSummaries = await getAllOutcomeSummaries(undefined, evaluationContext);
      console.log('[Summary] API call COMPLETE at:', new Date().toISOString(), 'Records received:', allSummaries.length);
      const summaryMap = new Map(
        allSummaries.map(s => [`${s.measureId}:${s.submissionMethod}`, s])
      );
      
      // Update baseData silently by merging with existing data
      const dataService = MipsDataService.getInstance();
      setBaseData(prevData => {
        return prevData.map(measure => {
          const key = `${measure.id}:${measure.submissionMethod}`;
          const freshSummary = summaryMap.get(key);
          
          if (freshSummary) {
            // Merge fresh data while preserving UI state
            const updatedMeasure = toMeasureRow(freshSummary, measure.title);

            // CRITICAL: Preserve config-based isMultiStrata determination.
            // The API may report isMultiStrata: false for manual-mode measures
            // that have no patient outcomes yet.  The config is the source of truth.
            const configIsMultiStrata = dataService.isMultiStrataMeasure(
              measure.id, measure.submissionMethod
            );

            return {
              ...measure,
              ...updatedMeasure,
              // Preserve UI-specific state
              isAdded: measure.isAdded,
              mode: measure.mode,
              manualValues: measure.manualValues,
              // Use config-based determination (matches loadMeasureData logic)
              isMultiStrata: configIsMultiStrata || updatedMeasure.isMultiStrata,
              // Preserve strata from measure if the fresh data lost it
              strata: (configIsMultiStrata && (!updatedMeasure.strata || updatedMeasure.strata.length === 0))
                ? measure.strata
                : updatedMeasure.strata,
            };
          }
          return measure;
        });
      });
      
      console.log('[Summary] Silent refresh completed');
    } catch (error) {
      console.error('[Summary] Silent refresh failed:', error);
      // Don't show error to user - they have working data
    }
  }, [selectedPracticeId, evaluationType, selectedNpi, programYear, isPhase3HoldActive, pathway]);
  
  // Keep the ref updated with the latest silentRefresh function
  useEffect(() => {
    silentRefreshRef.current = silentRefresh;
  }, [silentRefresh]);

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
  
  // Listen for manual edits completion to refresh the summary
  useSSE(`${apiUrl}/api/sse/calculation-progress`, {
    onMessage: useCallback((data) => {
      if (data.type === 'manual_edit_completed' && String(data.practiceId) === selectedPracticeId) {
        console.log('[Summary] Manual edit completed via SSE, refreshing silently...');
        silentRefresh();
      }
    }, [selectedPracticeId, silentRefresh]),
    onError: useCallback((error) => {
      console.error("[SSE Error in Summary - calculation]:", error);
    }, []),
  });

  // Listen for Phase 3 progress: hold last-good data while it runs, refresh
  // once when it completes (or fails — the API gate then serves the previous
  // fully-complete batch).
  useSSE(`${apiUrl}/api/sse/upload-progress`, {
    onMessage: useCallback((data) => {
      if (data.phase !== 'phase3' || String(data.practiceId) !== selectedPracticeId) return;

      if (data.status === 'processing') {
        console.log('[Summary] Phase 3 started via SSE - holding last-good data');
        phase3HoldStartedRef.current = Date.now();
        setPhase3InFlight(true);
      } else if (data.status === 'completed' || data.status === 'failed') {
        console.log(`[Summary] Phase 3 ${data.status} via SSE for this practice, refreshing silently...`);
        phase3HoldStartedRef.current = 0;
        setPhase3InFlight(false);
        silentRefresh();
      }
    }, [silentRefresh, selectedPracticeId]),
    onError: useCallback((error) => {
      console.error("[SSE Error in Summary - upload]:", error);
    }, []),
  });

  // Smart navigation detection with silent refresh
  // Detail URLs use /measure/ (e.g. /quality/measure/001:MIPS%20CQM)
  useEffect(() => {
    const isDetailPath = location.pathname.includes('/measure/');
    const wasInDetailView = sessionStorage.getItem('wasInDetailView') === 'true';
    const hasPendingUpdate = sessionStorage.getItem('detailUpdatePending') === 'true';
    
    if (!isDetailPath && wasInDetailView) {
      // Returning from detail view
      console.log('[Summary] Returning from detail view');
      sessionStorage.removeItem('wasInDetailView');
      
      // Always do a silent refresh when returning from detail (data may have changed)
      console.log('[Summary] Returning from detail view, refreshing silently...');
      if (hasPendingUpdate) {
        sessionStorage.removeItem('detailUpdatePending');
      }
      silentRefresh();
    } else if (isDetailPath) {
      // Navigating to detail view
      sessionStorage.setItem('wasInDetailView', 'true');
    }
    
    setPreviousPath(location.pathname);
  }, [location.pathname, silentRefresh]);

  // No migration needed - there's no important legacy data
  
  // Method-aware meta accessor: prefer submission method if provided
  const metaForMethod = useCallback(
    (measureId: string, submissionMethod?: string) => rowMetaFor(MipsDataService.getInstance(), measureId, submissionMethod),
    [],
  );

  // Sorting for Quality Summary table
  type SortKey = 'points' | 'pr' | 'dc' | 'title' | 'id';
  const sortOptions: { key: SortKey; label: string }[] = [
    { key: 'points', label: 'Points' },
    { key: 'pr', label: 'Performance Rate' },
    { key: 'dc', label: 'Complete Rate' },
    { key: 'title', label: 'Title' },
    { key: 'id', label: 'Measure ID' },
  ];
  const [sorters, setSorters] = useState<{ key: SortKey; dir: 'asc' | 'desc' }[]>([
    { key: 'points', dir: 'desc' },
  ]);
  const [sortOpen, setSortOpen] = useState(false);

  // Filters (global, persisted)
  type Tri = 'any' | 'yes' | 'no';
  interface Filters {
    // Simplified, opportunities-focused filters
    policyIssues: boolean; // DC < 75% OR (!bonus AND den < 20)
    collectionTypes: string[]; // multi
    inverse: Tri;
    autoPts: Tri;
    cap7: Tri;

    // Legacy fields retained for persistence (not shown in UI)
    title?: string;
    id?: string;
    added: 'any' | 'base' | 'added';
    mode: 'any' | 'manual' | 'calc';
    calcPending: Tri;
    noData: Tri;
    ranges: {
      den: [number | null, number | null];
      pr: [number | null, number | null];
      dc: [number | null, number | null];
      cmsAvg: [number | null, number | null];
      points: [number | null, number | null];
      potentialGain: [number | null, number | null];
    };
  }
  const collectionTypesAll = ["MIPS CQM", "eCQM"] as const;
  const defaultFilters: Filters = {
    policyIssues: false,
    collectionTypes: [],
    inverse: 'any',
    autoPts: 'any',
    cap7: 'any',
    // legacy
    title: '',
    id: '',
    added: 'any',
    mode: 'any',
    calcPending: 'any',
    noData: 'any',
    ranges: { den: [null, null], pr: [null, null], dc: [null, null], cmsAvg: [null, null], points: [null, null], potentialGain: [null, null] },
  };
  const [filters, setFilters] = useState<Filters>(() => {
    try { return JSON.parse(userStorage.getItem('gaps:filters') || 'null') ?? defaultFilters } catch { return defaultFilters }
  });
  useEffect(() => { userStorage.setItem('gaps:filters', JSON.stringify(filters)); }, [filters]);
  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.policyIssues) n++;
    n += filters.collectionTypes.length;
    if (filters.inverse !== 'any') n++;
    if (filters.autoPts !== 'any') n++;
    if (filters.cap7 !== 'any') n++;
    if (filters.title && filters.title.trim()) n++;
    return n;
  }, [filters]);
  const updateRange = (key: keyof Filters['ranges'], idx: 0 | 1, val: string) => {
    const num = val === '' ? null : Number(val);
    setFilters(prev => ({ ...prev, ranges: { ...prev.ranges, [key]: prev.ranges[key].map((v, i) => (i === idx ? (isNaN(num as any) ? null : num) : v)) as any } }));
  };
  const clearFilters = () => setFilters(defaultFilters);
  const presetPolicyReady = () => setFilters(prev => ({
    ...defaultFilters,
    policyIssues: true,
  }));
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedMeasure, setSelectedMeasure] = useState<any>(null);
  const [measureNotes, setMeasureNotes] = useState<Record<string, string>>(() => {
    try { return JSON.parse(userStorage.getItem("gaps:measureNotes") || "{}"); } catch { return {}; }
  });
  const [measureEvidence, setMeasureEvidence] = useState<Record<string, EvidenceFile[]>>(() => {
    try { return JSON.parse(userStorage.getItem("gaps:measureEvidence") || "{}"); } catch { return {}; }
  });
  
  useEffect(() => {
    userStorage.setItem("gaps:measureNotes", JSON.stringify(measureNotes));
  }, [measureNotes]);
  
  useEffect(() => {
    userStorage.setItem("gaps:measureEvidence", JSON.stringify(measureEvidence));
  }, [measureEvidence]);

  const [addOpen, setAddOpen] = useState(false);
  // OPTION 2: Removed 'added' array - all data comes from baseData (single source of truth)
  // Manual measures are tracked in baseData with calculation_mode from backend

  const [selectedBaseIds, setSelectedBaseIds] = useState<string[]>([]);
  
  // Initialize selectedBaseIds when practice changes
  useEffect(() => {
    // Clean up old non-practice-specific storage (migration)
    const oldKey = 'gaps:selectedBaseIds';
    if (userStorage.getItem(oldKey)) {
      userStorage.removeItem(oldKey);
      console.log('[Migration] Removed old non-practice-specific selections');
    }
    
    if (!selectedPracticeId) {
      setSelectedBaseIds([]);
      return;
    }
    
    // Don't clear selectedBaseIds here - the sync effect will handle it
    // This was causing flicker during navigation
    console.log(`[SYNC-FIX] Practice initialized: ${selectedPracticeId}`);
  }, [selectedPracticeId]);
  
  // CRITICAL FIX: Always sync selectedBaseIds with baseData (database source of truth)
  useEffect(() => {
    console.log('[SYNC-FIX] Sync effect triggered');
    console.log('[SYNC-FIX] selectedPracticeId:', selectedPracticeId);
    console.log('[SYNC-FIX] baseData.length:', baseData.length);
    
    if (!selectedPracticeId || baseData.length === 0) {
      console.log('[SYNC-FIX] Skipping sync - no practice or empty baseData');
      return;
    }
    
    // The database is authoritative - baseData contains exactly what's active in the DB
    // EXCLUDE partial measures from being displayed in the UI
    const dbMeasureIds = baseData
      .filter(b => !b.id.endsWith('_P')) // Exclude partial measures from UI
      .map(b => {
        return b.submissionMethod ? `${b.id}:${b.submissionMethod}` : b.id;
      });
    
    console.log('[SYNC-FIX] Syncing selectedBaseIds with database state');
    console.log('[SYNC-FIX] DB measures (baseData):', dbMeasureIds);
    console.log('[SYNC-FIX] Current selectedBaseIds before sync:', selectedBaseIds);
    console.log('[SYNC-FIX] Excluded partial measures from sync');
    
    // Check if they're already in sync (use spread to avoid mutating arrays)
    const areInSync = JSON.stringify([...dbMeasureIds].sort()) === JSON.stringify([...selectedBaseIds].sort());
    console.log('[SYNC-FIX] Are they already in sync?', areInSync);
    
    // Only update if not already in sync - prevents unnecessary re-renders
    if (!areInSync) {
      setSelectedBaseIds(dbMeasureIds);
      
      // Also update localStorage to stay in sync
      const storageKey = `gaps:selectedBaseIds:${selectedPracticeId}`;
      userStorage.setItem(storageKey, JSON.stringify(dbMeasureIds));
      console.log('[SYNC-FIX] Updated selectedBaseIds and localStorage to match DB state');
    } else {
      console.log('[SYNC-FIX] Already in sync, skipping state update');
    }
  }, [baseData, selectedPracticeId]);
  
  // Note: selectedBaseIds is now saved in the sync effect above
  const selectedIds = useMemo(() => new Set<string>([
    ...selectedBaseIds,
  ]), [selectedBaseIds]);

  // Column settings (visibility, order, tooltips)
  type ColumnKey = 'id' | 'title' | 'collectionType' | 'measureType' | 'flags' | 'inverse' | 'autoPts' | 'cmsAvg' | 'registryAvg' | 'cap7' | 'ip' | 'ex' | 'den' | 'met' | 'exc' | 'not' | 'dc' | 'pr' | 'points';
  interface ColumnSettingsValue { order: ColumnKey[]; visibility: Record<ColumnKey, boolean>; tooltips: boolean }
  const allColumnDefs: { key: ColumnKey; label: string; tooltip?: string; lock?: boolean }[] = [
    { key: 'id', label: 'Measure ID', tooltip: 'MIPS/QCDR measure identifier', lock: true },
    { key: 'title', label: 'Title', tooltip: 'Measure title' },
    { key: 'collectionType', label: 'Collection Type', tooltip: 'Submission method (QCDR, MIPS CQM, eCQM)' },
    { key: 'measureType', label: 'Measure Type', tooltip: 'Type of measure (Process, Outcome, etc.)' },
    { key: 'flags', label: 'Flags', tooltip: 'Collection Type, Measure Type, and other flags (Inverse, Bonus, 7‑pt Cap)' },
    { key: 'inverse', label: 'Inverse', tooltip: 'Lower performance is better' },
    { key: 'autoPts', label: 'Bonus Pts', tooltip: 'Qualifies for automatic bonus points' },
    { key: 'cmsAvg', label: "CMS Avg", tooltip: 'CMS national average performance' },
    { key: 'registryAvg', label: 'Registry Avg.', tooltip: 'Locally entered registry average for this measure' },
    { key: 'cap7', label: '7-pt Cap', tooltip: 'Measure capped at 7 points' },
    { key: 'ip', label: 'Initial Pop.', tooltip: 'Initial Population' },
    { key: 'ex', label: 'Denominator Exclusion', tooltip: 'Denominator exclusion' },
    { key: 'den', label: 'Denominator', tooltip: 'Eligible patients' },
    { key: 'met', label: 'Num Met', tooltip: 'Numerator met' },
    { key: 'exc', label: 'Num Exception', tooltip: 'Numerator exception' },
    { key: 'not', label: 'Num Not Met', tooltip: 'Numerator not met' },
    { key: 'dc', label: 'Complete Rate', tooltip: 'Reported/Eligible' },
    { key: 'pr', label: 'Performance Rate', tooltip: 'Numerator / Denominator' },
    { key: 'points', label: 'Est. Points', tooltip: 'Estimated points; shows gain when simulation is active' },
  ];

  // Presets
  interface ColumnPreset { id: string; name: string; settings: ColumnSettingsValue; builtin?: boolean }

  const summaryOrder: ColumnKey[] = ['id','title','flags','cmsAvg','registryAvg','dc','pr','points'];
  const summaryVisibility: Record<ColumnKey, boolean> = {
    id: true, title: true, collectionType: false, measureType: false, flags: true, inverse: false, autoPts: false, cmsAvg: true, registryAvg: false, cap7: false,
    ip: false, ex: false, den: false, met: false, exc: false, not: false, dc: true, pr: true, points: true,
  };
  const countsOrder: ColumnKey[] = ['id','title','den','met','exc','not','dc','pr','points'];
  const countsVisibility: Record<ColumnKey, boolean> = {
    id: true, title: true, collectionType: false, measureType: false, flags: false, inverse: false, autoPts: false, cmsAvg: false, registryAvg: false, cap7: false,
    ip: false, ex: false, den: true, met: true, exc: true, not: true, dc: true, pr: true, points: true,
  };
  const fullViewOrder: ColumnKey[] = ['id','title','flags','cmsAvg','registryAvg','den','met','exc','not','dc','pr','points'];
  const fullViewVisibility: Record<ColumnKey, boolean> = {
    id: true, title: true, collectionType: false, measureType: false, flags: true, inverse: false, autoPts: false, cmsAvg: true, registryAvg: false, cap7: false,
    ip: false, ex: false, den: true, met: true, exc: true, not: true, dc: true, pr: true, points: true,
  };
  const registryAvgOrder: ColumnKey[] = ['id','title','flags','cmsAvg','registryAvg','den','met','exc','not','dc','pr','points'];
  const registryAvgVisibility: Record<ColumnKey, boolean> = {
    id: true, title: true, collectionType: false, measureType: false, flags: true, inverse: false, autoPts: false, cmsAvg: true, registryAvg: true, cap7: false,
    ip: false, ex: false, den: true, met: true, exc: true, not: true, dc: true, pr: true, points: true,
  };

  const builtInPresets: ColumnPreset[] = [
    { id: 'summary', name: 'Summary (Policy)', settings: { order: summaryOrder, visibility: summaryVisibility, tooltips: false }, builtin: true },
    { id: 'registry-average', name: 'Registry Avg.', settings: { order: registryAvgOrder, visibility: registryAvgVisibility, tooltips: false }, builtin: true },
    { id: 'counts', name: 'Counts (Operational)', settings: { order: countsOrder, visibility: countsVisibility, tooltips: false }, builtin: true },
    { id: 'full', name: 'Full View', settings: { order: fullViewOrder, visibility: fullViewVisibility, tooltips: false }, builtin: true },
  ];

  const defaultColSettings: ColumnSettingsValue = builtInPresets[0].settings;

  
  const [manageOpen, setManageOpen] = useState(false);
  const [presets, setPresets] = useState<ColumnPreset[]>(() => {
    try {
      const stored = JSON.parse(userStorage.getItem('gaps:colPresets') || 'null');
      const userPresets = (Array.isArray(stored) ? stored : []).filter(p => !p.builtin);
      // Always use current built-in presets (they override any stored versions)
      return [...builtInPresets, ...userPresets];
    } catch { return builtInPresets }
  });
  const [activePresetId, setActivePresetId] = useState<string>(() => {
    try { return userStorage.getItem('gaps:activePresetId') || 'summary' } catch { return 'summary' }
  });
  const [colSettings, setColSettings] = useState<ColumnSettingsValue>(() => {
    try { 
      const activeId = userStorage.getItem('gaps:activePresetId') || 'summary';
      const activePreset = builtInPresets.find(p => p.id === activeId);
      
      // If active preset is a built-in preset, use its current definition
      if (activePreset) {
        return activePreset.settings;
      }
      
      // Otherwise use stored settings or default
      return JSON.parse(userStorage.getItem('gaps:colSettings') || 'null') ?? defaultColSettings;
    } catch { return defaultColSettings }
  });
  useEffect(() => {
    userStorage.setItem('gaps:colSettings', JSON.stringify(colSettings));
  }, [colSettings]);
  useEffect(() => {
    userStorage.setItem('gaps:colPresets', JSON.stringify(presets));
  }, [presets]);
  useEffect(() => {
    userStorage.setItem('gaps:activePresetId', activePresetId);
  }, [activePresetId]);





  const applyPreset = (id: string) => {
    const p = presets.find(x => x.id === id);
    if (!p) return;
    setColSettings(p.settings);
    setActivePresetId(id);
  };
  const saveCurrentAsPreset = () => {
    const name = window.prompt('Preset name');
    if (!name) return;
    const id = `user-${Date.now()}`;
    const p: ColumnPreset = { id, name, settings: colSettings };
    setPresets(prev => [...prev, p]);
    setActivePresetId(id);
    toast({ title: 'Preset saved', description: `"${name}" is now available.` });
  };
  const deleteCurrentPreset = () => {
    const cur = presets.find(p => p.id === activePresetId);
    if (!cur || cur.builtin) return;
    if (!window.confirm(`Delete preset "${cur.name}"?`)) return;
    setPresets(prev => prev.filter(p => p.id !== cur.id));
    setActivePresetId('summary');
    setColSettings(builtInPresets[0].settings);
    toast({ title: 'Preset deleted' });
  };

  const visibleOrder = useMemo(() => colSettings.order.filter(k => colSettings.visibility[k]), [colSettings]);
  const availableMeasures = useMemo(() => {
    const available = measureCatalog.filter(m => !selectedIds.has(m.id));
    
    // Debug logging for measure 493
    const measure493InCatalog = measureCatalog.find(m => m.id.startsWith('493:'));
    const measure493InSelected = Array.from(selectedIds).find(id => id.startsWith('493:'));
    const measure493InAvailable = available.find(m => m.id.startsWith('493:'));
    
    console.log('[AVAILABLE] Measure 493 status:', {
      inCatalog: measure493InCatalog ? measure493InCatalog.id : 'not found',
      inSelectedIds: measure493InSelected || 'not selected',
      inAvailable: measure493InAvailable ? measure493InAvailable.id : 'not available (already selected)'
    });
    
    return available;
  }, [selectedIds, measureCatalog]);

  const handleAddMeasure = async (compositeId: string) => {
    console.log('[OPTION2-ADD] Starting handleAddMeasure for:', compositeId);
    
    // Check current state
    console.log('[OPTION2-ADD] Current baseData:', baseData.map(b => `${b.id}:${b.submissionMethod}`));
    console.log('[OPTION2-ADD] Current selectedBaseIds:', selectedBaseIds);
    
    // Check if measure already exists in baseData
    const { measureId, submissionMethod } = parseCompositeId(compositeId);
    const existsInBaseData = baseData.some(b => b.id === measureId && b.submissionMethod === submissionMethod);
    console.log('[OPTION2-ADD] Exists in baseData?', existsInBaseData);
    
    const found = measureCatalog.find(m => m.id === compositeId);
    if (!found) {
      console.log('[TRACE-ADD] Measure not found in catalog');
      return;
    }
    
    // Save to database first
    if (selectedPracticeId) {
      const { measureId, submissionMethod } = parseCompositeId(compositeId);
      console.log('[TRACE-ADD] Parsed:', { measureId, submissionMethod });
      
      try {
        console.log('[TRACE-ADD] Sending POST request to save measure...');
        const response = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/measures?year=${programYear}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
          body: JSON.stringify({
            measure_id: measureId,
            measure_category: 'quality',
            submission_method: submissionMethod || 'MIPS CQM',
          }),
        });
        
        const responseData = await response.text();
        console.log('[TRACE-ADD] Response status:', response.status);
        console.log('[TRACE-ADD] Response data:', responseData);
        
        if (!response.ok) {
          throw new Error(`Failed to save measure to database: ${responseData}`);
        }
        
        console.log(`[TRACE-ADD] Successfully saved measure ${measureId} to practice ${selectedPracticeId}`);
        
        // Trigger a full data refresh to ensure consistency with database
        console.log('[TRACE-ADD] Triggering data refresh to load new measure from database');
        setDataVersion(v => v + 1);
        
      } catch (error) {
        console.error('Error saving measure to database:', error);
        toast({
          title: "Error",
          description: "Failed to save measure. Please try again.",
          variant: "destructive",
        });
        return;
      }
    }
    
    setAddOpen(false);
    
    // Show success message
    toast({
      title: "Measure Added",
      description: `${found.title} has been added and will appear shortly.`,
    });
  };
  const removeAdded = async (id: string) => {
    console.log('[TRACE-DELETE] Starting removeAdded for:', id);
    
    // Special logging for measure 493
    if (id.startsWith('493:')) {
      console.log('[TRACE-DELETE] Deleting measure 493 - this is a strata measure');
    }
    
    // Remove from database if we have a selected practice
    if (selectedPracticeId) {
      try {
        const { measureId, submissionMethod } = parseCompositeId(id);
        console.log('[TRACE-DELETE] Parsed:', { measureId, submissionMethod });
        
        const deleteUrl = `${apiUrl}/api/practices/${selectedPracticeId}/measures/${measureId}?category=quality&submission_method=${submissionMethod || 'MIPS CQM'}&year=${programYear}`;
        console.log('[TRACE-DELETE] DELETE URL:', deleteUrl);
        
        const response = await fetch(deleteUrl, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
        });
        
        const responseData = await response.text();
        console.log('[TRACE-DELETE] Response status:', response.status);
        console.log('[TRACE-DELETE] Response data:', responseData);
        
        if (!response.ok) {
          throw new Error(`Failed to remove measure from database: ${responseData}`);
        }
        
        console.log(`[TRACE-DELETE] Successfully removed measure ${measureId} from practice ${selectedPracticeId}`);
      } catch (error) {
        console.error('Error removing measure from database:', error);
        // Show error but still remove from UI since user expects it
        toast({
          title: "Warning",
          description: "Measure removed locally but may not have been saved to database",
          variant: "destructive",
        });
      }
    }
    
    // Remove from baseData - the sync effect will automatically update selectedBaseIds
    const { measureId, submissionMethod } = parseCompositeId(id);
    setBaseData(prev => prev.filter(m => 
      !(m.id === measureId && m.submissionMethod === (submissionMethod || 'MIPS CQM'))
    ));
    console.log('[TRACE-DELETE] Removed from baseData, sync effect will update selectedBaseIds');
  };

  const setMode = async (id: string, v: "calc" | "manual") => {
    console.log('[OPTION2-SET-MODE] Switching measure', id, 'to mode:', v);
    console.log('[OPTION2-SET-MODE] Current selectedBaseIds:', selectedBaseIds);
    
    const base = baseData.find((b) => `${b.id}:${b.submissionMethod}` === id);
    if (!base) {
      console.log('[OPTION2-SET-MODE] ERROR: Measure not found in baseData');
      return;
    }

    const { measureId, submissionMethod } = parseCompositeId(id);

    // Update mode in backend
    if (selectedPracticeId) {
      try {
        const response = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/measures/${measureId}/mode?year=${programYear}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
          body: JSON.stringify({
            mode: v === 'manual' ? 'manual' : 'calculated',
            category: 'quality',
            submission_method: submissionMethod || 'MIPS CQM',
          }),
        });

        if (!response.ok) {
          console.error('[OPTION2-SET-MODE] Failed to update mode in backend');
          toast({
            title: "Error",
            description: "Failed to update measure mode",
            variant: "destructive",
          });
          return;
        }
        console.log('[OPTION2-SET-MODE] Successfully updated mode in backend to:', v);
        
        // OPTION 2: Update the baseData to reflect the new mode
        // This will trigger a re-render and the rows will be recalculated
        setBaseData(prev => prev.map(m => {
          if (m.id === measureId && m.submissionMethod === submissionMethod) {
            return {
              ...m,
              calculationMode: v,
              // If switching to manual and no manual values exist, initialize with blanks
              manualValues: v === 'manual' && !m.manualValues ? {
                ip: 0, ex: 0, den: 0, met: 0, not: 0, exc: 0,
                overallPr: 0, overallDc: 0
              } : m.manualValues
            };
          }
          return m;
        }));
        
        // If switching to manual mode, initialize drafts for editing
        if (v === 'manual') {
      const blankCounts = { ip: 0, ex: 0, den: 0, met: 0, not: 0, exc: 0 };
          setDrafts(prev => ({
            ...prev,
            [id]: {
              manual: base.manualValues || blankCounts
            }
          }));
        } else {
          // Clear drafts when switching back to calc mode
          setDrafts(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
        }
        
      } catch (error) {
        console.error('[OPTION2-SET-MODE] Error updating mode:', error);
        toast({
          title: "Error",
          description: "Failed to update measure mode",
          variant: "destructive",
        });
        return;
      }
    }
  };

  const removeAny = async (id: string) => {
    console.log('[REMOVE-ANY] Starting removal for:', id);
    
    // Check if it's in baseData (already in backend)
      const { measureId, submissionMethod } = parseCompositeId(id);
    const existsInBaseData = baseData.some(b => b.id === measureId && b.submissionMethod === submissionMethod);
    console.log('[REMOVE-ANY] Exists in baseData?', existsInBaseData);
    
    // If it exists in backend, delete it there
    if (existsInBaseData && selectedPracticeId) {
      try {
        const deleteUrl = `${apiUrl}/api/practices/${selectedPracticeId}/measures/${measureId}?category=quality&submission_method=${submissionMethod || 'MIPS CQM'}&year=${programYear}`;
        console.log('[REMOVE-ANY] Calling DELETE API:', deleteUrl);
        
        const response = await fetch(deleteUrl, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          },
        });
        
        if (!response.ok) {
          throw new Error(`Failed to remove measure from database: ${response.status}`);
        }
        
        console.log('[REMOVE-ANY] Successfully deleted from backend');
      } catch (error) {
        console.error('[REMOVE-ANY] Error removing from backend:', error);
        toast({
          title: "Warning",
          description: "Failed to remove measure from database",
          variant: "destructive",
        });
        // Continue with UI removal even if backend fails
      }
    }
    
    // Clear any drafts for this measure
    setDrafts(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    
    // Remove from baseData so the sync effect doesn't restore it
    setBaseData(prev => prev.filter(m => 
      !(m.id === measureId && m.submissionMethod === (submissionMethod || 'MIPS CQM'))
    ));
  };

  // Helper function to validate manual measure inputs
  const validateManualInputs = (
    field: "ip" | "ex" | "met" | "not" | "exc",
    value: number,
    currentManual: NonNullable<AddedMeasure["manual"]>
  ): { isValid: boolean; errorMessage?: string } => {
    // Rule 1: All values must be non-negative
    if (value < 0) {
      return { isValid: false, errorMessage: "Values cannot be negative" };
    }
    
    // Create a temporary updated state to check constraints
    const updated = { ...currentManual, [field]: value };
    
    // ALWAYS recalculate denominator to ensure it's correct
    // This fixes the bug where denominator might be 0 in the stored state
    updated.den = Math.max(0, updated.ip - updated.ex);
    
    // Rule 2: Exclusions cannot exceed Initial Population
    if (updated.ex > updated.ip) {
      return { isValid: false, errorMessage: "Exclusions cannot exceed Initial Population" };
    }
    
    // Rule 3: Met + Not Met + Exception cannot exceed Denominator
    const totalNumerator = updated.met + updated.not + updated.exc;
    if (totalNumerator > updated.den) {
      return { 
        isValid: false, 
        errorMessage: `Total numerator (${totalNumerator}) cannot exceed denominator (${updated.den})` 
      };
    }
    
    // Rule 4: Check Complete Rate (should be 0-100%)
    const completeRate = updated.den > 0 ? (totalNumerator / updated.den) * 100 : 0;
    if (completeRate > 100) {
      return { 
        isValid: false, 
        errorMessage: `Data completeness would be ${completeRate.toFixed(1)}% (max 100%)` 
      };
    }
    
    return { isValid: true };
  };
  const updateManual = (id: string, field: keyof NonNullable<AddedMeasure["manual"]>, value: number) => {
    // OPTION 2: Update drafts instead of added array
    const { measureId, submissionMethod } = parseCompositeId(id);
    const baseMeasure = baseData.find(b => b.id === measureId && b.submissionMethod === submissionMethod);
    
    if (!baseMeasure || baseMeasure.calculationMode !== 'manual') {
      return; // Can only update manual measures
    }
    
    const currentManual = drafts[id]?.manual || baseMeasure.manualValues || {
      ip: 0, ex: 0, den: 0, met: 0, not: 0, exc: 0
      };
      
      const validation = validateManualInputs(field as any, value, currentManual);
      if (!validation.isValid) {
        toast({
          title: "Invalid Input",
          description: validation.errorMessage,
          variant: "destructive",
        });
      return;
    }
    
    // Apply the change to drafts
    const updatedManual = { ...currentManual, [field]: value };
    
    // Recalculate den if IP or EX changed
    if (field === 'ip' || field === 'ex') {
      updatedManual.den = Math.max(0, updatedManual.ip - updatedManual.ex);
    }
    
    setDrafts(prev => ({
      ...prev,
      [id]: {
        ...prev[id],
        manual: updatedManual
      }
    }));
  };

  // Detect partial measures
  const partialMeasures = useMemo(() => {
    return baseData.filter(m => m.id.endsWith('_P'));
  }, [baseData]);
  
  const hasPartialMeasures = partialMeasures.length > 0;
  const partialMeasuresCount = partialMeasures.length;
  
  // Export partial measures function
  const exportPartialMeasures = async (measureIds?: string[]) => {
    // Validate practice is selected
    if (!selectedPracticeId) {
      toast({
        title: "No practice selected",
        description: "Please select a practice before exporting.",
        variant: "destructive",
      });
      return;
    }
    
    try {
      // Close modal immediately for better UX
      setPartialMeasuresModalOpen(false);
      setExportingPartialMeasures(true);
      
      const measuresToExport = measureIds || partialMeasures.map(m => m.id);
      
      if (measuresToExport.length === 0) {
        toast({
          title: "No measures to export",
          description: "Please select at least one partial measure to export.",
          variant: "destructive",
        });
        return;
      }
      
      // Show loading state
      toast({
        title: "Preparing export",
        description: `Fetching patient data for ${measuresToExport.length} partial measure(s)...`,
      });
      
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const token = localStorage.getItem('auth_token');
      const allPatientData: Record<string, any[]> = {};
      
      // Fetch patient data for each partial measure
      for (const measure of measuresToExport) {
        const measureData = partialMeasures.find(m => m.id === measure);
        if (!measureData) continue;
        
        try {
          const response = await fetch(
            `${apiUrl}/api/performance/${selectedPracticeId}/measures/${measure}/patients?submissionMethod=${measureData.submissionMethod || 'MIPS CQM'}&year=${programYear}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            }
          );
          
          if (response.ok) {
            const patientRows = await response.json();
            allPatientData[measure] = patientRows;
          } else {
            console.error(`Failed to fetch data for measure ${measure}`);
            allPatientData[measure] = [];
          }
        } catch (error) {
          console.error(`Error fetching data for measure ${measure}:`, error);
          allPatientData[measure] = [];
        }
      }
      
      // Create CSV content
      const headers = [
        "Measure_ID",
        "TIN",
        "Practice_Name",
        "Practice_Alias",
        "Patient_ID",
        "Full_Name",
        "DOB",
        "Gender",
        "NPI",
        "NPI_Name",
        "Date_of_Service",
        "CPT_Codes",
        "CPT_Modifiers",
        "ICD10_Codes",
        "Place_of_Service",
        "Initial_Population",
        "Denominator_Exclusion",
        "Denominator",
        "Numerator_Met",
        "Numerator_Exception",
        "Numerator_Not_Met",
        "Data_Source",
        "Notes",
      ];
      
      const allRows: any[] = [];
      
      for (const [measureId, patientRows] of Object.entries(allPatientData)) {
        patientRows.forEach(r => {
          allRows.push({
            Measure_ID: measureId,
            TIN: selectedPractice?.tin || "",
            Practice_Name: selectedPractice?.tin_name || "",
            Practice_Alias: selectedPractice?.practice_alias || "",
            Patient_ID: r.patient_id || r.pid,
            Full_Name: r.full_name || (r.first_name && r.last_name ? `${r.first_name} ${r.last_name}` : ""),
            DOB: r.date_of_birth || r.dob,
            Gender: r.gender,
            NPI: r.npi,
            NPI_Name: r.npi_name || r.npiName,
            Date_of_Service: r.date_of_service || r.dos,
            CPT_Codes: r.cpt_codes || r.cptCodes,
            CPT_Modifiers: r.cpt_modifiers || r.cptModifiers,
            ICD10_Codes: r.icd10_codes || r.icd10Codes,
            Place_of_Service: r.place_of_service || r.placeOfService,
            Initial_Population: r.ip ? "True" : "False",
            Denominator_Exclusion: r.ex ? "True" : "False",
            Denominator: r.den ? "True" : "False",
            Numerator_Met: r.met ? "True" : "False",
            Numerator_Exception: r.exc ? "True" : "False",
            Numerator_Not_Met: r.not ? "True" : "False",
            Data_Source: r.source || r.data_source || "",
            Notes: r.notes || "",
          });
        });
      }
      
      if (allRows.length === 0) {
        toast({
          title: "No data available",
          description: "No patient data found for the selected partial measures.",
          variant: "destructive",
        });
        return;
      }
      
      const csv = toCsv(allRows, headers);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      a.download = `partial-measures-${timestamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast({
        title: "Export complete",
        description: `Exported ${allRows.length} patient records from ${measuresToExport.length} partial measure(s).`,
      });
      
    } catch (error) {
      console.error('Export failed:', error);
      toast({
        title: "Export failed",
        description: "Failed to export partial measures data.",
        variant: "destructive",
      });
    } finally {
      setExportingPartialMeasures(false);
    }
  };

  const rows = useMemo(() => {
    console.log('[OPTION2-ROWS] Building rows from baseData only...');
    console.log('[OPTION2-ROWS] selectedBaseIds:', selectedBaseIds);
    console.log('[OPTION2-ROWS] baseData IDs:', baseData.map(b => `${b.id}:${b.submissionMethod}`));
    console.log('[OPTION2-ROWS] baseData.length:', baseData.length);
    
    const dataService = MipsDataService.getInstance();
    
    // Existing selected/locked measures (per-method via selectedBaseIds composites)
    const baseRows = selectedBaseIds.map((compositeId) => {
      const { measureId, submissionMethod } = parseCompositeId(compositeId);
      const src = baseData.find(b => b.id === measureId && b.submissionMethod === submissionMethod);
      if (!src) {
        console.log(`[TRACE-ROWS] No baseData found for ${compositeId} - measureId: ${measureId}, submissionMethod: ${submissionMethod}`);
        console.log(`[TRACE-ROWS] Available in baseData:`, baseData.map(b => ({ id: b.id, submission: b.submissionMethod })));
        return null;
      }
      return buildGapRow(src, compositeId, measureId, submissionMethod, drafts[compositeId], {
        practiceSize,
        simRule,
        simDelta,
        ds: dataService,
        meta: metaForMethod(measureId, submissionMethod),
      });
    }).filter(Boolean) as any[];

    // OPTION 2: No more addedRows - everything comes from baseData
    console.log('[OPTION2-ROWS] Total baseRows:', baseRows.length);
    console.log('[OPTION2-ROWS] Manual measures:', baseRows.filter((r: any) => r?.mode === 'manual').map((r: any) => r?.id));
    
    return baseRows;
  }, [simulate, simDelta, simRule, practiceSize, selectedBaseIds, baseData, metaForMethod, parseCompositeId, drafts]);

  // Top-6 (Traditional) / top-4 (MVP) submission strategy: which outcome measures to leave out of the
  // submission so a high-priority measure can hold the outcome/high-priority slot.
  const submissionTip = useMemo(() => {
    type Pick = { measureId: string; points: number };
    const label = (m: Pick) => `${m.measureId} (${m.points.toFixed(1)} pts)`;
    const build = (leaveOutList: Pick[], slotFilledBy: string, counted: string[], bestCount: string) => {
      const leaveOut = leaveOutList.map(label);
      return {
        key: [...leaveOutList.map(m => m.measureId), slotFilledBy].join('|'),
        leaveOut: leaveOut.length > 1 ? `${leaveOut.slice(0, -1).join(', ')} and ${leaveOut[leaveOut.length - 1]}` : leaveOut[0],
        slotFilledBy,
        counted: counted.join(', '),
        bestCount,
      };
    };
    if (pathwayMvpId) {
      const detail = mvpPreviewRow?.quality.detail as
        | { leaveOut?: Pick[]; selected?: Array<Pick & { reservedSlot: boolean }> }
        | undefined;
      const slot = detail?.selected?.find(s => s.reservedSlot);
      if (!detail?.leaveOut?.length || !slot) return null;
      return build(detail.leaveOut, slot.measureId, (detail.selected ?? []).map(s => s.measureId), 'four');
    }
    if (pathway !== 'traditional' || viewSubgroupId != null) return null;
    const ds = MipsDataService.getInstance();
    const measures = rows.filter(rowHasData).map(r => fromScorePayload(toCategoryPayload(r, practiceSize, ds)));
    const guidance = submissionGuidance(measures, computeQualityCategory(measures, 30, practiceSize));
    if (!guidance) return null;
    return build(guidance.leaveOut, guidance.slotFilledBy.measureId, guidance.counted.map(m => m.measureId), 'six');
  }, [rows, practiceSize, pathway, viewSubgroupId, pathwayMvpId, mvpPreviewRow]);
  const [dismissedTipKey, setDismissedTipKey] = useState<string | null>(null);

  // Auto-calculate quality scores when measure data changes
  const calculateQualityScores = useCallback(async () => {
    if (!selectedPracticeId || !rows || rows.length === 0) return;
    // MVP preview mode NEVER persists: the rows are pool-filtered, and posting
    // them would overwrite the practice's stored Traditional quality score.
    // Preview scores come from the engine (useMvpScoring), display-only.
    // ALL checks required: `pathway`/`viewSubgroupId` guard preview scope,
    // `rowsLoadedForPathway` guards the switch-back window where stale
    // MVP/subgroup rows are still mounted.
    if (pathway !== 'traditional' || viewSubgroupId != null
      || rowsLoadedForPathway.current !== TRADITIONAL_DATA_KEY) return;
    // When the viewed year is locked (read-only), the score-calc endpoint is
    // correctly blocked (403). The scores computed before locking remain valid
    // and are shown via GET /scores, so skip the (blocked) auto-recalc entirely
    // instead of firing it and surfacing a "Failed to calculate" warning.
    if (isCurrentYearLocked) return;
    
    setCalculatingScore(true);
    try {
      // Filter rows that have actual data (not just placeholders)
      const measuresWithData = rows.filter(row => 
        row.den > 0 || row.met > 0 || row.not > 0 || row.exc > 0
      );
      
      if (measuresWithData.length > 0) {
        console.log('Auto-calculating quality scores for', measuresWithData.length, 'measures');
        
        const result = await mipsScoreCalculator.calculateWithDebounce(
          selectedPracticeId,
          measuresWithData,
          500,
          programYear
        );
        
        if (result) {
          setMipsScores(prev => ({
            ...prev,
            qualityScore: Number(result.quality_score) || 0,
            piScore: Number(result.pi_score) || prev.piScore,
            iaScore: Number(result.ia_score) || prev.iaScore,
            costScore: Number(result.cost_score) || prev.costScore,
            patientComplexityBonus: Number(result.patient_complexity_bonus) || prev.patientComplexityBonus,
            compositeMipsScore: Number(result.composite_mips_score) || 0
          }));
          
          console.log('Quality scores updated:', result);
          setScoresCalculated(true);
        } else {
          setScoresCalculated(true);
        }
      } else {
        console.log('No measures with data -- resetting quality score to 0');
        setMipsScores(prev => ({
          ...prev,
          qualityScore: 0,
          compositeMipsScore: prev.piScore + prev.iaScore + prev.costScore + prev.patientComplexityBonus
        }));
        setScoresCalculated(true);
        // Persist the reset (same as the no-measures-selected branch below) —
        // otherwise the stale stored score survives in practice_category_scores
        // and every consumer of GET /scores (this header on next load, Overview,
        // Insights, MVP projections) keeps showing it.
        mipsScoreCalculator.calculateWithDebounce(selectedPracticeId, [], 500, programYear)
          .catch(err => console.error('Failed to reset stale scores:', err));
      }
    } catch (error) {
      console.error('Error calculating quality scores:', error);
      toast({
        title: "Score Calculation",
        description: "Failed to calculate quality scores. They will be updated on next save.",
        variant: "warning"
      });
    } finally {
      setCalculatingScore(false);
    }
  }, [selectedPracticeId, rows, programYear, isCurrentYearLocked, toast, pathway, viewSubgroupId]);
  
  // Trigger auto-calculation when rows change
  useEffect(() => {
    // MVP preview mode: no persistence of any kind (incl. the stale-score reset
    // below — an empty MVP pool must not zero the stored Traditional score).
    // Also guarded against the switch-back race (stale MVP rows still mounted).
    if (pathway !== 'traditional' || viewSubgroupId != null
      || rowsLoadedForPathway.current !== TRADITIONAL_DATA_KEY) return;
    if (rows && rows.length > 0) {
      calculateQualityScores();
    } else if (hasLoadedOnce && baseData.length === 0) {
      // No measures at all -- reset stale quality scores and persist to backend
      setMipsScores(prev => ({
        ...prev,
        qualityScore: 0,
        compositeMipsScore: prev.piScore + prev.iaScore + prev.costScore + prev.patientComplexityBonus
      }));
      setScoresCalculated(true);
      if (selectedPracticeId) {
        mipsScoreCalculator.calculateWithDebounce(selectedPracticeId, [], 500, programYear)
          .catch(err => console.error('Failed to reset stale scores:', err));
      }
    }
  }, [rows, calculateQualityScores, hasLoadedOnce, baseData.length, selectedPracticeId, programYear, pathway, viewSubgroupId]);

  // Apply filters then multi-sort on rows
  const filteredRows = useMemo(() => {
    const passTri = (val: boolean | undefined, tri: Tri) => tri === 'any' ? true : (tri === 'yes' ? !!val : !val);
    return rows.filter((r: any) => {
      // Search filter: by measure ID or title keywords (supports comma-separated IDs)
      if (filters.title && filters.title.trim()) {
        const searchInput = filters.title.toLowerCase().trim();
        const measureId = String(r.id || '').toLowerCase();
        const title = String(r.title || '').toLowerCase();
        
        // Check if input contains commas (multiple measure IDs)
        if (searchInput.includes(',')) {
          // Split by comma and check if measure ID matches any of the specified IDs
          const searchTerms = searchInput.split(',').map(term => term.trim()).filter(term => term);
          const matchesAnyId = searchTerms.some(term => measureId.includes(term));
          if (!matchesAnyId) return false;
        } else {
          // Single search term - check both ID and title
          const matchesId = measureId.includes(searchInput);
          const matchesTitle = title.includes(searchInput);
        if (!matchesId && !matchesTitle) return false;
        }
      }
      
      // Opportunities preset: either DC < 75% OR (!bonus AND Den < 20)
      if (filters.policyIssues) {
        const isIssue = (Number(r.dc || 0) < 75) || (!r.autoPts && Number(r.den || 0) < 20);
        if (!isIssue) return false;
      }
      // Simple toggles
      if (filters.collectionTypes.length && !filters.collectionTypes.includes(String(r.collectionType))) return false;
      if (!passTri(r.inverse, filters.inverse)) return false;
      if (!passTri(r.autoPts, filters.autoPts)) return false;
      if (!passTri(r.cap7, filters.cap7)) return false;
      return true;
    });
  }, [rows, filters]);

  // Store the last sorted order to stabilize during editing
  const lastSortOrderRef = useRef<string[]>([]);
  
  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    if (!sorters.length) return arr;
    
    // If user has pending drafts, preserve the last sort order to prevent jumping
    // Only re-sort when drafts are cleared (after commit) or sorters change
    if (hasPendingDraftsGlobal && lastSortOrderRef.current.length > 0) {
      // Sort by the preserved order
      const orderMap = new Map(lastSortOrderRef.current.map((id, idx) => [id, idx]));
      arr.sort((a, b) => {
        const aId = a.compositeId || `${a.id}:${a.submissionMethod || ''}`;
        const bId = b.compositeId || `${b.id}:${b.submissionMethod || ''}`;
        const aIdx = orderMap.get(aId) ?? 999999;
        const bIdx = orderMap.get(bId) ?? 999999;
        return aIdx - bIdx;
      });
      return arr;
    }
    
    const getVal = (r: any, key: SortKey) => {
      switch (key) {
        case 'points':
        case 'pr':
        case 'dc':
          return Number(r[key] ?? 0);
        case 'title':
        case 'id':
          return String(r[key] ?? '').toLowerCase();
        default:
          return (r as any)[key as any];
      }
    };
    arr.sort((a, b) => {
      for (const s of sorters) {
        const av: any = getVal(a, s.key);
        const bv: any = getVal(b, s.key);
        if (av === bv) continue;
        const cmp = av > bv ? 1 : -1;
        return s.dir === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
    
    // Store the sorted order for stabilization during editing
    lastSortOrderRef.current = arr.map(r => r.compositeId || `${r.id}:${r.submissionMethod || ''}`);
    
    return arr;
  }, [filteredRows, sorters, hasPendingDraftsGlobal]);

  const maxGain = Math.max(...rows.map((r) => r.potentialGain));
  const qualityChecks = useMemo(() => ({
    measuresSix: rows.length >= 6,
    outcomeOrHigh: rows.some((r: any) => /MIPS-110|QCDR-004/.test(r.id)),
  }), [rows]);
  const iaSelectedCount = 2; // mock selection count
  const iaTwoPlus = iaSelectedCount >= 2;
  const piBaseMet = true; // mock base requirements status

  const exportSummary = () => {
    const headers = [
      "TIN","Practice Name","Practice Alias","Measure ID","Title","Collection Type","Initial Pop.","Denominator Exclusion","Denominator","Num Met","Numerator Exception","Num Not Met","Complete Rate","Performance Rate","Est. Points"
    ];
    const body = rows.map(r => ({
      "TIN": selectedPractice?.tin || '',
      "Practice Name": selectedPractice?.tin_name || '',
      "Practice Alias": selectedPractice?.practice_alias || '',
      "Measure ID": r.id,
      "Title": r.title,
      "Collection Type": r.collectionType || '',
      "Initial Pop.": r.ip,
      "Denominator Exclusion": r.ex,
      "Denominator": r.den,
      "Num Met": r.met,
      "Numerator Exception": r.exc,
      "Num Not Met": r.not,
      "Complete Rate": `${(r.dc || 0).toFixed(2)}%`,
      "Performance Rate": `${(r.pr || 0).toFixed(2)}%`,
      "Est. Points": r.points,
    }));
    const csv = toCsv(body, headers);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const timestamp = new Date().toISOString().split('T')[0];
    a.download = `quality-summary-${timestamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const recalc = async () => {
    setIsRecalculating(true);
    try {
      console.log('[OPTION2-RECALC] Starting recalc...');
      console.log('[OPTION2-RECALC] Current selectedBaseIds:', selectedBaseIds);
      
      // Log manual measures specifically
      const manualMeasures = baseData.filter(m => m.calculationMode === 'manual');
      console.log('[OPTION2-RECALC] Manual measures in baseData:', manualMeasures.map(m => ({ id: m.id, mode: m.calculationMode })));
      
      // 1) Commit buffered edits for manual entries (now saves to backend)
      await commitDrafts();
      
      // 2) No more pending calc measures - everything is already in database
      console.log('[OPTION2-RECALC] All measures are in database, refreshing data...');
      
      // 3) Clear all caches and force complete data reload
      // This will fetch fresh backend-calculated data for all selected measures
    console.log('[TRACE-RECALC] Clearing cache and incrementing dataVersion...');
    console.log('[TRACE-RECALC] dataVersion before:', dataVersion);
    
    clearOutcomeCache();
    setDataVersion(v => {
      console.log('[TRACE-RECALC] Setting dataVersion from', v, 'to', v + 1);
      return v + 1;
    }); // Triggers useEffect to reload all data
    
      toast({ 
        title: "Refreshing Data", 
        description: "Loading calculated values from backend. This may take a moment..." 
      });
      
      console.log('[TRACE-RECALC] Recalc complete - data reload triggered');
    } catch (error) {
      console.error('[RECALC] Error during recalculation:', error);
      toast({
        title: "Error",
        description: "Failed to refresh data. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsRecalculating(false);
    }
  };
  
  const openMeasureDetail = (measure: any) => {
    setSelectedMeasure(measure);
    setDetailOpen(true);
  };
  
  const updateMeasureNotes = (measureId: string, notes: string) => {
    setMeasureNotes(prev => ({ ...prev, [measureId]: notes }));
  };

  const handleEvidenceUpload = async (measureId: string, files: FileList) => {
    const arr = Array.from(files);
    const reads = await Promise.all(
      arr.map(
        (f) =>
          new Promise<EvidenceFile>((resolve) => {
            const fr = new FileReader();
            fr.onload = () =>
              resolve({ name: f.name, type: f.type, size: f.size, dataUrl: String(fr.result), uploadedAt: Date.now() });
            fr.readAsDataURL(f);
          })
      )
    );
    setMeasureEvidence(prev => ({
      ...prev,
      [measureId]: [...(prev[measureId] || []), ...reads]
    }));
  };

  const filesToFileList = (files: File[]) => {
    const dt = new DataTransfer();
    files.forEach((f) => dt.items.add(f));
    return dt.files;
  };
  const handleEvidenceUploadFiles = async (measureId: string, files: File[]) => {
    await handleEvidenceUpload(measureId, filesToFileList(files));
  };

  const removeEvidence = (measureId: string, name: string, uploadedAt: number) => {
    setMeasureEvidence(prev => ({
      ...prev,
      [measureId]: (prev[measureId] || []).filter(e => !(e.name === name && e.uploadedAt === uploadedAt))
    }));
  };

  const updateManualField = (id: string, field: "ip" | "ex" | "den" | "met" | "not" | "exc", value: number) => {
    // OPTION 2: This is now handled by updateManual function above
    // Just call updateManual with the same parameters
    if (isNaN(value)) return;
      
      // Don't allow manual updates to denominator
      if (field === 'den') {
        toast({
          title: "Invalid Action",
          description: "Denominator is calculated automatically from IP - EX",
          variant: "destructive",
        });
      return;
    }
    
    updateManual(id, field as any, value);
  };

  // OPTION 2: Draft helpers for manual entry – view shows drafts if present
  const getManualView = (id: string, committed: NonNullable<AddedMeasure["manual"]>) =>
    drafts[id]?.manual ?? committed;

  const setManualDraft = (id: string, field: "ip" | "ex" | "met" | "not" | "exc", value: number) =>
    setDrafts(prev => {
      // OPTION 2: Initialize from baseData manual values
      const { measureId, submissionMethod } = parseCompositeId(id);
      const baseMeasure = baseData.find(b => b.id === measureId && b.submissionMethod === submissionMethod);
      const committed = baseMeasure?.manualValues || { ip: 0, ex: 0, den: 0, met: 0, not: 0, exc: 0 };
      const base = prev[id]?.manual ?? committed;
      
      // Validate before updating
      const validation = validateManualInputs(field, value, base);
      if (!validation.isValid) {
        toast({
          title: "Invalid Input",
          description: validation.errorMessage,
          variant: "destructive",
        });
        return prev; // Don't update if invalid
      }
      
      const next = { ...base, [field]: value };
      next.den = Math.max(0, next.ip - next.ex);
      return { ...prev, [id]: { ...prev[id], manual: next } };
    });

  const setStrataDraft = (id: string, idx: number, field: keyof Omit<StrataData, 'name' | 'description'>, value: number) =>
    setDrafts(prev => {
      // OPTION 2: Initialize from baseData strata
      const { measureId, submissionMethod } = parseCompositeId(id);
      const baseMeasure = baseData.find(b => b.id === measureId && b.submissionMethod === submissionMethod);
      const committedStrata = baseMeasure?.manualValues?.strata || baseMeasure?.strata || [];
      const baseArr = prev[id]?.strata ?? committedStrata;
      const nextArr = [...baseArr];
      const curr = baseArr[idx] ?? committedStrata[idx] ?? { 
        name: '', 
        description: '', 
        ip: 0, 
        ex: 0, 
        den: 0, 
        met: 0, 
        not: 0, 
        exc: 0 
      };
      
      // Validate before updating
      const validation = validateStratumInputs(field, value, curr);
      if (!validation.isValid) {
        toast({
          title: "Invalid Input",
          description: validation.errorMessage,
          variant: "destructive",
        });
        return prev; // Don't update if invalid
      }
      
      const updated = { ...curr, [field]: value };
      updated.den = Math.max(0, updated.ip - updated.ex);
      nextArr[idx] = updated;
      return { ...prev, [id]: { ...prev[id], strata: nextArr } };
    });

  // Commit drafts into added[]
  const commitDrafts = async () => {
    // OPTION 2: Process drafts and save to backend
    const updatedMeasures: any[] = [];
    
    // Process each draft entry
    for (const [compositeId, draft] of Object.entries(drafts)) {
      const { measureId, submissionMethod } = parseCompositeId(compositeId);
      const baseMeasure = baseData.find(b => b.id === measureId && b.submissionMethod === submissionMethod);
      
      if (!baseMeasure || baseMeasure.calculationMode !== 'manual') {
        continue; // Skip if not in manual mode
      }
      
      let manualValues: any = baseMeasure.manualValues || {};
      
      // Process manual values
      if (draft.manual) {
        const v = draft.manual;
        const manual = { ...v, den: Math.max(0, v.ip - v.ex) };
        // validate once against one field; helper recomputes den internally
        const check = validateManualInputs('met', manual.met, manual);
        if (!check.isValid) {
          toast({ title: "Invalid Input", description: check.errorMessage, variant: "destructive" });
          continue;
        }
        manualValues = { ...manualValues, ...manual };
      }
      
      // Process strata if present
      if (draft.strata && baseMeasure.isMultiStrata) {
        const existingStrata = manualValues.strata || baseMeasure.strata || [];
        const nextStrata = existingStrata.map((s: any, i: number) => {
          const ds = draft.strata![i];
          if (!ds) return s;
          return { ...s, ...ds, den: Math.max(0, ds.ip - ds.ex) };
        });
        manualValues = { ...manualValues, strata: nextStrata };
      }
      
      updatedMeasures.push({
        compositeId,
        measureId,
        submissionMethod,
        manualValues
      });
    }
    
    // Save manual values to backend
    if (selectedPracticeId && updatedMeasures.length > 0) {
      for (const { compositeId, measureId, submissionMethod, manualValues } of updatedMeasures) {
        try {
          const response = await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/measures/${measureId}/manual-values?year=${programYear}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            },
            body: JSON.stringify({
              values: manualValues,
              category: 'quality',
              submission_method: submissionMethod || 'MIPS CQM',
            }),
          });
          
          if (!response.ok) {
            console.error('[OPTION2-commitDrafts] Failed to save manual values for', measureId);
          } else {
            console.log('[OPTION2-commitDrafts] Saved manual values for', measureId);
            
            // Update baseData with the new manual values
            setBaseData(prev => prev.map(m => {
              if (m.id === measureId && m.submissionMethod === submissionMethod) {
                return { ...m, manualValues };
              }
              return m;
            }));
          }
        } catch (error) {
          console.error('[OPTION2-commitDrafts] Error saving manual values:', error);
        }
      }
    }
    
    setDrafts({});
  };

  // Helper function to validate stratum manual inputs
  const validateStratumInputs = (
    field: keyof Omit<StrataData, 'name' | 'description'>,
    value: number,
    currentStratum: StrataData
  ): { isValid: boolean; errorMessage?: string } => {
    // Rule 1: All values must be non-negative
    if (value < 0) {
      return { isValid: false, errorMessage: "Values cannot be negative" };
    }
    
    // Create a temporary updated state
    const updated = { ...currentStratum, [field]: value };
    
    // Recalculate denominator if ip or ex changed
    if (field === 'ip' || field === 'ex') {
      updated.den = Math.max(0, updated.ip - updated.ex);
    }
    
    // Rule 2: Exclusions cannot exceed Initial Population
    if (updated.ex > updated.ip) {
      return { isValid: false, errorMessage: "Exclusions cannot exceed Initial Population" };
    }
    
    // Rule 3: Met + Not Met + Exception cannot exceed Denominator
    const totalNumerator = updated.met + updated.not + updated.exc;
    if (totalNumerator > updated.den) {
      return { 
        isValid: false, 
        errorMessage: `Total numerator (${totalNumerator}) cannot exceed denominator (${updated.den})` 
      };
    }
    
    return { isValid: true };
  };

  const downloadMeasureDetail = async (measure: any) => {
    try {
      // Show loading state
      toast({
        title: "Preparing download",
        description: "Fetching patient data...",
      });

      // Fetch real patient data from API
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(
        `${apiUrl}/api/performance/${selectedPracticeId}/measures/${measure.id.split(':')[0]}/patients?submissionMethod=${measure.submissionMethod || 'MIPS CQM'}&year=${programYear}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch patient data: ${response.status}`);
      }

      const patientRows = await response.json();
      
      // If no data, show a message
      if (!patientRows || patientRows.length === 0) {
        toast({
          title: "No data available",
          description: "No patient data found for this measure.",
          variant: "destructive",
        });
        return;
      }

      const headers = [
        "TIN",
        "Practice_Name",
        "Practice_Alias",
        "Patient_ID",
        "Full_Name",
        "DOB",
        "Gender",
        "NPI",
        "NPI_Name",
        "Date_of_Service",
        "CPT_Codes",
        "CPT_Modifiers",
        "ICD10_Codes",
        "Place_of_Service",
        "Initial_Population",
        "Denominator_Exclusion", 
        "Denominator",
        "Numerator_Met",
        "Numerator_Exception",
        "Numerator_Not_Met",
        "Data_Source",
        "Notes",
      ];
      
      const body = patientRows.map((r: any) => {
        // Use fullName if available, otherwise combine firstName and lastName
        const fullName = r.fullName || 
          (r.firstName && r.lastName ? `${r.firstName} ${r.lastName}` : 
           r.firstName || r.lastName || `Patient ${r.pid}`);
        
        return {
          TIN: selectedPractice?.tin || '',
          Practice_Name: selectedPractice?.tin_name || '',
          Practice_Alias: selectedPractice?.practice_alias || '',
          Patient_ID: r.pid,
          Full_Name: fullName,
          DOB: r.dob,
          Gender: r.gender,
          NPI: r.npi,
          NPI_Name: r.npiName,
          Date_of_Service: r.dos,
          CPT_Codes: r.cptCodes,
          CPT_Modifiers: r.cptModifiers,
          ICD10_Codes: r.icd10Codes,
          Place_of_Service: r.placeOfService,
          Initial_Population: r.ip ? "True" : "False",
          Denominator_Exclusion: r.ex ? "True" : "False",
          Denominator: r.den ? "True" : "False",
          Numerator_Met: r.met ? "True" : "False",
          Numerator_Exception: r.exc ? "True" : "False",
          Numerator_Not_Met: r.not ? "True" : "False",
          Data_Source: r.source,
          Notes: r.notes,
        };
      });
      
      const csv = toCsv(body, headers);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `measure-${measure.id}-detail.csv`;
      a.click();
      URL.revokeObjectURL(url);
      
      toast({
        title: "Download complete",
        description: `Downloaded ${patientRows.length} patient records.`,
      });
    } catch (error) {
      console.error("Error downloading measure detail:", error);
      toast({
        title: "Download failed",
        description: "Failed to download patient data. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Show error state if there's an error
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="text-destructive font-semibold">Error loading measures</div>
        <div className="text-muted-foreground">{error.message}</div>
        <Button onClick={() => window.location.reload()}>Reload Page</Button>
      </div>
    );
  }

  // Removed loading state - page renders immediately

  const updateStratumManualField = (
    measureCompositeId: string, 
    stratumIndex: number, 
    field: keyof Omit<StrataData, 'name' | 'description'>, 
    value: number
  ) => {
    if (isNaN(value)) return;

    // Don't allow manual updates to denominator
    if (field === 'den') {
      toast({
        title: "Invalid Action",
        description: "Denominator is calculated automatically from IP - EX",
        variant: "destructive",
      });
      return;
    }

    // CRITICAL FIX: Use drafts instead of the non-existent setAdded
    // This function updates stratum values for manual measures
    const { measureId, submissionMethod } = parseCompositeId(measureCompositeId);
    const baseMeasure = baseData.find(b => b.id === measureId && b.submissionMethod === submissionMethod);
    
    if (!baseMeasure || baseMeasure.calculationMode !== 'manual') {
      console.warn('[updateStratumManualField] Measure not in manual mode or not found');
      return;
    }

    // Get current strata from drafts or base measure
    const currentStrata = drafts[measureCompositeId]?.strata || 
                          baseMeasure.manualValues?.strata || 
                          baseMeasure.strata || [];
    
    if (!currentStrata[stratumIndex]) {
      console.warn('[updateStratumManualField] Stratum index not found');
      return;
    }

        // Validate the input
    const oldStratum = currentStrata[stratumIndex];
        const validation = validateStratumInputs(field, value, oldStratum);
        if (!validation.isValid) {
          toast({
            title: "Invalid Input",
            description: validation.errorMessage,
            variant: "destructive",
          });
      return;
        }

    // Create updated strata array
    const newStrata = [...currentStrata];
        let updatedStratum = { ...oldStratum, [field]: value };
    
    // Recalculate denominator if ip/ex changed
        if (field === 'ip' || field === 'ex') {
          updatedStratum.den = Math.max(0, updatedStratum.ip - updatedStratum.ex);
        }

    // Update per-stratum rates
    // @ts-expect-error - pr/dc are added dynamically
        updatedStratum.pr = computePerformanceRate(
          updatedStratum.met, updatedStratum.not, updatedStratum.exc
        );
        // @ts-expect-error
        updatedStratum.dc = computeDataCompletenessRate(
          updatedStratum.den, updatedStratum.met, updatedStratum.not, updatedStratum.exc
        );

        newStrata[stratumIndex] = updatedStratum;

    // Calculate overall rates if multi-strata
        const dataService = MipsDataService.getInstance();
        const strataConfig = dataService.getStrataConfig(measureId, submissionMethod);
        const strataDefinitions = dataService.getStrataDefinitions(measureId);
        
    let overallPr = 0;
    let overallDc = 0;

        if (strataConfig) {
      // Sanitize the entire strata array
          const sanitizedStrata = newStrata.map(s => ({
            ...s,
            ip: Number(s.ip || 0),
            ex: Number(s.ex || 0),
            den: Number(s.den || 0),
            met: Number(s.met || 0),
            exc: Number(s.exc || 0),
            not: Number(s.not || 0),
          }));

      overallPr = calculateOverallPerformanceRate(sanitizedStrata, strataConfig.performanceRate, strataDefinitions);
      overallDc = calculateOverallDataCompletenessRate(sanitizedStrata, strataConfig.completeRate, strataDefinitions);
    }

    // Update drafts with the new strata values
    setDrafts(prev => ({
      ...prev,
      [measureCompositeId]: {
        ...prev[measureCompositeId],
          strata: newStrata,
        // Store overall rates in draft for display
        overallPr,
        overallDc
      }
    }));
  };

  // Unified loading gate - show skeleton only until first successful load
  // After first load, page stays visible and data updates in place (no skeleton on NPI switch, etc.)
  const pageReady = hasLoadedOnce;

  useEffect(() => {
    if (pageReady) {
      const timer = setTimeout(() => setPageReadyDelayDone(true), 75); // tiny buffer to smooth reveal
      return () => clearTimeout(timer);
    }
    setPageReadyDelayDone(false);
  }, [pageReady]);
  
  console.log('[GAP-MGMT] Loading states:', { 
    hasLoadedOnce, 
    pageReadyDelayDone,
    pageReady
  });
  
  if (!pageReady || !pageReadyDelayDone) {
    console.log('[GAP-MGMT] Showing skeleton - first load not complete');
    return (
      <>
        <Helmet>
          <title>Gap Management – Close MIPS Reporting Gaps</title>
          <meta name="description" content="Identify and close MIPS Quality, PI, and IA gaps with summaries, per-measure drilldowns, and recalculation." />
          <link rel="canonical" href="/programs/mips/gap-management/quality" />
        </Helmet>
        <GapManagementFullPageSkeleton />
      </>
    );
  }

  return (
    <div id="quality-print-scope" className="overflow-x-hidden max-w-full">
      <Helmet>
        <title>Gap Management – Close MIPS Reporting Gaps</title>
        <meta name="description" content="Identify and close MIPS Quality, PI, and IA gaps with summaries, per-measure drilldowns, and recalculation." />
        <link rel="canonical" href="/programs/mips/gap-management/quality" />
      </Helmet>

      <div className="quality-print-header hidden items-start justify-between border-b border-slate-300 pb-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold">Gap Management Report</h1>
          <p className="text-sm text-slate-600">
            {selectedPractice?.tin_name || selectedPractice?.practice_alias || 'Selected Practice'} · {viewSubgroupId != null ? `Subgroup View · ${activeSubgroup?.name ?? viewSubgroupId}` : evaluationType === 'GROUP' ? 'Group/TIN View' : `NPI View${selectedNpi ? ` · ${selectedNpi}` : ''}`}
          </p>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-wide text-slate-500">Printed On</p>
          <p className="text-sm font-semibold">{reportDate}</p>
        </div>
      </div>

      <div className="mb-3 quality-print-hide">
        <YearLockBanner />
      </div>

      {phase3InFlight && (
        <div className="mb-3 flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 quality-print-hide">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>
            Calculating outcomes for the latest upload&hellip; Numbers below reflect the
            previous complete calculation and will refresh automatically when processing finishes.
          </span>
        </div>
      )}

      <div className="mb-4 space-y-1.5 quality-print-hide">
        <div className="flex items-start justify-between gap-4">
          <h1 className="text-3xl font-bold leading-tight">Gap Management</h1>
          <div className="flex items-center gap-2 flex-wrap justify-end">
          {/* Pathway selector (MVP module Phase D) — preview any pathway; commit is explicit */}
          {mvpCatalog.length > 0 && (
            <div className="flex items-center gap-2 border rounded-lg px-3 py-1.5 bg-muted/30">
              <span className="text-sm text-muted-foreground">Pathway:</span>
              <Select value={pathway} onValueChange={setPathway}>
                <SelectTrigger className="w-[260px] h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    value="traditional"
                    disabled={viewSubgroupId != null}
                  >
                    Traditional MIPS{viewSubgroupId != null ? ' — unavailable for subgroups' : ''}
                  </SelectItem>
                  {mvpCatalog.map(m => {
                    const isCommittedHere = viewSubgroupId != null
                      ? committedMvpForSubgroup(viewSubgroupId) === m.mvpId
                      : committedGroupMvp?.mvp_id === m.mvpId;
                    return (
                      <SelectItem key={m.mvpId} value={m.mvpId}>
                        {isCommittedHere ? '★ ' : ''}{m.title} ({m.mvpId})
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {pathwayMvpId && pathwaySubgroupId == null && (
                committedGroupMvp?.mvp_id === pathwayMvpId ? (
                  <Badge variant="secondary" className="whitespace-nowrap">Our MVP</Badge>
                ) : (
                  <Button size="sm" variant="outline" className="h-7 whitespace-nowrap" onClick={commitPreviewedMvp}>
                    Set as our MVP
                  </Button>
                )
              )}
            </div>
          )}
          {/* Evaluation Type Toggle - fixed width container */}
          <div className="flex items-center gap-2 border rounded-lg px-3 py-1.5 bg-muted/30 min-w-[480px]">
            <span className="text-sm text-muted-foreground">View:</span>
            <div className="flex items-center gap-1">
              <Button
                variant={evaluationType === 'GROUP' && viewSubgroupId == null ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-3"
                onClick={() => {
                  setEvaluationType('GROUP');
                  setSelectedNpi('');
                  setViewSubgroupId(null);
                }}
              >
                Group/TIN
              </Button>
              <Button
                variant={evaluationType === 'NPI' ? 'default' : 'ghost'}
                size="sm"
                className="h-7 px-3"
                onClick={() => {
                  setEvaluationType('NPI');
                  setViewSubgroupId(null);
                  // Auto-select first provider if providers already loaded
                  if (providers.length > 0 && !selectedNpi) {
                    setSelectedNpi(providers[0].npi);
                  }
                }}
              >
                NPI
              </Button>
              {mvpSubgroups.length > 0 && (
                <Button
                  variant={viewSubgroupId != null ? 'default' : 'ghost'}
                  size="sm"
                  className="h-7 px-3"
                  onClick={() => {
                    if (viewSubgroupId != null) return;
                    // Default to the first subgroup with a committed MVP, else the first
                    const preferred = mvpSubgroups.find(sg => committedMvpForSubgroup(sg.id))
                      ?? mvpSubgroups[0];
                    selectSubgroupView(preferred.id);
                  }}
                >
                  Subgroup
                </Button>
              )}
            </div>

            {/* Selector zone: subgroup picker > practice name (GROUP) > NPI dropdown */}
            {viewSubgroupId != null ? (
              <Select
                value={String(viewSubgroupId)}
                onValueChange={v => selectSubgroupView(Number(v))}
              >
                <SelectTrigger className="w-[280px] h-8">
                  <SelectValue placeholder="Select subgroup" />
                </SelectTrigger>
                <SelectContent>
                  {mvpSubgroups.map(sg => (
                    <SelectItem key={sg.id} value={String(sg.id)}>
                      {sg.name} ({sg.members.length})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : evaluationType === 'GROUP' ? (
              <div className="w-[280px] h-8 px-3 flex items-center text-sm font-medium text-foreground truncate border rounded-md bg-background">
                {selectedPractice?.tin_name || selectedPractice?.practice_alias || 'All Providers'}
              </div>
            ) : (
              <Select
                value={selectedNpi}
                onValueChange={(value) => setSelectedNpi(value)}
                disabled={loadingProviders || providers.length === 0}
              >
                <SelectTrigger className="w-[280px] h-8">
                  <SelectValue placeholder={loadingProviders ? "Loading providers..." : "Select provider"} />
                </SelectTrigger>
                <SelectContent>
                  {providers.map((provider) => (
                    <SelectItem key={provider.npi} value={provider.npi}>
                      {provider.displayName}
                    </SelectItem>
                  ))}
                  {providers.length === 0 && !loadingProviders && (
                    <SelectItem value="__no_providers__" disabled>
                      No providers found
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            )}
          </div>
          </div>
        </div>

        {/* MVP preview banner — every number in this mode is a projection */}
        {pathwayMvpId && (
          <Alert>
            <InfoIcon className="h-4 w-4" />
            <AlertDescription>
              <strong>MVP preview: {mvpCatalog.find(m => m.mvpId === pathwayMvpId)?.title ?? pathwayMvpId}</strong>
              {pathwaySubgroupId != null && ` — subgroup: ${activeSubgroup?.name ?? pathwaySubgroupId} (quality from this roster; cost, PI, and population health stay practice-level per CMS)`}.
              {' '}The table shows this MVP's quality measure pool; greyed rows are in the pool but not tracked by this practice.
              Score cards show engine projections — your stored Traditional scores are untouched. Switch back to Traditional MIPS to edit tracking or recalculate.
            </AlertDescription>
          </Alert>
        )}
        <div className="flex items-center justify-between gap-4">
          <QualityPrintControls
            reportDate={reportDate}
            onReportDateChange={handleReportDateChange}
            showButton={false}
          />
          <div className="flex justify-end">
            <QualityPrintControls
              reportDate={reportDate}
              onReportDateChange={handleReportDateChange}
              showDate={false}
            />
          </div>
        </div>
      </div>

      {/* Score cards */}
      <section className="mb-4">
            <UnifiedScoreCard
              compositeScore={mvpPreviewRow ? mvpPreviewRow.finalScore : displayCompositeScore}
              patientComplexityBonus={mvpPreviewRow ? mvpPreviewRow.cpb : mipsScores.patientComplexityBonus}
              onViewReport={() => setShowPaymentAdjustmentModal(true)}
              variant={6}
              evaluationType={evaluationType}
              selectedNpi={selectedNpi}
            />
          </section>
        <section className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {/* Quality */}
          <Card onClick={() => setActiveCategory('quality')} className={activeCategory==='quality' ? 'group cursor-pointer transition-all rounded-2xl ring-1 ring-primary/40 border-primary/40 bg-muted/20 shadow-none' : 'group cursor-pointer transition-all rounded-2xl hover:shadow-md hover:bg-muted/40'}>
            <CardHeader className="px-4 py-3 flex flex-col min-h-[7rem]">
              <div className="flex items-start justify-between flex-1">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-primary" />
                  <CardTitle className="text-xl whitespace-nowrap">Quality</CardTitle>
                </div>
                <div className="text-2xl font-semibold">
                  {mvpPreviewRow
                    ? `${mvpPreviewRow.quality.points.toFixed(1)}/${Math.round(mvpPreviewRow.quality.weight)}`
                    : (mipsWeights.qualityWeight === 0 ? '—' : `${mipsScores.qualityScore.toFixed(1)}/${Math.round(mipsWeights.qualityWeight)}`)}
                </div>
              </div>
              <Separator className="mt-auto mb-1" />
              <CardDescription>
                {mvpPreviewRow ? 'MVP Preview (Projected)' : 'Weighted Score (Calculated)'}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0">
              <div className="h-12"></div>
            </CardContent>
          </Card>
          {/* Promoting Interoperability */}
          <Card onClick={() => setActiveCategory('pi')} className={activeCategory==='pi' ? 'group cursor-pointer transition-all rounded-2xl ring-1 ring-primary/40 border-primary/40 bg-muted/20 shadow-none' : 'group cursor-pointer transition-all rounded-2xl hover:shadow-md hover:bg-muted/40'}>
            <CardHeader className="px-4 py-3 flex flex-col min-h-[7rem]">
              <div className="flex items-start justify-between flex-1">
                <div className="flex items-center gap-2">
                  <Shield className="h-5 w-5 text-primary" />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <CardTitle className="text-xl whitespace-nowrap">
                          <span className="hidden 2xl:inline">Promoting Interop.</span>
                          <span className="2xl:hidden">PI</span>
                        </CardTitle>
                      </TooltipTrigger>
                      <TooltipContent>
                        Promoting Interoperability
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="text-2xl font-semibold">
                  {mvpPreviewRow
                    ? (mvpPreviewRow.pi.weight === 0 ? '—' : `${mvpPreviewRow.pi.points.toFixed(1)}/${Math.round(mvpPreviewRow.pi.weight)}`)
                    : (mipsWeights.piWeight === 0 ? '—' : `${mipsScores.piScore.toFixed(1)}/${Math.round(mipsWeights.piWeight)}`)}
                </div>
              </div>
              <Separator className="mt-auto mb-1" />
              <CardDescription>
                {mvpPreviewRow ? 'MVP Preview (Projected)' : <>Weighted Score {useModeledPi ? "(Modeled)" : "(Calculated)"}</>}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0">
              <div className="h-12"></div>
            </CardContent>
          </Card>
          {/* Improvement Activities */}
          <Card onClick={() => setActiveCategory('ia')} className={activeCategory==='ia' ? 'group cursor-pointer transition-all rounded-2xl ring-1 ring-primary/40 border-primary/40 bg-muted/20 shadow-none' : 'group cursor-pointer transition-all rounded-2xl hover:shadow-md hover:bg-muted/40'}>
            <CardHeader className="px-4 py-3 flex flex-col min-h-[7rem]">
              <div className="flex items-start justify-between flex-1">
                <div className="flex items-center gap-2">
                  <Layers className="h-5 w-5 text-primary" />
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <CardTitle className="text-xl whitespace-nowrap">
                          <span className="hidden 2xl:inline">Improvement Activities</span>
                          <span className="2xl:hidden">IA</span>
                        </CardTitle>
                      </TooltipTrigger>
                      <TooltipContent>
                        Improvement Activities
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <div className="text-2xl font-semibold">
                  {mvpPreviewRow
                    ? (mvpPreviewRow.ia.weight === 0 ? '—' : `${mvpPreviewRow.ia.points.toFixed(1)}/${Math.round(mvpPreviewRow.ia.weight)}`)
                    : (mipsWeights.iaWeight === 0 ? '—' : `${mipsScores.iaScore.toFixed(1)}/${Math.round(mipsWeights.iaWeight)}`)}
                </div>
              </div>
              <Separator className="mt-auto mb-1" />
              <CardDescription>
                {mvpPreviewRow ? 'MVP Preview (Projected)' : <>Weighted Score {useModeledIa ? "(Modeled)" : "(Calculated)"}</>}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0">
              <div className="h-12"></div>
            </CardContent>
          </Card>
          {/* Cost */}
          <Card onClick={() => setActiveCategory('cost')} className={activeCategory==='cost' ? 'group cursor-pointer transition-all rounded-2xl ring-1 ring-primary/40 border-primary/40 bg-muted/20 shadow-none' : 'group cursor-pointer transition-all rounded-2xl hover:shadow-md hover:bg-muted/40'}>
            <CardHeader className="px-4 py-3 flex flex-col min-h-[7rem]">
              <div className="flex items-start justify-between flex-1">
                <div className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" />
                  <CardTitle className="text-xl whitespace-nowrap">Cost</CardTitle>
                </div>
                <div className="text-2xl font-semibold">
                  {mvpPreviewRow
                    ? (mvpPreviewRow.cost.weight === 0 ? '—' : `${mvpPreviewRow.cost.points.toFixed(1)}/${Math.round(mvpPreviewRow.cost.weight)}`)
                    : (mipsWeights.costWeight === 0 ? '—' : `${mipsScores.costScore.toFixed(1)}/${Math.round(mipsWeights.costWeight)}`)}
                </div>
              </div>
              <Separator className="mt-auto mb-1" />
              <CardDescription>
                {mvpPreviewRow ? 'MVP Preview (Projected)' : <>Weighted Score {useModeledCost ? "(Modeled)" : "(Calculated)"}</>}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-4 pb-4 pt-0">
              <div className="h-12"></div>
            </CardContent>
          </Card>
        </section>

      {/* Quality tab - kept mounted but hidden to prevent remounting/re-fetching */}
      <div style={{ display: activeCategory === "quality" ? "block" : "none" }}>
        <section className="mt-6">
          <GapsToClose category="quality" practiceId={selectedPractice?.id} />
        </section>
        <section className="mt-8">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <CardTitle className="text-2xl font-bold">Quality Summary</CardTitle>
              </div>
                <div className="quality-print-hide flex flex-wrap items-center gap-2 text-sm w-full lg:w-auto lg:ml-auto">
                  <div className="flex flex-wrap items-center gap-2 lg:gap-3">
                    <div className="flex flex-col lg:flex-row items-center gap-1 lg:gap-2">
                      <span className="text-xs lg:text-sm text-muted-foreground">Tooltips</span>
                      <Switch checked={colSettings.tooltips} onCheckedChange={(v)=>setColSettings(prev=>({ ...prev, tooltips: !!v }))} />
                    </div>
                    <Button
                      variant={isSelectionMode ? "default" : "outline"}
                      size="sm"
                      onClick={() => {
                        if (isSelectionMode) {
                          exitSelectionMode();
                        } else {
                          setIsSelectionMode(true);
                        }
                      }}
                      className="flex items-center gap-1"
                    >
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 pointer-events-none"
                        checked={isSelectionMode}
                        readOnly
                      />
                      Select
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <Columns className="mr-2 h-4 w-4" />
                          View
                          <ChevronDown className="ml-2 h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="z-50 bg-background" align="end">
                        <DropdownMenuLabel>Column Views</DropdownMenuLabel>
                        <DropdownMenuRadioGroup 
                          value={activePresetId}
                          onValueChange={(v) => {
                            if (presets.some(p=>p.id===v)) { 
                              applyPreset(v); 
                            }
                          }}
                        >
                          {/* System views */}
                          {presets.filter(p=>p.builtin).map((p) => (
                            <DropdownMenuRadioItem key={p.id} value={p.id}>
                              {p.name}
                            </DropdownMenuRadioItem>
                          ))}
                          {/* User views */}
                          {presets.some(p=>!p.builtin) && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuLabel className="text-xs">My Views</DropdownMenuLabel>
                              {presets.filter(p=>!p.builtin).map((p) => (
                                <DropdownMenuRadioItem key={p.id} value={p.id}>
                                  {p.name}
                                </DropdownMenuRadioItem>
                              ))}
                            </>
                          )}
                        </DropdownMenuRadioGroup>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onSelect={() => setManageOpen(true)}>
                          <Settings2 className="mr-2 h-4 w-4" />
                          Manage Views…
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex items-center gap-2">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <BarChart3 className="mr-2 h-4 w-4" />
                          Simulate Scoring Scenario
                          <span className="ml-2 rounded border px-1.5 py-0.5 text-xs bg-muted/50">
                            {(() => {
                              if (simRule === 'no75') return 'Waive 75% Completeness';
                              if (simRule === 'noden') return 'Waive Case Minimum';
                              if (simRule === 'both') return 'Waive Both Requirements';
                              if (simulate === 'off') return 'Off';
                              return `+${simulate}% Perf Rate`;
                            })()}
                          </span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="z-50 bg-background" align="end">
                        <DropdownMenuLabel>Simulate Scoring Scenario</DropdownMenuLabel>
                        <DropdownMenuRadioGroup
                          value={simRule === 'no75' ? 'rule:no75' : simRule === 'noden' ? 'rule:noden' : simRule === 'both' ? 'rule:both' : (simulate === 'off' ? 'off' : `delta:${simulate}`)}
                          onValueChange={(v) => {
                            if (v === 'off') { setSimulate('off'); setSimRule('none'); return; }
                            if (v.startsWith('delta:')) { setSimulate(v.split(':')[1] as any); setSimRule('none'); return; }
                            if (v.startsWith('rule:')) {
                              const key = v.split(':')[1] as 'no75' | 'noden' | 'both';
                              setSimulate('off');
                              setSimRule(key);
                              return;
                            }
                          }}
                        >
                          <DropdownMenuLabel className="text-xs opacity-70">Performance rate increase</DropdownMenuLabel>
                          <DropdownMenuRadioItem value="off">Off</DropdownMenuRadioItem>
                          <DropdownMenuRadioItem value="delta:2.5">+2.5% Perf Rate</DropdownMenuRadioItem>
                          <DropdownMenuRadioItem value="delta:5">+5% Perf Rate</DropdownMenuRadioItem>
                          <DropdownMenuRadioItem value="delta:10">+10% Perf Rate</DropdownMenuRadioItem>
                          <DropdownMenuRadioItem value="delta:15">+15% Perf Rate</DropdownMenuRadioItem>
                          <DropdownMenuRadioItem value="delta:20">+20% Perf Rate</DropdownMenuRadioItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuLabel className="text-xs opacity-70">What-if scenarios</DropdownMenuLabel>
                          <DropdownMenuRadioItem value="rule:no75">
                            <div className="flex w-full items-center justify-between gap-2">
                              <span>Waive 75% Completeness</span>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <CircleHelp className="h-3.5 w-3.5" />
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    Shows potential points if the 75% data completeness requirement was waived, revealing scoring opportunities.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </DropdownMenuRadioItem>
                          <DropdownMenuRadioItem value="rule:noden">
                            <div className="flex w-full items-center justify-between gap-2">
                              <span>Waive Case Minimum</span>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <CircleHelp className="h-3.5 w-3.5" />
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    Shows potential points if the minimum denominator requirement (20 cases) was waived, revealing scoring opportunities.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </DropdownMenuRadioItem>
                          <DropdownMenuRadioItem value="rule:both">
                            <div className="flex w-full items-center justify-between gap-2">
                              <span>Waive Both Requirements</span>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <CircleHelp className="h-3.5 w-3.5" />
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-xs">
                                    Shows maximum potential points if both completeness and denominator requirements were waived, revealing all scoring opportunities.
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </DropdownMenuRadioItem>
                        </DropdownMenuRadioGroup>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setSortOpen(o => !o)}>
                      <ArrowUpDown className="mr-2 h-4 w-4" />
                      Sort & Filter ({sorters.length + activeFilterCount})
                    </Button>
                    
                    {/* Export Dropdown with Chart Abstraction List */}
                    <ChartAbstractionExportDropdown
                      onExportSummary={exportSummary}
                      measureCount={sortedRows.length}
                      hasPartialMeasures={hasPartialMeasures}
                      partialMeasuresCount={partialMeasuresCount}
                      onExportPartialMeasures={() => setPartialMeasuresModalOpen(true)}
                      practiceId={selectedPracticeId || ''}
                      practiceName={selectedPractice?.tin_name || selectedPractice?.practice_alias || 'Unknown Practice'}
                      measures={sortedRows}
                      evaluationType={evaluationType}
                      selectedNpi={selectedNpi}
                    />
                  </div>
                </div>
            </div>
          </CardHeader>
          <CardContent>
            {submissionTip && dismissedTipKey !== submissionTip.key && (
              <div className="mb-3 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 quality-print-hide">
                <InfoIcon className="mt-0.5 h-4 w-4 shrink-0" />
                <p className="flex-1" title={`Submit these measures: ${submissionTip.counted}`}>
                  <span className="font-medium">Submission tip:</span> leave {submissionTip.leaveOut} out of your CMS
                  submission. {submissionTip.slotFilledBy} fills the outcome/high-priority requirement, so your best
                  {' '}{submissionTip.bestCount} measures count.
                </p>
                <button
                  type="button"
                  aria-label="Dismiss submission tip"
                  onClick={() => setDismissedTipKey(submissionTip.key)}
                  className="rounded p-0.5 text-blue-700 hover:bg-blue-100"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
            {sortOpen && (
              <div className="mb-4 rounded-lg border bg-muted/30 p-4 space-y-4">
                {/* Sort section */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-medium">Sort</div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={presetPolicyReady}>Policy-ready preset</Button>
                    <Button variant="ghost" size="sm" onClick={clearFilters}>Clear filters</Button>
                    <Button size="sm" onClick={()=>setSortOpen(false)}>Apply</Button>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {sorters.map((s, idx) => (
                    <div key={idx} className="flex items-center gap-1 rounded-md border px-2 py-1 bg-background">
                      <Select
                        value={s.key as any}
                        onValueChange={(v: any) => setSorters(prev => prev.map((p, i) => i === idx ? { ...p, key: v } : p))}
                      >
                        <SelectTrigger className="h-8 w-[170px]"><SelectValue placeholder="Field" /></SelectTrigger>
                        <SelectContent className="z-50 bg-background">
                          {sortOptions.map(o => (<SelectItem key={o.key} value={o.key as any}>{o.label}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSorters(prev => prev.map((p, i) => i === idx ? { ...p, dir: p.dir === 'asc' ? 'desc' : 'asc' } : p))} aria-label="Toggle sort direction">
                        <ArrowUpDown className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setSorters(prev => prev.filter((_, i) => i !== idx))} aria-label="Remove sort">
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={() => setSorters(prev => [...prev, { key: 'points', dir: 'desc' }])}>+ Add sort</Button>
                </div>

                <Separator />

                {/* Filters section */}
                <div className="font-medium">Filters</div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                    type="button"
                    variant={filters.policyIssues ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFilters(prev => ({ ...prev, policyIssues: !prev.policyIssues }))}
                    >
                      Policy‑readiness issues
                    </Button>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md hover:-translate-y-0.5 transition-transform"
                            aria-label="Policy-readiness info"
                          >
                            <CircleHelp className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="start" className="max-w-xs z-50">
                          <div className="text-sm space-y-2">
                            <p>Highlights policy-readiness issues:</p>
                            <ul className="list-disc pl-5 space-y-1">
                              <li>Data completeness below 75%</li>
                              <li>Non-bonus measures with denominator under 20</li>
                            </ul>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  {collectionTypesAll.map((t)=>{
                    const active = filters.collectionTypes.includes(t as any);
                    return (
                      <Button key={t as any} type="button" size="sm" variant={active?"default":"outline"}
                        onClick={()=>setFilters(prev=>({ ...prev, collectionTypes: active ? prev.collectionTypes.filter(x=>x!==t) : [...prev.collectionTypes, t as any] }))}
                      >{t}</Button>
                    );
                  })}
                  <Button
                    variant={filters.inverse === 'yes' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilters(prev => ({ ...prev, inverse: prev.inverse === 'yes' ? 'any' : 'yes' }))}
                  >
                    Inverse measures
                  </Button>
                  <Button
                    variant={filters.autoPts === 'yes' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilters(prev => ({ ...prev, autoPts: prev.autoPts === 'yes' ? 'any' : 'yes' }))}
                  >
                    Bonus measures
                  </Button>
                  <Button
                    variant={filters.cap7 === 'yes' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setFilters(prev => ({ ...prev, cap7: prev.cap7 === 'yes' ? 'any' : 'yes' }))}
                  >
                    7‑point cap measures
                  </Button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex-1 min-w-[150px] lg:min-w-[200px] max-w-sm relative">
                    <Input
                      placeholder="Search ID, keywords, or comma-separated IDs..."
                      value={filters.title || ''}
                      onChange={(e) => setFilters(prev => ({ ...prev, title: e.target.value }))}
                      className="h-9 pr-8"
                    />
                    {(filters.title || '').length > 0 && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                        onClick={() => setFilters(prev => ({ ...prev, title: '' }))}
                        aria-label="Clear search"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  {(filters.title || '').length > 0 && (
                    <div className="text-sm text-muted-foreground">
                      {sortedRows.length} result{sortedRows.length !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="quality-print-table rounded-lg border">
              <Table className="[&>div]:max-w-full">
                <TableHeader>
                  <TableRow>
                    {isSelectionMode && (
                      <TableHead className="quality-print-hide text-center w-12">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          checked={selectedMeasures.size > 0 && selectedMeasures.size === sortedRows.length}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = selectedMeasures.size > 0 && selectedMeasures.size < sortedRows.length;
                            }
                          }}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const allIds = sortedRows.map((m: any) => m.compositeId || `${m.id}:${m.submissionMethod || ''}`);
                              selectAllMeasures(allIds);
                            } else {
                              clearAllSelections();
                            }
                          }}
                        />
                      </TableHead>
                    )}
                    {visibleOrder.map((key) => {
                      const def = allColumnDefs.find((d) => d.key === key)!;
                      const label = (colSettings.tooltips && def.tooltip) ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help">{def.label}</span>
                            </TooltipTrigger>
                            <TooltipContent className="z-50">{def.tooltip}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        def.label
                      );
                      const numericColumns = ['den', 'met', 'exc', 'not', 'dc', 'points', 'ip', 'ex', 'cmsAvg', 'registryAvg', 'inverse', 'autoPts', 'cap7'];
                      const headerClassName = cn(
                        "text-center",
                        key === 'title' && "min-w-28 xl:min-w-32 2xl:min-w-48 max-w-80 whitespace-normal leading-tight",
                        key === 'pr' && "min-w-28 xl:min-w-32 2xl:min-w-48 max-w-64",
                        key === 'den' && "max-w-16 sm:max-w-20 whitespace-normal leading-tight break-words 2xl:whitespace-nowrap",
                        key === 'ex' && "max-w-16 sm:max-w-20 whitespace-normal leading-tight break-words 2xl:whitespace-nowrap",
                        numericColumns.includes(key) && key !== 'den' && key !== 'ex' && "max-w-20"
                      );
                      return <TableHead key={key} className={headerClassName}>{label}</TableHead>;
                    })}
                    <TableHead className="quality-print-hide text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedRows.length === 0 ? (
                    <TableRow>
                      <TableCell 
                        colSpan={visibleOrder.length + (isSelectionMode ? 2 : 1)} 
                        className="text-center py-8 text-muted-foreground"
                      >
                        No measures found. Add measures to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedRows.map((m: any) => {
                    const isAdded = !!m.isAdded;
                    const isManual = isAdded && m.mode === "manual";
                    const isCalcPending = isAdded && m.mode === "calc" && m.status !== "calculated";
                    
                    // Check for drafts using all possible ID formats
                    const hasPendingDrafts = !!(
                      drafts[m.compositeId] || 
                      drafts[m.id] || 
                      drafts[`${m.id}:${m.submissionMethod}`] ||
                      (drafts[m.compositeId || m.id] && (drafts[m.compositeId || m.id].manual || drafts[m.compositeId || m.id].strata))
                    );
                    const rowId = `${m.id}:${m.submissionMethod || ''}`;
                    const isExpanded = isRowExpanded(m.id);
                    
                    return (
                      <>
                        <TableRow key={rowId} className={cn(
                          (hasPendingDrafts || isCalcPending) ? "opacity-60" : undefined,
                          (hasPendingDrafts || isCalcPending) ? "!border-l-4 !border-l-amber-500" : undefined,
                          // MVP preview: in the MVP's pool but not tracked by this practice
                          mvpUntrackedIds.has(rowId) ? "opacity-50" : undefined,
                          "hover:bg-muted/50 transition-colors",
                          // Zebra striping - only apply if no pending state
                          !(hasPendingDrafts || isCalcPending) && sortedRows.indexOf(m) % 2 === 0 ? "bg-background" : undefined,
                          !(hasPendingDrafts || isCalcPending) && sortedRows.indexOf(m) % 2 !== 0 ? "bg-muted/20" : undefined
                        )}>
                        {isSelectionMode && (
                          <TableCell className="quality-print-hide text-center w-12">
                            <input
                              type="checkbox"
                              className="rounded border-gray-300"
                              checked={isMeasureSelected(m.compositeId || rowId)}
                              onChange={() => toggleMeasureSelection(m.compositeId || rowId)}
                            />
                          </TableCell>
                        )}
                        {visibleOrder.map((key) => {
                          let content: any = null;
                          switch (key) {
                            case 'id':
                              content = (
                                <div className="flex items-center">
                                  <div className="w-6 flex justify-center">
                                  {m.isMultiStrata && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-6 w-6 p-0"
                                      onClick={() => toggleRowExpansion(m.id)}
                                    >
                                      {isRowExpanded(m.id) ? (
                                        <ChevronDown className="h-4 w-4" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4" />
                                      )}
                                    </Button>
                                  )}
                                  </div>
                                <Button 
                                  variant="link" 
                                  className="h-auto p-0 text-xl font-black text-primary hover:text-primary/80" 
                                  onClick={() => {
                                    setSelectedMeasure(m);
                                    setDetailOpen(true);
                                  }}
                                >
                                  {m.id}
                                </Button>
                                </div>
                              );
                              break;
                            case 'title':
                              content = (
                                <div 
                                  className="min-w-28 xl:min-w-32 2xl:min-w-48 max-w-80 whitespace-normal leading-tight line-clamp-3"
                                  title={m.title}
                                >
                                  {m.title}
                                </div>
                              );
                              break;
                            case 'collectionType':
                              content = (
                                <Badge
                                  variant="secondary"
                                  className="rounded-full bg-slate-100 text-slate-700 border-slate-200/80 hover:bg-slate-200/70 whitespace-nowrap px-2.5 py-1 text-xs font-medium"
                                >
                                  {m.collectionType}
                                </Badge>
                              );
                              break;
                            case 'measureType':
                              content = formatMeasureType(m.measureType);
                              break;
                            case 'flags':
                              content = (
                                <div className="flex flex-col items-start gap-1.5 whitespace-normal leading-tight">
                                  {/* Collection Type Badge */}
                                  <Badge
                                    variant="secondary"
                                    className="rounded-full bg-slate-100 text-slate-700 border-slate-200/80 hover:bg-slate-200/70 whitespace-nowrap px-2.5 py-1 text-xs font-medium"
                                  >
                                    {m.collectionType}
                                  </Badge>
                                  
                                  {/* Additional badges */}
                                  <div className="flex flex-col items-start gap-1.5">
                                    {formatMeasureType(m.measureType)}
                                    {m.inverse && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span>
                                              <Badge variant="secondary" className="rounded-full bg-slate-100 text-slate-700 border-slate-200/80 hover:bg-slate-200/70 whitespace-nowrap px-2.5 py-1 text-xs font-medium">
                                                Inverse
                                              </Badge>
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent className="z-50">Lower performance rate is better</TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                    {m.autoPts && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span>
                                              <Badge variant="secondary" className="rounded-full bg-slate-100 text-slate-700 border-slate-200/80 hover:bg-slate-200/70 whitespace-nowrap px-2.5 py-1 text-xs font-medium">
                                                Bonus Measure
                                              </Badge>
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent className="z-50">Qualifies for automatic bonus points</TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                    {m.cap7 && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <span>
                                              <Badge variant="secondary" className="rounded-full bg-slate-100 text-slate-700 border-slate-200/80 hover:bg-slate-200/70 whitespace-nowrap px-2.5 py-1 text-xs font-medium">
                                                7‑pt Cap
                                              </Badge>
                                            </span>
                                          </TooltipTrigger>
                                          <TooltipContent className="z-50">Capped at 7 points</TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                  </div>
                                </div>
                              );
                              break;

                            case 'inverse':
                              content = m.inverse ? 'Yes' : 'No';
                              break;
                            case 'autoPts':
                              content = m.autoPts ? 'Yes' : 'No';
                              break;
                            case 'cmsAvg':
                              content = `${(m.cmsAvg ?? 0).toFixed(2)}%`;
                              break;
                            case 'registryAvg': {
                              const registryKey = m.compositeId || rowId;
                              const value = registryAverages[registryKey] || '';
                              content = (
                                <div className="flex items-center justify-center">
                                  <div className="quality-print-plain-pill inline-flex min-w-[4.5rem] items-center justify-center rounded-full px-2 py-1 text-sm text-slate-700">
                                    <Input
                                      value={value}
                                      onChange={(event) => updateRegistryAverage(registryKey, event.target.value)}
                                      onBlur={() => normalizeRegistryAverage(registryKey)}
                                      placeholder="—"
                                      inputMode="decimal"
                                      aria-label={`Registry average for measure ${m.id}`}
                                      className="quality-print-plain-input h-6 w-14 border-0 bg-transparent p-0 text-right text-sm shadow-none placeholder:text-slate-500 focus-visible:ring-1 focus-visible:ring-primary/20"
                                    />
                                    {value ? <span className="ml-0.5 text-slate-500">%</span> : null}
                                  </div>
                                </div>
                              );
                              break;
                            }
                            case 'cap7':
                              content = m.cap7 ? 'Yes' : 'No';
                              break;
                            case 'den':
                            case 'met':
                            case 'exc':
                            case 'not':
                              // Show dashes for Simple Average multi-strata measures
                              content = (m.isMultiStrata && m.overallAlgorithm === 'Simple Average') 
                                ? '—' 
                                : (m[key as any]?.toLocaleString() || m[key as any]);
                              break;
                            case 'ip':
                            case 'ex':
                              // Show dashes for Simple Average multi-strata measures
                              content = (m.isMultiStrata && m.overallAlgorithm === 'Simple Average') 
                                ? '—' 
                                : (m[key as any]?.toLocaleString() || m[key as any]);
                              break;
                            case 'dc':
                              content = m.dcStr ?? `${(m.dc || 0).toFixed(2)}%`;
                              break;
                            case 'pr':
                              content = (
                                <div className="min-w-28 xl:min-w-32 2xl:min-w-48 max-w-64 px-2">
                                  <DecileBenchmarkBar
                                    value={m.pr ?? 0}
                                    deciles={(m as any).collectionType && (m as any).collectionType !== 'Unknown'
                                      ? getDecilesForMeasureBySubmissionMethod(m.id, (m as any).collectionType)
                                      : getDecilesForMeasure(m.id, m.inverse)}
                                    inverse={m.inverse}
                                    variant="segmented"
                                    algorithm={m.isMultiStrata ? getStratumDescription(m.overallAlgorithm || '', m.id, dataService) : undefined}
                                    cmsAvg={m.cmsAvg}
                                  />
                                </div>
                              );
                              break;
                            case 'points':
                              content = isCalcPending ? '—' : (
                                <div className="flex flex-col items-center gap-1">
                                  <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-sm font-bold bg-slate-100 text-slate-800 border border-slate-200 shadow-sm">
                                    {typeof m.points === 'number' ? m.points.toFixed(1) : m.points}
                                  </span>
                                  {m.potentialGain > 0 && (simulate !== 'off' || simRule !== 'none') && (
                                    <span className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap border bg-success/10 text-success border-success/30 ring-1 ring-success/20">
                                      <span aria-hidden>▲</span>
                                      <span>+{m.potentialGain.toFixed(1)}</span>
                                      {m.potentialGain === maxGain ? (
                                        <span className="ml-1">• High Impact</span>
                                      ) : null}
                                    </span>
                                  )}
                                </div>
                              );
                              break;

                            default:
                              content = m[key as any];
                          }
                      const numericColumns = ['den', 'met', 'exc', 'not', 'dc', 'points', 'ip', 'ex', 'cmsAvg', 'registryAvg', 'inverse', 'autoPts', 'cap7'];
                          const cellClassName = cn(
                            key === 'collectionType' && 'align-top',
                            key === 'title' ? 'text-left min-w-28 xl:min-w-32 2xl:min-w-48 max-w-80' : 'text-center',
                            key === 'pr' && 'min-w-28 xl:min-w-32 2xl:min-w-48 max-w-64 px-1',
                            numericColumns.includes(key) && 'max-w-20'
                          );
                          return (<TableCell key={key} className={cellClassName}>{content}</TableCell>);
                        })}
                        <TableCell className="quality-print-hide">
                          <div className="flex 2xl:flex-row flex-col 2xl:items-center items-end gap-1 2xl:gap-2 whitespace-nowrap">
                        {/* Show badge based on mode directly, not isAdded */}
                        {m.mode === "manual" ? (
                          hasPendingDrafts ? (
                            <Badge variant="outline" className="!px-2 !py-0.5 !text-xs !leading-none !h-auto border-amber-400 text-amber-600 order-2 2xl:order-none">Pending Changes</Badge>
                          ) : (
                            <Badge variant="secondary" className="!px-2 !py-0.5 !text-xs !leading-none !h-auto order-2 2xl:order-none">Manual</Badge>
                          )
                        ) : null}
                            <DropdownMenu key={`dropdown-${m.id}-${m.status || 'base'}-${m.noData || false}`}>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" aria-label="Row actions" className="h-8 w-8 relative z-10">
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="z-50 bg-background" align="end">
                                <DropdownMenuItem onClick={() => downloadMeasureDetail(m)}>Download Details</DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                                  const params = new URLSearchParams();
                                  params.set('evaluationType', evaluationType);
                                  if (evaluationType === 'NPI' && selectedNpi) {
                                    params.set('npi', selectedNpi);
                                  }
                                  navigate(`/programs/mips/gap-management/quality/measure/${rowId}?${params.toString()}`);
                                }}>View Details</DropdownMenuItem>
                                <DropdownMenuSeparator />
                                {/* Chart Abstraction List option */}
                                <ChartAbstractionActionMenuItem measureId={m.id} />
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel>Mode</DropdownMenuLabel>
                                <DropdownMenuRadioGroup value={m.mode} onValueChange={(v: any) => setMode(m.compositeId || `${m.id}:${m.submissionMethod}`, v)}>
                                  <DropdownMenuRadioItem value="calc">Recalculate from data</DropdownMenuRadioItem>
                                  <DropdownMenuRadioItem value="manual">Manual entry (modeling)</DropdownMenuRadioItem>
                                </DropdownMenuRadioGroup>
                                <DropdownMenuSeparator />
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                      <span className="text-destructive">Remove</span>
                                    </DropdownMenuItem>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Remove Measure</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Are you sure you want to remove measure {m.id}? This action cannot be undone.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={async () => await removeAny(m.compositeId || m.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                        Remove
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                      
                      {/* Expanded strata details row */}
                      {m.isMultiStrata && isExpanded && m.strata && (
                        <TableRow key={`${rowId}-expanded`} className={cn(
                          (hasPendingDrafts || isCalcPending) ? "!border-l-4 !border-l-amber-500" : undefined,
                          "hover:bg-muted/30 transition-colors",
                          // Slightly different background for expanded rows - only apply if no pending state
                          !(hasPendingDrafts || isCalcPending) && sortedRows.indexOf(m) % 2 === 0 ? "bg-muted/10" : undefined,
                          !(hasPendingDrafts || isCalcPending) && sortedRows.indexOf(m) % 2 !== 0 ? "bg-muted/30" : undefined
                        )}>
                          <TableCell colSpan={visibleOrder.length + 1} className="p-0">
                            <div className={cn(
                              "p-4 border-t overflow-x-auto",
                              // Only show blue border when NOT in pending state
                              !(hasPendingDrafts || isCalcPending) && "border-l-4 border-l-blue-500"
                            )}>
                              <Table>
                                <colgroup>
                                  <col className="w-[20%] min-w-[120px]" /> {/* Stratum Name - gets priority space */}
                                  <col className="w-[8%] min-w-[60px]" />   {/* Initial Pop */}
                                  <col className="w-[8%] min-w-[60px]" />   {/* Exclusions */}
                                  <col className="w-[9%] min-w-[70px]" />   {/* Denominator */}
                                  <col className="w-[7%] min-w-[50px]" />   {/* Met */}
                                  <col className="w-[8%] min-w-[60px]" />   {/* Exceptions */}
                                  <col className="w-[8%] min-w-[60px]" />   {/* Not Met */}
                                  <col className="w-[11%] min-w-[80px]" />  {/* Perf. Rate % */}
                                  <col className="w-[11%] min-w-[80px]" />  {/* Complete Rate % */}
                                  <col className="w-[10%] min-w-[80px]" />  {/* Actions */}
                                </colgroup>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead className="text-left text-sm font-medium">Stratum Name</TableHead>
                                    <TableHead className="text-center text-sm font-medium whitespace-normal leading-tight">Initial Pop.</TableHead>
                                    <TableHead className="text-center text-sm font-medium whitespace-normal leading-tight">Exclusions</TableHead>
                                    <TableHead className="text-center text-sm font-medium whitespace-normal leading-tight">Denominator</TableHead>
                                    <TableHead className="text-center text-sm font-medium">Met</TableHead>
                                    <TableHead className="text-center text-sm font-medium whitespace-normal leading-tight">Exceptions</TableHead>
                                    <TableHead className="text-center text-sm font-medium whitespace-normal leading-tight">Not Met</TableHead>
                                    <TableHead className="text-center text-sm font-medium whitespace-normal leading-tight">Perf. Rate %</TableHead>
                                    <TableHead className="text-center text-sm font-medium whitespace-normal leading-tight">Complete Rate %</TableHead>
                                    <TableHead className="quality-print-hide text-center text-sm font-medium">Actions</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {m.strata?.map((stratum, stratumIndex) => {
                                    // Get draft values first (for manual mode)
                                    const sid = m.compositeId;
                                    const viewStratum = (drafts[sid]?.strata?.[stratumIndex]) ?? stratum;
                                    
                                    // Calculate rates using viewStratum (which has manual values if in manual mode)
                                    // CMS formula: PR excludes exceptions from denominator
                                    const stratumPr = (viewStratum.met + viewStratum.not > 0)
                                      ? (viewStratum.met / (viewStratum.met + viewStratum.not)) * 100
                                      : 0;
                                    
                                    const stratumDc = computeDataCompletenessRate(viewStratum.den, viewStratum.met, viewStratum.not, viewStratum.exc);
                                    const isManual = m.mode === 'manual';

                                    return (
                                      <TableRow key={stratum.name} className="bg-white">
                                        <TableCell>
                                          <TooltipProvider>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span className="cursor-help">{stratum.name}</span>
                                              </TooltipTrigger>
                                              <TooltipContent>
                                                <p>
                                                  {stratum.description ||
                                                    dataService
                                                      .getStrataDefinitions(m.id)
                                                      .find((d) => d.name === stratum.name)?.description ||
                                                    '—'}
                                                </p>
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                        </TableCell>
                                                                                {(() => {
                                          // viewStratum already defined above, use it for display
                                          return (
                                            <>
                                              <TableCell className="text-center">
                                                {isManual ? (
                                                  <div className="flex justify-center">
                                                  <Input
                                                    type="number"
                                                    value={viewStratum.ip}
                                                    onChange={(e) => setStrataDraft(sid, stratumIndex, 'ip', parseInt(e.target.value, 10) || 0)}
                                                      className="w-16 h-8 text-center"
                                                  />
                                                  </div>
                                                ) : stratum.ip?.toLocaleString() || '0'}
                                              </TableCell>
                                              <TableCell className="text-center">
                                                {isManual ? (
                                                  <div className="flex justify-center">
                                                  <Input
                                                    type="number"
                                                    value={viewStratum.ex}
                                                    onChange={(e) => setStrataDraft(sid, stratumIndex, 'ex', parseInt(e.target.value, 10) || 0)}
                                                      className="w-16 h-8 text-center"
                                                  />
                                                  </div>
                                                ) : stratum.ex?.toLocaleString() || '0'}
                                              </TableCell>
                                              <TableCell className="text-center">
                                                {isManual ? (
                                                  <div className="flex justify-center">
                                                  <Input
                                                    type="number"
                                                    value={Math.max(0, viewStratum.ip - viewStratum.ex)}
                                                    readOnly
                                                      className="w-16 h-8 text-center bg-slate-100"
                                                  />
                                                  </div>
                                                ) : stratum.den?.toLocaleString() || '0'}
                                              </TableCell>
                                              <TableCell className="text-center">
                                                {isManual ? (
                                                  <div className="flex justify-center">
                                                  <Input
                                                    type="number"
                                                    value={viewStratum.met}
                                                    onChange={(e) => setStrataDraft(sid, stratumIndex, 'met', parseInt(e.target.value, 10) || 0)}
                                                      className="w-16 h-8 text-center"
                                                  />
                                                  </div>
                                                ) : stratum.met?.toLocaleString() || '0'}
                                              </TableCell>
                                              <TableCell className="text-center">
                                                {isManual ? (
                                                  <div className="flex justify-center">
                                                  <Input
                                                    type="number"
                                                    value={viewStratum.exc}
                                                    onChange={(e) => setStrataDraft(sid, stratumIndex, 'exc', parseInt(e.target.value, 10) || 0)}
                                                      className="w-16 h-8 text-center"
                                                  />
                                                  </div>
                                                ) : stratum.exc?.toLocaleString() || '0'}
                                              </TableCell>
                                              <TableCell className="text-center">
                                                {isManual ? (
                                                  <div className="flex justify-center">
                                                  <Input
                                                    type="number"
                                                    value={viewStratum.not}
                                                    onChange={(e) => setStrataDraft(sid, stratumIndex, 'not', parseInt(e.target.value, 10) || 0)}
                                                      className="w-16 h-8 text-center"
                                                  />
                                                  </div>
                                                ) : stratum.not?.toLocaleString() || '0'}
                                              </TableCell>
                                            </>
                                          );
                                        })()}
                                        <TableCell className="text-center">{stratumPr.toFixed(2)}%</TableCell>
                                        <TableCell className="text-center">{stratumDc.toFixed(2)}%</TableCell>
                                        <TableCell className="quality-print-hide">
                                          <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                              <Button variant="ghost" className="h-8 w-8 p-0">
                                                <span className="sr-only">Open menu</span>
                                                <MoreHorizontal className="h-4 w-4" />
                                              </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                              <DropdownMenuItem onClick={() => downloadMeasureDetail(m)}>Download Details</DropdownMenuItem>
                                              <DropdownMenuItem onClick={() => {
                                                const params = new URLSearchParams();
                                                params.set('evaluationType', evaluationType);
                                                if (evaluationType === 'NPI' && selectedNpi) {
                                                  params.set('npi', selectedNpi);
                                                }
                                                navigate(`/programs/mips/gap-management/quality/measure/${rowId}?${params.toString()}`);
                                              }}>View Details</DropdownMenuItem>
                                            </DropdownMenuContent>
                                          </DropdownMenu>
                                        </TableCell>
                                      </TableRow>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                              <div className="mt-2 p-2 text-sm text-right text-gray-600 bg-gray-100 rounded-b-lg">
                                <span className="font-semibold">Overall Calculated Rate:</span> {(m.overallPr || 0).toFixed(2)}%
                                {m.overallAlgorithm && (
                                  <span className="italic ml-2">({getStratumDescription(m.overallAlgorithm, m.id, dataService)})</span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </>);
                  })
                  )}
                </TableBody>
              </Table>
            </div>
            {/* Bulk delete action bar */}
            {isSelectionMode && selectedMeasures.size > 0 && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-blue-700">
                    {selectedMeasures.size} measure{selectedMeasures.size === 1 ? '' : 's'} selected
                  </span>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => setShowBulkDeleteDialog(true)}
                  >
                    <i className="fas fa-trash mr-1"></i>Delete Selected ({selectedMeasures.size})
                  </Button>
                </div>
              </div>
            )}
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <LoadingButton 
                  onClick={() => setAddOpen(true)}
                  loading={isAddingMeasure}
                  disabled={pathwayMvpId != null}
                  title={pathwayMvpId != null ? "The MVP's measure pool is fixed by CMS — switch to Traditional MIPS to change tracked measures" : undefined}
                >
                  + Add Measure
                </LoadingButton>
                <LoadingButton 
                  onClick={recalc}
                  loading={isRecalculating}
                  loadingText="Recalculating..."
                  variant={Object.keys(drafts).length > 0 ? "default" : "outline"}
                  className={Object.keys(drafts).length > 0 ? "bg-amber-600 hover:bg-amber-700" : undefined}
                >
                  {(() => {
                    const draftCount = Object.keys(drafts).length;
                    
                    if (draftCount > 0) {
                      return <>Confirm Updates & Recalculate ({draftCount} pending)</>;
                    }
                    return <>Confirm Updates & Recalculate</>;
                  })()}
                </LoadingButton>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
      </div>

      {/* PI, IA, Cost tabs — lazy-mounted on first select (Snappiness 0.3),
          then kept mounted but hidden so tab returns don't remount/re-fetch */}
      {mountedCategories.has("ia") && (
      <section className="mt-6" style={{ display: activeCategory === "ia" ? "block" : "none" }}>
        <IACalculator
          practiceId={selectedPracticeId || ''}
          practiceName={selectedPractice?.tin_name || selectedPractice?.practice_alias}
          useModeledIa={useModeledIa}
          modeledIaPercent={modeledIaPercent}
          iaWeight={mipsWeights.iaWeight}
          onToggleModeled={handleToggleModeledIa}
          onScoreChange={(rawScore) => {
            // Update IA score in the top card when calculator mode is active
            // Apply weight here (single source of truth) - IA max is 40 points
            if (!useModeledIa) {
              const weightedScore = (rawScore / 40) * mipsWeights.iaWeight;
              setMipsScores(prev => ({
                ...prev,
                iaScore: weightedScore
              }));
            }
          }}
          providers={providers}
          evaluationType={evaluationType}
          selectedNpi={selectedNpi}
          onEvaluationTypeChange={setEvaluationType}
          onNpiChange={setSelectedNpi}
          disabled={isCurrentYearLocked}
        />
      </section>
      )}

      {mountedCategories.has("pi") && (
      <section className="mt-6" style={{ display: activeCategory === "pi" ? "block" : "none" }}>
        <PICalculator
          practiceId={selectedPracticeId || ''}
          practiceName={selectedPractice?.tin_name || selectedPractice?.practice_alias}
          disabled={isCurrentYearLocked}
          useModeledPi={useModeledPi}
          modeledPiPercent={modeledPiPercent}
          piWeight={mipsWeights.piWeight}
          onToggleModeled={handleToggleModeledPi}
          onScoreChange={(rawScore) => {
            // Update PI score in the top card when calculator mode is active
            // Apply weight here (single source of truth) - PI max is 100 points
            if (!useModeledPi) {
              const weightedScore = (rawScore / 100) * mipsWeights.piWeight;
              setMipsScores(prev => ({
                ...prev,
                piScore: weightedScore
              }));
            }
          }}
          providers={providers}
          evaluationType={evaluationType}
          selectedNpi={selectedNpi}
          onEvaluationTypeChange={setEvaluationType}
          onNpiChange={setSelectedNpi}
        />
      </section>
      )}

      {mountedCategories.has("cost") && (
      <section className="mt-6" style={{ display: activeCategory === "cost" ? "block" : "none" }}>
        <CostCalculator
          practiceId={selectedPracticeId || ''}
          practiceName={selectedPractice?.tin_name || selectedPractice?.practice_alias}
          useModeledCost={useModeledCost}
          modeledCostPercent={modeledCostPercent}
          costWeight={mipsWeights.costWeight}
          onToggleModeled={handleToggleModeledCost}
          onScoreChange={(rawScore) => {
            // Update Cost score in the top card when calculator mode is active
            // Apply weight here (single source of truth) - Cost max is 100 points
            if (!useModeledCost) {
              const weightedScore = (rawScore / 100) * mipsWeights.costWeight;
              setMipsScores(prev => ({
                ...prev,
                costScore: weightedScore
              }));
            }
          }}
          providers={providers}
          evaluationType={evaluationType}
          selectedNpi={selectedNpi}
          onEvaluationTypeChange={setEvaluationType}
          onNpiChange={setSelectedNpi}
        />
      </section>
      )}

      {activeCategory === "composite" && (
        <section className="mt-6">
          <GapsToClose category={activeCategory} practiceId={selectedPractice?.id} />
        </section>
      )}

      <AddMeasureModal
        open={addOpen}
        onOpenChange={setAddOpen}
        measures={availableMeasures}
        onAdd={async (ids) => {
          const existing = new Set<string>([...selectedBaseIds]);
          const toAddSet = new Set<string>();
          const toAdd: any[] = [];
          
          // Process async calls first, outside of state update
          for (const compositeId of ids) {
            if (existing.has(compositeId) || toAddSet.has(compositeId)) continue;
            
            const { measureId, submissionMethod } = parseCompositeId(compositeId);
            const found = availableMeasures.find((m) => m.id === compositeId) || { id: compositeId, title: compositeId };
            const title = found.title.replace(/ \(.*\)$/, '');
            
            // FIXED: Await the async call outside of state callback
            const summary = await getOutcomeSummary(measureId, submissionMethod, undefined, {
              evaluationType,
              npi: evaluationType === 'NPI' ? selectedNpi : undefined,
              year: programYear
            });
            let newMeasure: AddedMeasure;

            if (summary) {
              const baseRow = toMeasureRow(summary, title);
              newMeasure = {
                ...baseRow,
                id: compositeId,
                mode: "calc" as AddedMeasureMode,
                manual: undefined,
              };
            } else {
              // Use config to determine multi-strata status (not hardcoded false)
              const addDataService = MipsDataService.getInstance();
              const isMulti = addDataService.isMultiStrataMeasure(measureId, submissionMethod);
              newMeasure = {
                id: compositeId,
                title,
                submissionMethod,
                isMultiStrata: isMulti,
                ip: 0, ex: 0, den: 0, met: 0, exc: 0, not: 0, dc: 0, pr: 0,
                mode: "manual" as AddedMeasureMode,
                manual: { ip: 0, ex: 0, den: 0, met: 0, exc: 0, not: 0 },
                // Populate strata from CMS definitions when adding a multi-strata measure
                ...(isMulti ? {
                  strata: addDataService.getStrataDefinitions(measureId).map(def => ({
                    name: def.name || '',
                    description: def.description,
                    ip: 0, ex: 0, den: 0, met: 0, exc: 0, not: 0, pr: 0, dc: 0
                  })),
                  overallAlgorithm: addDataService.getStrataConfig(measureId, submissionMethod)?.performanceRate,
                  overallPr: 0,
                  overallDc: 0,
                } : {}),
              };
            }
            
            toAdd.push(newMeasure);
            toAddSet.add(compositeId);
          }
          
          // Save to database if we have a selected practice
          if (selectedPracticeId && toAdd.length > 0) {
            try {
              // Save each measure to the database
              for (const measure of toAdd) {
                const { measureId, submissionMethod } = parseCompositeId(measure.id);
                await fetch(`${apiUrl}/api/practices/${selectedPracticeId}/measures?year=${programYear}`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
                  },
                  body: JSON.stringify({
                    measure_id: measureId,
                    measure_category: 'quality',
                    submission_method: submissionMethod || 'MIPS CQM',
                  }),
                });
              }
              console.log(`Saved ${toAdd.length} measures to practice ${selectedPracticeId}`);
            } catch (error) {
              console.error('Error saving measures to database:', error);
              toast({
                title: "Warning",
                description: "Measures added locally but failed to save to database",
                variant: "destructive",
              });
            }
          }
          
          // OPTION 2: No need to update added array - measures are saved to database
          if (toAdd.length > 0) {
            console.log('[OPTION2-BULK-ADD] Measures saved to database, triggering data refresh');
            
            // Trigger a full data refresh to load the new measures from database
            setDataVersion(v => v + 1);
            
            toast({
              title: "Measures added",
              description: `Added ${toAdd.length} measure(s) to your selection.`,
            });
          }
        }}
      />

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Selected Measures</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedMeasures.size} selected measure{selectedMeasures.size === 1 ? '' : 's'}? This action cannot be undone.
              {selectedMeasures.size > 0 && (
                <div className="mt-3 max-h-32 overflow-y-auto">
                  <div className="text-sm font-medium mb-2">Measures to be deleted:</div>
                  <ul className="text-sm space-y-1">
                    {Array.from(selectedMeasures).map(compositeId => {
                      const measure = sortedRows.find((m: any) => (m.compositeId || `${m.id}:${m.submissionMethod || ''}`) === compositeId);
                      return (
                        <li key={compositeId} className="flex items-center gap-2">
                          <span className="font-mono text-xs bg-gray-100 px-1 rounded">{measure?.id}</span>
                          <span>{measure?.title}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={async () => {
                // removeAny deletes each selected row from the backend (exact measure +
                // collection type) and from local state.
                await Promise.all(
                  Array.from(selectedMeasures).map(compositeId => removeAny(compositeId))
                );
                
                // Clear selections, exit selection mode, and close dialog
                exitSelectionMode();
                setShowBulkDeleteDialog(false);
                
                // Show success toast
                toast({
                  title: "Measures Deleted",
                  description: `Successfully deleted ${selectedMeasures.size} measure${selectedMeasures.size === 1 ? '' : 's'}.`,
                });
              }} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete {selectedMeasures.size} Measure{selectedMeasures.size === 1 ? '' : 's'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ManageViewsModal
        open={manageOpen}
        onOpenChange={setManageOpen}
        presets={presets as any}
        activePresetId={activePresetId as any}
        allColumns={allColumnDefs as any}
        systemDefault={defaultColSettings as any}
        onCommit={(next: any[], activeId?: string) => {
          setPresets(next as any);
          if (activeId) {
            setActivePresetId(activeId as any);
            const p = next.find((x: any) => x.id === activeId);
            if (p) setColSettings(p.settings as any);
          } else {
            const cur = next.find((x: any) => x.id === activePresetId);
            if (cur) setColSettings(cur.settings as any);
          }
        }}
      />

      {/* Measure Detail Sheet */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent className="bg-background sm:max-w-[48rem] w-[95vw] overflow-y-auto">
          {/* Use a relative container *inside* SheetContent to avoid breaking component styles */}
          <div className="relative px-6 pt-2 pb-6">
            <SheetHeader>
              <div className="space-y-3">
                {/* Top Row: Title/ID and CMS Circle */}
                <div className="flex items-start justify-between gap-6">
                  {/* Left side: Title, ID, Subtitle */}
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-800 text-white px-4 py-2 rounded-lg font-bold text-lg flex-shrink-0">
                      {selectedMeasure?.id}
                    </div>
                    <div>
                      <SheetTitle className="text-xl font-bold text-slate-900 leading-tight">{selectedMeasure?.title}</SheetTitle>
                      <div className="text-sm text-slate-500 mt-1">
                        Collection: {selectedMeasure?.collectionType} &bull; Measure type: {selectedMeasure?.measureType || 'process'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Right side: CMS Circle */}
                  <div className="flex-shrink-0 pt-1">
                    {selectedMeasure?.cmsAvg !== undefined && selectedMeasure.cmsAvg !== null && (
                      <div 
                        className="relative w-16 h-16 rounded-full flex items-center justify-center"
                        style={{ background: `conic-gradient(#1e40af ${selectedMeasure.cmsAvg * 3.6}deg, #e5e7eb ${selectedMeasure.cmsAvg * 3.6}deg)` }}
                      >
                        <div className="absolute w-[58px] h-[58px] bg-background rounded-full flex flex-col items-center justify-center">
                          <span className="text-lg font-bold text-blue-800">
                            {selectedMeasure.cmsAvg.toFixed(0)}%
                          </span>
                          <span className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">CMS AV</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bottom Row: Pills and Button */}
                <div className="flex items-center justify-between">
                  {/* Left side: Pills */}
                  {selectedMeasure && (
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="rounded-full bg-slate-100 text-slate-700 border-slate-200/80 hover:bg-slate-200/70">Inverse: {selectedMeasure.inverse ? 'Yes' : 'No'}</Badge>
                      <Badge variant="secondary" className="rounded-full bg-slate-100 text-slate-700 border-slate-200/80 hover:bg-slate-200/70">Bonus Pts: {selectedMeasure.autoPts ? 'Yes' : 'No'}</Badge>
                      <Badge variant="secondary" className="rounded-full bg-slate-100 text-slate-700 border-slate-200/80 hover:bg-slate-200/70">7-pt cap: {selectedMeasure.cap7 ? 'Yes' : 'No'}</Badge>
                    </div>
                  )}
                  
                  {/* Right side: Button */}
                  <Button
                    size="sm"
                    className="bg-blue-800 hover:bg-blue-900 text-white font-medium shadow-sm"
                    onClick={() => {
                      const specUrl = getSpecificationUrl(selectedMeasure);
                      if (specUrl) {
                        window.open(specUrl, '_blank', 'noopener,noreferrer');
                      }
                    }}
                    disabled={!getSpecificationUrl(selectedMeasure)}
                  >
                    <ExternalLink className="h-4 w-4 mr-1.5" />
                    Measure Spec
                  </Button>
                </div>
              </div>
            </SheetHeader>

            {/* Visual Divider */}
            <div className="border-t border-slate-200 my-4"></div>

            {selectedMeasure && (
              <div className="space-y-4">
                {/* Calculated Metrics */}
                <div>
                  <h3 className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wider">Calculated Metrics</h3>
                  <div className="bg-white border border-slate-200/60 rounded-xl shadow-sm p-3">
                    <div className="grid grid-cols-3 gap-4 items-center">
                      {/* Left Side: Performance Rate */}
                      <div className="col-span-2">
                        <div className="text-sm text-slate-600 font-medium">Performance Rate</div>
                        <div className="text-3xl font-bold text-blue-800">{selectedMeasure.prStr}</div>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 bg-slate-200 rounded-full h-2">
                            <div className="bg-blue-800 h-2 rounded-full" style={{ width: `${selectedMeasure.pr}%` }}></div>
                          </div>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">{selectedMeasure.inverse ? "Lower is better" : "Higher is better"}</div>
                      </div>
                      {/* Right Side: Other Metrics */}
                      <div className="col-span-1 space-y-2">
                        <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-2">
                          <div className="text-xs text-slate-500 font-medium">Complete Rate</div>
                          <div className="text-base font-bold text-slate-800">{selectedMeasure.dcStr}</div>
                        </div>
                        <div>
                          <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-2">
                            <div className="text-xs text-slate-500 font-medium">Score (est)</div>
                            <div className="text-base font-bold text-purple-600">{`${typeof selectedMeasure.points === 'number' ? `${selectedMeasure.points.toFixed(1)} / 10` : 'N/A'} pts`}</div>
                          </div>
                          <div className="text-xs text-slate-500 text-right mt-1">Based on benchmark</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Modern Measure Outcomes Table */}
                <div className="bg-white border border-slate-200/60 rounded-xl shadow-sm overflow-hidden">
                  {/* Headers Row */}
                  <div className="grid grid-cols-6 bg-gradient-to-r from-slate-50 to-slate-50/80">
                    <div className="text-xs font-semibold text-slate-600 text-center py-2 px-3 border-r border-slate-200/50 last:border-r-0 tracking-wide uppercase">Initial Pop.</div>
                    <div className="text-xs font-semibold text-slate-600 text-center py-2 px-3 border-r border-slate-200/50 last:border-r-0 tracking-wide uppercase">Exclusions</div>
                    <div className="text-xs font-semibold text-slate-600 text-center py-2 px-3 border-r border-slate-200/50 last:border-r-0 tracking-wide uppercase">Denominator</div>
                    <div className="text-xs font-semibold text-slate-600 text-center py-2 px-3 border-r border-slate-200/50 last:border-r-0 tracking-wide uppercase">Met</div>
                    <div className="text-xs font-semibold text-slate-600 text-center py-2 px-3 border-r border-slate-200/50 last:border-r-0 tracking-wide uppercase">Exception</div>
                    <div className="text-xs font-semibold text-slate-600 text-center py-2 px-3 border-r border-slate-200/50 last:border-r-0 tracking-wide uppercase">Not Met</div>
                  </div>
                  {/* Values Row */}
                  <div className="grid grid-cols-6 bg-white border-t border-slate-200/30">
                    {(() => {
                      // Compute manual view for this measure using drafts
                      const measureId = selectedMeasure.compositeId || selectedMeasure.id;
                      const { measureId: mId, submissionMethod } = parseCompositeId(measureId);
                      const baseMeasure = baseData.find(b => b.id === mId && b.submissionMethod === submissionMethod);
                      const committed = baseMeasure?.manualValues || { ip: 0, ex: 0, den: 0, met: 0, not: 0, exc: 0 };
                      const manualView = getManualView(measureId, committed);
                      
                      return (
                        <>
                          {/* Initial Population */}
                          <div className="group border-r border-slate-200/30 last:border-r-0">
                            {selectedMeasure.isAdded && selectedMeasure.mode === "manual" ? ( <div className="h-10 px-3 py-2 text-sm font-medium text-slate-700 text-center bg-white hover:bg-blue-50/50 cursor-pointer transition-all duration-200 flex items-center justify-center group-hover:shadow-inner" onClick={(e) => { const input = e.currentTarget.querySelector('input'); if (input) input.focus(); }}> <Input type="number" value={manualView.ip} onChange={(e) => setManualDraft(measureId, 'ip', Number(e.target.value) || 0)} onFocus={(e) => e.target.select()} className="border-0 p-0 h-auto text-center bg-transparent focus:ring-0 focus:border-0 shadow-none w-full font-medium text-slate-700" /> </div> ) : ( <div className="h-10 px-3 py-2 text-sm font-medium text-slate-600 text-center bg-white flex items-center justify-center"> {/* Show dashes for Simple Average multi-strata measures */} {selectedMeasure.isMultiStrata && selectedMeasure.overallAlgorithm === 'Simple Average' ? '—' : (selectedMeasure.ip?.toLocaleString() || selectedMeasure.ip || '—')} </div> )}
                          </div>
                          {/* Denominator Exclusion */}
                          <div className="group border-r border-slate-200/30 last:border-r-0">
                            {selectedMeasure.isAdded && selectedMeasure.mode === "manual" ? ( <div className="h-10 px-3 py-2 text-sm font-medium text-slate-700 text-center bg-white hover:bg-blue-50/50 cursor-pointer transition-all duration-200 flex items-center justify-center group-hover:shadow-inner" onClick={(e) => { const input = e.currentTarget.querySelector('input'); if (input) input.focus(); }}> <Input type="number" value={manualView.ex} onChange={(e) => setManualDraft(measureId, 'ex', Number(e.target.value) || 0)} onFocus={(e) => e.target.select()} className="border-0 p-0 h-auto text-center bg-transparent focus:ring-0 focus:border-0 shadow-none w-full font-medium text-slate-700" /> </div> ) : ( <div className="h-10 px-3 py-2 text-sm font-medium text-slate-600 text-center bg-white flex items-center justify-center"> {/* Show dashes for Simple Average multi-strata measures */} {selectedMeasure.isMultiStrata && selectedMeasure.overallAlgorithm === 'Simple Average' ? '—' : (selectedMeasure.ex?.toLocaleString() || selectedMeasure.ex || '—')} </div> )}
                          </div>
                          {/* Denominator (calculated, read-only) */}
                          <div className="border-r border-slate-200/30 last:border-r-0"><div className="h-10 px-3 py-2 text-sm font-semibold text-slate-500 text-center bg-slate-50/50 flex items-center justify-center relative"><div className="absolute top-1 right-1 w-1.5 h-1.5 bg-slate-300 rounded-full" title="Calculated field"></div>{selectedMeasure.isAdded && selectedMeasure.mode === "manual" ? Math.max(0, manualView.ip - manualView.ex).toLocaleString() : (selectedMeasure.isMultiStrata && selectedMeasure.overallAlgorithm === 'Simple Average' ? '—' : (selectedMeasure.den?.toLocaleString() || selectedMeasure.den || '—'))}</div></div>
                          {/* Numerator Met */}
                          <div className="group border-r border-slate-200/30 last:border-r-0">
                            {selectedMeasure.isAdded && selectedMeasure.mode === "manual" ? ( <div className="h-10 px-3 py-2 text-sm font-medium text-slate-700 text-center bg-white hover:bg-emerald-50/50 cursor-pointer transition-all duration-200 flex items-center justify-center group-hover:shadow-inner" onClick={(e) => { const input = e.currentTarget.querySelector('input'); if (input) input.focus(); }}> <Input type="number" value={manualView.met} onChange={(e) => setManualDraft(measureId, 'met', Number(e.target.value) || 0)} onFocus={(e) => e.target.select()} className="border-0 p-0 h-auto text-center bg-transparent focus:ring-0 focus:border-0 shadow-none w-full font-medium text-slate-700" /> </div> ) : ( <div className="h-10 px-3 py-2 text-sm font-medium text-slate-600 text-center bg-white flex items-center justify-center"> {/* Show dashes for Simple Average multi-strata measures */} {selectedMeasure.isMultiStrata && selectedMeasure.overallAlgorithm === 'Simple Average' ? '—' : (selectedMeasure.met?.toLocaleString() || selectedMeasure.met || '—')} </div> )}
                          </div>
                          {/* Numerator Exception */}
                          <div className="group border-r border-slate-200/30 last:border-r-0">
                            {selectedMeasure.isAdded && selectedMeasure.mode === "manual" ? ( <div className="h-10 px-3 py-2 text-sm font-medium text-slate-700 text-center bg-white hover:bg-amber-50/50 cursor-pointer transition-all duration-200 flex items-center justify-center group-hover:shadow-inner" onClick={(e) => { const input = e.currentTarget.querySelector('input'); if (input) input.focus(); }}> <Input type="number" value={manualView.exc} onChange={(e) => setManualDraft(measureId, 'exc', Number(e.target.value) || 0)} onFocus={(e) => e.target.select()} className="border-0 p-0 h-auto text-center bg-transparent focus:ring-0 focus:border-0 shadow-none w-full font-medium text-slate-700" /> </div> ) : ( <div className="h-10 px-3 py-2 text-sm font-medium text-slate-600 text-center bg-white flex items-center justify-center"> {/* Show dashes for Simple Average multi-strata measures */} {selectedMeasure.isMultiStrata && selectedMeasure.overallAlgorithm === 'Simple Average' ? '—' : (selectedMeasure.exc?.toLocaleString() || selectedMeasure.exc || '—')} </div> )}
                          </div>
                          {/* Numerator Not Met */}
                          <div className="group border-r border-slate-200/30 last:border-r-0">
                            {selectedMeasure.isAdded && selectedMeasure.mode === "manual" ? ( <div className="h-10 px-3 py-2 text-sm font-medium text-slate-700 text-center bg-white hover:bg-red-50/50 cursor-pointer transition-all duration-200 flex items-center justify-center group-hover:shadow-inner" onClick={(e) => { const input = e.currentTarget.querySelector('input'); if (input) input.focus(); }}> <Input type="number" value={manualView.not} onChange={(e) => setManualDraft(measureId, 'not', Number(e.target.value) || 0)} onFocus={(e) => e.target.select()} className="border-0 p-0 h-auto text-center bg-transparent focus:ring-0 focus:border-0 shadow-none w-full font-medium text-slate-700" /> </div> ) : ( <div className="h-10 px-3 py-2 text-sm font-medium text-slate-600 text-center bg-white flex items-center justify-center"> {/* Show dashes for Simple Average multi-strata measures */} {selectedMeasure.isMultiStrata && selectedMeasure.overallAlgorithm === 'Simple Average' ? '—' : (selectedMeasure.not?.toLocaleString() || selectedMeasure.not || '—')} </div> )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
                
                <Separator />

                {/* Benchmark */}
                <div>
                  <h3 className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wider">Benchmark</h3>
                  <div className="bg-white rounded-lg border border-slate-200/60 shadow-sm p-4">
                    <DecileBenchmarkBar
                      value={selectedMeasure.pr ?? 0}
                      deciles={
                        selectedMeasure.collectionType && selectedMeasure.collectionType !== 'Unknown'
                          ? getDecilesForMeasureBySubmissionMethod(selectedMeasure.id, selectedMeasure.collectionType)
                          : getDecilesForMeasure(selectedMeasure.id, selectedMeasure.inverse)
                      }
                      inverse={selectedMeasure.inverse}
                      variant="muted"
                      algorithm={selectedMeasure.isMultiStrata ? getStratumDescription(selectedMeasure.overallAlgorithm || '', selectedMeasure.id, dataService) : undefined}
                      cmsAvg={selectedMeasure.cmsAvg}
                    />
                  </div>
                </div>

                <Separator />

                {/* Notes and Evidence */}
                <div>
                  <h3 className="text-sm font-semibold mb-4">NOTES</h3>
                  <RichTextEditor
                    value={measureNotes[selectedMeasure.id] || ''}
                    onChange={(html) => updateMeasureNotes(selectedMeasure.id, html)}
                    placeholder="Add comments and notes..."
                  />
                </div>

                <div className="space-y-3">
                  <div className="text-sm font-medium">Upload evidence/screenshots</div>
                  {isFeatureEnabled('EVIDENCE_FILES_ENABLED') ? (
                    <>
                  <Dropzone
                    onFiles={(files) => handleEvidenceUploadFiles(selectedMeasure.id, files)}
                    onError={(msg) => toast({ title: "Upload error", description: msg })}
                    accept={["image/*", ".pdf", ".csv", "application/vnd.ms-excel", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"]}
                  />
                  {measureEvidence[selectedMeasure.id] && measureEvidence[selectedMeasure.id].length > 0 && (
                    <div className="grid grid-cols-2 gap-3">
                      {measureEvidence[selectedMeasure.id].map((ev) => (
                        <div key={`${ev.name}-${ev.uploadedAt}`} className="rounded border p-3">
                          {ev.type.startsWith("image/") ? (
                            <img 
                              src={ev.dataUrl} 
                              alt={`Evidence ${ev.name}`} 
                              className="h-20 w-full rounded object-cover mb-2" 
                              loading="lazy" 
                            />
                          ) : (
                            <div className="text-xs mb-2">
                              <p className="font-medium truncate" title={ev.name}>{ev.name}</p>
                              <p className="text-muted-foreground">{Math.round(ev.size / 1024)} KB</p>
                            </div>
                          )}
                          <div className="flex items-center gap-2">
                            <Button asChild size="sm" variant="outline">
                              <a href={ev.dataUrl} target="_blank" rel="noreferrer">View</a>
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive" 
                              onClick={() => removeEvidence(selectedMeasure.id, ev.name, ev.uploadedAt)}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                    </>
                  ) : (
                    <Alert>
                      <InfoIcon className="h-4 w-4" />
                      <AlertDescription>
                        Evidence file uploads are temporarily disabled.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
      
      {/* Partial Measures Export Modal */}
      <AlertDialog open={partialMeasuresModalOpen} onOpenChange={setPartialMeasuresModalOpen}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Export Partial Measures</AlertDialogTitle>
            <AlertDialogDescription>
              Select partial measures to export patient-level details. These measures are used for supplementary calculations and are not displayed in the main summary.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="my-4 space-y-4">
            {partialMeasures.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No partial measures found for this practice.
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Available Partial Measures</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (selectedPartialMeasures.size === partialMeasures.length) {
                          setSelectedPartialMeasures(new Set());
                        } else {
                          setSelectedPartialMeasures(new Set(partialMeasures.map(m => m.id)));
                        }
                      }}
                    >
                      {selectedPartialMeasures.size === partialMeasures.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  
                  <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                    {partialMeasures.map((measure) => (
                      <div key={measure.id} className="flex items-center space-x-3 p-3 hover:bg-muted/50">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300"
                          checked={selectedPartialMeasures.has(measure.id)}
                          onChange={(e) => {
                            const newSet = new Set(selectedPartialMeasures);
                            if (e.target.checked) {
                              newSet.add(measure.id);
                            } else {
                              newSet.delete(measure.id);
                            }
                            setSelectedPartialMeasures(newSet);
                          }}
                        />
                        <div className="flex-1">
                          <div className="font-medium">{measure.id}</div>
                          <div className="text-sm text-muted-foreground">
                            {measure.title || 'Partial Measure'}
                          </div>
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {measure.submissionMethod || 'MIPS CQM'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <Alert>
                  <InfoIcon className="h-4 w-4" />
                  <AlertDescription>
                    The export will include patient-level details (Patient ID, Name, DOB, NPI, CPT/ICD codes, and measure outcomes) for all selected partial measures in a single CSV file.
                  </AlertDescription>
                </Alert>
              </>
            )}
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {partialMeasures.length > 0 && (
              <AlertDialogAction
                onClick={() => {
                  if (selectedPartialMeasures.size === 0) {
                    // Export all if none selected
                    exportPartialMeasures();
                  } else {
                    // Export selected
                    exportPartialMeasures(Array.from(selectedPartialMeasures));
                  }
                }}
                disabled={exportingPartialMeasures}
              >
                {exportingPartialMeasures ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Export {selectedPartialMeasures.size > 0 ? `${selectedPartialMeasures.size} Selected` : 'All'}
                  </>
                )}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Payment Adjustment Report Modal */}
      <PaymentAdjustmentModal
        open={showPaymentAdjustmentModal}
        onOpenChange={setShowPaymentAdjustmentModal}
        compositeScore={displayCompositeScore}
      />
    </div>
  );
}

export default function Gaps(props: any) {
  // Lightweight wrapper to gate rendering and avoid pre-gate work.
  // No-practice gate (same pattern as APP Plus's NoAcoState): rendered
  // synchronously once auth resolves — no skeleton flash, and the heavy
  // content component never mounts without a practice.
  const { user, loading: authLoading, selectedPractice } = useAuth();
  const content = useMemo(() => <GapManagementContent {...props} />, [props]);
  if (!authLoading && user !== undefined && !selectedPractice) return <NoPracticeState />;
  return content;
}
